<template>
  <div
    class="music-sheet-container font-sans select-none focus:outline-none"
    :class="[
      props.embedded
        ? (isFittingContainer ? 'block w-full' : 'block')
        : 'flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden',
      !props.embedded && isFocused ? 'ring-2 ring-indigo-500' : ''
    ]"
    tabindex="0"
    @focus="isFocused = true"
    @blur="isFocused = false"
    @keydown="handleKeyDown"
  >
    <!-- Header / Title Bar (Only when not embedded) -->
    <div
      v-if="!props.embedded"
      class="flex flex-wrap items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 gap-2"
    >
      <div class="flex items-center gap-2">
        <div class="p-1.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-lg">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18V5l12-2v13"></path>
            <circle cx="6" cy="18" r="3"></circle>
            <circle cx="18" cy="16" r="3"></circle>
          </svg>
        </div>
        <input
          v-if="!props.readonly"
          v-model="scoreTitle"
          type="text"
          class="bg-transparent text-sm font-semibold text-gray-800 dark:text-gray-100 border-b border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none px-1 py-0.5"
          :placeholder="t('score.defaultTitle')"
        />
        <span v-else class="text-sm font-semibold text-gray-800 dark:text-gray-100 px-1">{{ scoreTitle }}</span>
      </div>

      <!-- Quick Playback, Tempo & Modal Triggers -->
      <div class="flex items-center gap-2">
        <!-- Tempo (BPM) Widget -->
        <div class="flex items-center gap-1.5 bg-white dark:bg-gray-700 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm text-xs">
          <span class="font-serif font-bold text-gray-600 dark:text-gray-300">♩=</span>
          <input
            v-if="!props.readonly"
            v-model.number="selectedTempo"
            type="number"
            min="30"
            max="300"
            step="5"
            class="w-12 bg-transparent text-center font-bold text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-0.5 py-0.5"
            :title="t('toolbar.tempo')"
          />
          <span v-else class="font-bold text-gray-800 dark:text-gray-100">{{ selectedTempo }}</span>
          <span class="text-[10px] text-gray-400 font-medium">BPM</span>

          <!-- Tempo Presets Dropdown / Quick Buttons -->
          <div v-if="!props.readonly" class="relative group ml-1">
            <button class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
            </button>
            <div class="absolute right-0 top-full mt-1 hidden group-hover:flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-30 min-w-[120px]">
              <button
                v-for="preset in tempoPresets"
                :key="preset.bpm"
                @click="selectedTempo = preset.bpm"
                class="px-3 py-1 text-left text-xs hover:bg-indigo-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center justify-between"
              >
                <span>{{ preset.label }}</span>
                <span class="text-[10px] text-gray-400 font-mono">{{ preset.bpm }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Soundfont Instrument Selector Widget (Bound to Active Voice / Instrument) -->
        <MusicSheetSelect
          v-model="selectedInstrument"
          :groups="instrumentGroups"
          :icon="activeInstrumentIcon"
          button-class="bg-white dark:bg-gray-700/90 hover:bg-gray-50 dark:hover:bg-gray-700 px-2.5 py-1 rounded-lg border border-gray-200/90 dark:border-gray-600 shadow-xs text-xs text-gray-800 dark:text-gray-100 max-w-[210px]"
          menu-class="w-56"
          :title="t('voices.select', { name: activeVoice.name || activeVoice.id })"
        />

        <!-- Zoom Widget (when not embedded) -->
        <div class="flex items-center bg-white dark:bg-gray-700/90 rounded-lg border border-gray-200/90 dark:border-gray-600 shadow-xs text-xs overflow-hidden h-[30px]">
          <button
            type="button"
            @click="zoomOutScore"
            class="px-2 h-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 font-bold transition-colors cursor-pointer"
            :title="t('toolbar.zoomOut')"
          >−</button>
          <button
            type="button"
            @click="resetZoomScore"
            class="px-1.5 h-full text-[11px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer border-x border-gray-200 dark:border-gray-600"
            :title="t('toolbar.zoomReset')"
          >{{ Math.round(effectiveZoom * 100) }}%</button>
          <button
            type="button"
            @click="zoomInScore"
            class="px-2 h-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 font-bold transition-colors cursor-pointer"
            :title="t('toolbar.zoomIn')"
          >+</button>
        </div>

        <!-- Playback Button -->
        <button
          @click="togglePlay"
          :class="isPlaying ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm transition-colors"
          :title="isPlaying ? t('toolbar.stop') : t('toolbar.play')"
        >
          <svg v-if="!isPlaying" class="w-4 h-4 fill-current" viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
          <svg v-else class="w-4 h-4 fill-current" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
          <span>{{ isPlaying ? t('toolbar.stopBtn') : t('toolbar.listen') }}</span>
        </button>

        <button
          @click="showImportModal = true"
          v-if="!props.readonly"
          class="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
          :title="t('toolbar.import')"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
          <span>{{ t('toolbar.import') }}</span>
        </button>

        <button
          @click="showExportModal = true"
          class="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
          :title="t('toolbar.export')"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
          <span>{{ t('toolbar.export') }}</span>
        </button>
      </div>
    </div>


    <!-- Body: Sidebar + Score -->
    <div class="flex flex-row flex-1 overflow-hidden min-h-[100px]">

      <!-- ===== SIDEBAR ===== -->
      <transition name="sidebar">
        <div
          v-if="!props.readonly && !props.embedded"
          class="flex flex-col bg-gray-50 dark:bg-gray-800/90 border-r border-gray-200 dark:border-gray-700 h-[520px] max-h-[520px] transition-all duration-200 select-none shrink-0"
          :class="sidebarCollapsed ? 'w-9 min-w-[36px]' : 'w-[270px] min-w-[270px]'"
        >
          <!-- Collapsed strip: just the expand button -->
          <div v-if="sidebarCollapsed" class="flex flex-col items-center gap-3 pt-3 h-full overflow-y-auto">
            <button
              @click="sidebarCollapsed = false"
              class="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-indigo-600 transition-colors"
              :title="t('sidebar.expand')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <!-- Mini icons when collapsed -->
            <button @click="chooseDuration('4', false); sidebarCollapsed = false" class="p-1 text-gray-500 hover:text-indigo-600" :title="t('notes.title')">
              <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
            </button>
            <button @click="chooseDuration(selectedDuration, true); sidebarCollapsed = false" class="p-1 text-gray-500 hover:text-indigo-600" :title="t('rests.title')">
              <svg class="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/><path stroke-linecap="round" stroke-linejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/></svg>
            </button>
            <button @click="undo" :disabled="!canUndo" class="p-1 text-gray-500 hover:text-indigo-600 disabled:opacity-30 transition-colors" :title="t('actions.undo')">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"/></svg>
            </button>
            <button @click="redo" :disabled="!canRedo" class="p-1 text-gray-500 hover:text-indigo-600 disabled:opacity-30 transition-colors" :title="t('actions.redo')">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3"/></svg>
            </button>
          </div>

          <!-- Expanded sidebar content -->
          <div v-else class="flex flex-col h-full overflow-hidden">

            <!-- Sidebar header with collapse button (Stays sticky at top) -->
            <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 shrink-0 bg-gray-100/90 dark:bg-gray-800 z-10">
              <span class="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('sidebar.tools') }}</span>
              <button
                @click="sidebarCollapsed = true"
                class="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                :title="t('sidebar.collapse')"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
              </button>
            </div>

            <!-- Scrollable container for ALL tools & sections -->
            <div class="sidebar-scroll-area flex-1 overflow-y-auto overflow-x-hidden py-1">

              <!-- ──── SECTION: Notas ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('notes')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                    {{ t('notes.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.notes ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.notes" class="px-2 pb-2 flex flex-col gap-0.5">
                  <span class="text-[10px] text-gray-500 dark:text-gray-400 px-1">{{ t('notes.stackHint') }}</span>
                  <!-- Semibreve -->
                  <button
                    @click="chooseDuration('1', false)"
                    :class="selectedDuration === '1' && !isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('notes.wholeDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2.5"><ellipse cx="12" cy="12" rx="6" ry="4" transform="rotate(-20 12 12)"/></svg>
                    <span>{{ t('notes.whole') }}</span>
                  </button>
                  <!-- Mínima -->
                  <button
                    @click="chooseDuration('2', false)"
                    :class="selectedDuration === '2' && !isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('notes.halfDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2"><ellipse cx="9" cy="15" rx="4.5" ry="3.2" transform="rotate(-25 9 15)" stroke-width="2.2"/><line x1="13" y1="14" x2="13" y2="4" stroke-width="2" stroke-linecap="round"/></svg>
                    <span>{{ t('notes.half') }}</span>
                  </button>
                  <!-- Semínima -->
                  <button
                    @click="chooseDuration('4', false)"
                    :class="selectedDuration === '4' && !isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('notes.quarterDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><ellipse cx="9" cy="15" rx="4.5" ry="3.2" transform="rotate(-25 9 15)"/><line x1="13" y1="14" x2="13" y2="4" stroke-width="2" stroke-linecap="round"/></svg>
                    <span>{{ t('notes.quarter') }}</span>
                  </button>
                  <!-- Colcheia -->
                  <button
                    @click="chooseDuration('8', false)"
                    :class="selectedDuration === '8' && !isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('notes.eighthDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><ellipse cx="8" cy="15" rx="4.2" ry="3" transform="rotate(-25 8 15)"/><line x1="11.8" y1="14" x2="11.8" y2="4" stroke-width="2" stroke-linecap="round"/><path d="M12 4 Q17 6 15 11" fill="none" stroke-width="2.2" stroke-linecap="round"/></svg>
                    <span>{{ t('notes.eighth') }}</span>
                  </button>
                  <!-- Semicolcheia -->
                  <button
                    @click="chooseDuration('16', false)"
                    :class="selectedDuration === '16' && !isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('notes.sixteenthDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><ellipse cx="8" cy="16" rx="4" ry="2.8" transform="rotate(-25 8 16)"/><line x1="11.5" y1="15" x2="11.5" y2="3" stroke-width="2" stroke-linecap="round"/><path d="M11.5 3.5 Q16.5 5.5 14.5 9" fill="none" stroke-width="2" stroke-linecap="round"/><path d="M11.5 7.5 Q16.5 9.5 14.5 13" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                    <span>{{ t('notes.sixteenth') }}</span>
                  </button>
                  <!-- Fusa (32th) -->
                  <button
                    @click="chooseDuration('32', false)"
                    :class="selectedDuration === '32' && !isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('notes.thirtySecondDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><ellipse cx="8" cy="17" rx="3.8" ry="2.5" transform="rotate(-25 8 17)"/><line x1="11" y1="16" x2="11" y2="2" stroke-width="1.8" stroke-linecap="round"/><path d="M11 2.5 Q15.5 4.5 13.5 7" fill="none" stroke-width="1.8" stroke-linecap="round"/><path d="M11 6 Q15.5 8 13.5 10.5" fill="none" stroke-width="1.8" stroke-linecap="round"/><path d="M11 9.5 Q15.5 11.5 13.5 14" fill="none" stroke-width="1.8" stroke-linecap="round"/></svg>
                    <span>{{ t('notes.thirtySecond') }}</span>
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Pausas ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('rests')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/><path stroke-linecap="round" stroke-linejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/></svg>
                    {{ t('rests.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.rests ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.rests" class="px-2 pb-2 flex flex-col gap-0.5">
                  <!-- Pausa Semibreve -->
                  <button
                    @click="chooseDuration('1', true)"
                    :class="selectedDuration === '1' && isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.wholeDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24"><line x1="2" y1="9" x2="22" y2="9" stroke="currentColor" stroke-width="1.5"/><rect x="7" y="9" width="10" height="5" fill="currentColor"/></svg>
                    <span>{{ t('rests.whole') }}</span>
                  </button>
                  <!-- Pausa Mínima -->
                  <button
                    @click="chooseDuration('2', true)"
                    :class="selectedDuration === '2' && isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.halfDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24"><line x1="2" y1="15" x2="22" y2="15" stroke="currentColor" stroke-width="1.5"/><rect x="7" y="10" width="10" height="5" fill="currentColor"/></svg>
                    <span>{{ t('rests.half') }}</span>
                  </button>
                  <!-- Pausa Semínima -->
                  <button
                    @click="chooseDuration('4', true)"
                    :class="selectedDuration === '4' && isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.quarterDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current" viewBox="0 0 24 24"><path d="M14.2 3.8 c-1.2 1.6 -3.4 3.4 -3.4 5.2 c0 1.2 0.8 2.2 2 2.8 l-3.8 4.6 c1.6 0 3.2 1.2 3.2 2.8 c0 1.6 -1.4 3 -3.2 3 c-1.4 0 -2.6 -0.8 -2.6 -2 c0 -1.6 1.8 -2.8 3.2 -3.4 l2.6 -3.2 c-1.4 -0.6 -2.4 -1.8 -2.4 -3.2 c0 -2.2 2.4 -4.4 4.4 -6.6 z"/></svg>
                    <span>{{ t('rests.quarter') }}</span>
                  </button>
                  <!-- Pausa Colcheia -->
                  <button
                    @click="chooseDuration('8', true)"
                    :class="selectedDuration === '8' && isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.eighthDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><circle cx="9" cy="8" r="2.2"/><path d="M9 8 Q14 8 13 13 L8 21" fill="none" stroke-width="2" stroke-linecap="round"/></svg>
                    <span>{{ t('rests.eighth') }}</span>
                  </button>
                  <!-- Pausa de Semicolcheia -->
                  <button
                    @click="chooseDuration('16', true)"
                    :class="selectedDuration === '16' && isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.sixteenthDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><circle cx="9" cy="6.5" r="2"/><circle cx="8" cy="11.5" r="2"/><path d="M9 6.5 Q14 7 13 11 L7 21" fill="none" stroke-width="1.8" stroke-linecap="round"/><path d="M8 11.5 Q13 12 12 16" fill="none" stroke-width="1.8" stroke-linecap="round"/></svg>
                    <span>{{ t('rests.sixteenth') }}</span>
                  </button>
                  <!-- Pausa de Fusa -->
                  <button
                    @click="chooseDuration('32', true)"
                    :class="selectedDuration === '32' && isRestSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.thirtySecondDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-current stroke-current" viewBox="0 0 24 24"><circle cx="9" cy="5" r="1.8"/><circle cx="8" cy="9" r="1.8"/><circle cx="7" cy="13" r="1.8"/><path d="M9 5 Q13 5.5 12 9 L6 21" fill="none" stroke-width="1.6" stroke-linecap="round"/><path d="M8 9 Q12 9.5 11 13" fill="none" stroke-width="1.6" stroke-linecap="round"/><path d="M7 13 Q11 13.5 10 17" fill="none" stroke-width="1.6" stroke-linecap="round"/></svg>
                    <span>{{ t('rests.thirtySecond') }}</span>
                  </button>
                  <!-- Pausa multicompasso -->
                  <button
                    @click="editMultiRestActiveNote"
                    :class="selectedNote && selectedNote.isRest && selectedNote.multiRest > 1 ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('rests.multiDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2"><line x1="4" y1="12" x2="20" y2="12" stroke-linecap="round"/><line x1="4" y1="8" x2="4" y2="16"/><line x1="20" y1="8" x2="20" y2="16"/></svg>
                    <span>{{ t('rests.multi') }}{{ selectedNote && selectedNote.isRest && selectedNote.multiRest > 1 ? ` (${selectedNote.multiRest})` : '' }}</span>
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Acidentes ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('accidentals')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <span class="font-serif text-sm leading-none">♯</span>
                    {{ t('accidentals.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.accidentals ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.accidentals" class="px-2 pb-2 flex flex-col gap-0.5">
                  <button
                    @click="selectedAccidental = ''"
                    :class="selectedAccidental === '' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('accidentals.natural')"
                  >
                    <span class="font-serif text-sm leading-none w-4 text-center shrink-0">♮</span>
                    <span>{{ t('accidentals.natural') }}</span>
                  </button>
                  <button
                    @click="selectedAccidental = (selectedAccidental === '#' ? '' : '#')"
                    :class="selectedAccidental === '#' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('accidentals.sharp')"
                  >
                    <span class="font-serif text-sm leading-none w-4 text-center shrink-0">♯</span>
                    <span>{{ t('accidentals.sharp') }}</span>
                  </button>
                  <button
                    @click="selectedAccidental = (selectedAccidental === 'b' ? '' : 'b')"
                    :class="selectedAccidental === 'b' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('accidentals.flat')"
                  >
                    <span class="font-serif text-sm leading-none w-4 text-center shrink-0">♭</span>
                    <span>{{ t('accidentals.flat') }}</span>
                  </button>
                  <button
                    @click="selectedAccidental = (selectedAccidental === '##' ? '' : '##')"
                    :class="selectedAccidental === '##' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('accidentals.doubleSharp')"
                  >
                    <span class="font-serif text-sm leading-none w-4 text-center shrink-0">𝄪</span>
                    <span>{{ t('accidentals.doubleSharp') }}</span>
                  </button>
                  <button
                    @click="selectedAccidental = (selectedAccidental === 'bb' ? '' : 'bb')"
                    :class="selectedAccidental === 'bb' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('accidentals.doubleFlat')"
                  >
                    <span class="font-serif text-sm leading-none w-4 text-center shrink-0">𝄫</span>
                    <span>{{ t('accidentals.doubleFlat') }}</span>
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Claves ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('clefsAndPerc')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <span class="text-xs">𝄞/𝄢</span>
                    {{ t('clefs.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.clefsAndPerc ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.clefsAndPerc" class="px-2 pb-2.5 flex flex-col gap-2">
                  <!-- Troca de Clave na Nota Selecionada -->
                  <div>
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('clefs.inlineTitle') }}</span>
                    <div class="grid grid-cols-3 gap-1 pt-1">
                      <button
                        @click="setInlineClef('treble')"
                        :class="selectedNote && selectedNote.inlineClef === 'treble' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('clefs.trebleDesc')"
                      >
                        𝄞 {{ t('clefs.treble') }}
                      </button>
                      <button
                        @click="setInlineClef('bass')"
                        :class="selectedNote && selectedNote.inlineClef === 'bass' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('clefs.bassDesc')"
                      >
                        𝄢 {{ t('clefs.bass') }}
                      </button>
                      <button
                        @click="setInlineClef('alto')"
                        :class="selectedNote && selectedNote.inlineClef === 'alto' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('clefs.altoDesc')"
                      >
                        𝄡 {{ t('clefs.alto') }}
                      </button>
                    </div>
                  </div>

                  <!-- Notehead da Nota Selecionada -->
                  <div>
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('noteheads.title') }}</span>
                    <div class="grid grid-cols-4 gap-1 pt-1">
                      <button
                        @click="setNoteheadType('normal')"
                        :class="!selectedNote?.notehead || selectedNote.notehead === 'normal' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('noteheads.normalDesc')"
                      >
                        𝅘𝅥
                      </button>
                      <button
                        @click="setNoteheadType('x')"
                        :class="selectedNote?.notehead === 'x' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors font-bold"
                        :title="t('noteheads.xDesc')"
                      >
                        ✕
                      </button>
                      <button
                        @click="setNoteheadType('diamond')"
                        :class="selectedNote?.notehead === 'diamond' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('noteheads.diamondDesc')"
                      >
                        ◇
                      </button>
                      <button
                        @click="setNoteheadType('slash')"
                        :class="selectedNote?.notehead === 'slash' ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="px-2 py-1.5 rounded text-xs border border-gray-200 dark:border-gray-600 transition-colors font-bold"
                        :title="t('noteheads.slashDesc')"
                      >
                        /
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ──── SECTION: Bateria ──── -->
              <div v-if="activeVoice && activeVoice.instrument === 'drum_kit'" class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('drums')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <span class="text-xs">🥁</span>
                    {{ t('drums.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.drums ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.drums" class="px-2 pb-2.5 grid grid-cols-3 gap-1">
                  <button
                    v-for="piece in drumPieces"
                    :key="piece.id"
                    @click="insertDrumPiece(piece.id)"
                    class="px-1.5 py-1.5 rounded-md text-[11px] font-semibold bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-colors"
                    :title="t('drums.' + piece.id + 'Desc')"
                  >
                    {{ t('drums.' + piece.id) }}
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Modificadores & Articulações ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('modifiers')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <span class="font-serif text-sm leading-none">𝄐</span>
                    {{ t('modifiers.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.modifiers ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.modifiers" class="px-2 pb-2 flex flex-col gap-0.5">
                  <button
                    @click="cycleDotsSelected"
                    :class="(isDottedSelected || selectedDotsCount > 0) ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.dottedDesc')"
                  >
                    <span class="font-bold text-base w-4 text-center shrink-0">{{ selectedDotsCount >= 2 ? '••' : '•' }}</span>
                    <span>{{ t('modifiers.dotted') }}</span>
                  </button>
                  <button
                    @click="toggleArticulationActiveNote('staccato')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.staccatoDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">·</span>
                    <span>{{ t('modifiers.staccato') }}</span>
                  </button>
                  <button
                    @click="toggleArticulationActiveNote('accent')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.accentDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">&gt;</span>
                    <span>{{ t('modifiers.accent') }}</span>
                  </button>
                  <button
                    @click="toggleArticulationActiveNote('marcato')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.marcatoDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">^</span>
                    <span>{{ t('modifiers.marcato') }}</span>
                  </button>
                  <button
                    @click="toggleGraceActiveNote"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.graceNoteDesc')"
                  >
                    <span class="font-serif italic font-bold text-xs w-4 text-center shrink-0">𝅘</span>
                    <span>{{ t('modifiers.graceNote') }}</span>
                  </button>
                  <button
                    @click="toggleGlissandoActiveNote"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.glissandoDesc')"
                  >
                    <span class="font-serif italic font-bold text-xs w-4 text-center shrink-0">gliss</span>
                    <span>{{ t('modifiers.glissando') }}</span>
                  </button>
                  <button
                    @click="toggleCautionaryActiveNote"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.cautionaryDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0">(♯)</span>
                    <span>{{ t('modifiers.cautionary') }}</span>
                  </button>
                  <button
                    @click="toggleArticulationActiveNote('fermata')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.fermataDesc')"
                  >
                    <span class="font-bold text-base w-4 text-center shrink-0 leading-none">𝄐</span>
                    <span>{{ t('modifiers.fermata') }}</span>
                  </button>
                  <button
                    @click="toggleArticulationActiveNote('downbow')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.downBowDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0">⨅</span>
                    <span>{{ t('modifiers.downBow') }}</span>
                  </button>
                  <button
                    @click="toggleArticulationActiveNote('upbow')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.upBowDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0">V</span>
                    <span>{{ t('modifiers.upBow') }}</span>
                  </button>
                  <button
                    @click="toggleTupletActiveNote"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.tupletDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0 font-mono">(3)</span>
                    <span>{{ t('modifiers.tuplet') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('coda')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.codaDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">𝄌</span>
                    <span>{{ t('modifiers.coda') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('segno')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.segnoDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">𝄋</span>
                    <span>{{ t('modifiers.segno') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('fine')"
                    :class="selectedNote && selectedNote.navigation === 'fine' ? 'bg-indigo-600 text-white font-bold' : ''"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.navFineDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">Fin</span>
                    <span>{{ t('modifiers.navFine') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('to coda')"
                    :class="selectedNote && selectedNote.navigation === 'to coda' ? 'bg-indigo-600 text-white font-bold' : ''"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.navToCodaDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">→𝄌</span>
                    <span>{{ t('modifiers.navToCoda') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('d.s. al fine')"
                    :class="selectedNote && selectedNote.navigation === 'd.s. al fine' ? 'bg-indigo-600 text-white font-bold' : ''"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.navDsAlFineDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">D.S</span>
                    <span>{{ t('modifiers.navDsAlFine') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('d.c. al fine')"
                    :class="selectedNote && selectedNote.navigation === 'd.c. al fine' ? 'bg-indigo-600 text-white font-bold' : ''"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.navDcAlFineDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">D.C</span>
                    <span>{{ t('modifiers.navDcAlFine') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('d.s. al coda')"
                    :class="selectedNote && selectedNote.navigation === 'd.s. al coda' ? 'bg-indigo-600 text-white font-bold' : ''"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.navDsAlCodaDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">S𝄌</span>
                    <span>{{ t('modifiers.navDsAlCoda') }}</span>
                  </button>
                  <button
                    @click="toggleNavigationActiveNote('d.c. al coda')"
                    :class="selectedNote && selectedNote.navigation === 'd.c. al coda' ? 'bg-indigo-600 text-white font-bold' : ''"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.navDcAlCodaDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">C𝄌</span>
                    <span>{{ t('modifiers.navDcAlCoda') }}</span>
                  </button>
                  <button
                    @click="toggleOrnamentActiveNote('trill')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.trillDesc')"
                  >
                    <span class="font-serif italic font-extrabold text-xs w-4 text-center shrink-0">tr</span>
                    <span>{{ t('modifiers.trill') }}</span>
                  </button>
                  <button
                    @click="toggleOrnamentActiveNote('turn')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.turnDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">∽</span>
                    <span>{{ t('modifiers.turn') }}</span>
                  </button>
                  <button
                    @click="toggleOrnamentActiveNote('mordent')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.mordentDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">≁</span>
                    <span>{{ t('modifiers.mordent') }}</span>
                  </button>
                  <button
                    @click="toggleHairpinActiveNote('crescendo-start')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.crescendoDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">&lt;</span>
                    <span>{{ t('modifiers.crescendo') }}</span>
                  </button>
                  <button
                    @click="toggleHairpinActiveNote('diminuendo-start')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.diminuendoDesc')"
                  >
                    <span class="font-bold text-sm w-4 text-center shrink-0">&gt;</span>
                    <span>{{ t('modifiers.diminuendo') }}</span>
                  </button>
                  <button
                    @click="toggleHairpinActiveNote('crescendo-end')"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('modifiers.hairpinEndDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0">|</span>
                    <span>{{ t('modifiers.hairpinEnd') }}</span>
                  </button>
                  <button
                    @click="toggleOttavaStartActiveNote('8va')"
                    :class="selectedNote && selectedNote.ottavaStart === '8va' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.ottava8vaDesc')"
                  >
                    <span class="font-bold text-[10px] w-4 text-center shrink-0 font-mono">8va</span>
                    <span>{{ t('modifiers.ottava8va') }}</span>
                  </button>
                  <button
                    @click="toggleOttavaStartActiveNote('8vb')"
                    :class="selectedNote && selectedNote.ottavaStart === '8vb' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.ottava8vbDesc')"
                  >
                    <span class="font-bold text-[10px] w-4 text-center shrink-0 font-mono">8vb</span>
                    <span>{{ t('modifiers.ottava8vb') }}</span>
                  </button>
                  <button
                    @click="toggleOttavaEndActiveNote"
                    :class="selectedNote && selectedNote.ottavaEnd ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.ottavaEndDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0">┘</span>
                    <span>{{ t('modifiers.ottavaEnd') }}</span>
                  </button>
                  <button
                    @click="toggleArpeggioActiveNote"
                    :class="selectedNote && selectedNote.arpeggio ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.arpeggioDesc')"
                  >
                    <span class="font-serif font-bold text-sm w-4 text-center shrink-0">𝄔</span>
                    <span>{{ t('modifiers.arpeggio') }}</span>
                  </button>
                  <button
                    @click="cycleTremoloActiveNote"
                    :class="selectedNote && selectedNote.tremolo ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.tremoloDesc')"
                  >
                    <span class="font-bold text-xs w-4 text-center shrink-0 font-mono">{{ selectedNote && selectedNote.tremolo ? '///'.substring(0, selectedNote.tremolo) : '///' }}</span>
                    <span>{{ t('modifiers.tremolo') }}</span>
                  </button>
                  <button
                    @click="toggleBreathActiveNote"
                    :class="selectedNote && selectedNote.breath ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.breathDesc')"
                  >
                    <span class="font-serif font-bold text-base w-4 text-center shrink-0 leading-none">,</span>
                    <span>{{ t('modifiers.breath') }}</span>
                  </button>
                  <button
                    @click="togglePedalActiveNote('start')"
                    :class="selectedNote && selectedNote.pedalStart ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.pedalStartDesc')"
                  >
                    <span class="font-serif italic font-bold text-[10px] w-4 text-center shrink-0">Ped</span>
                    <span>{{ t('modifiers.pedalStart') }}</span>
                  </button>
                  <button
                    @click="togglePedalActiveNote('end')"
                    :class="selectedNote && selectedNote.pedalEnd ? 'bg-indigo-600 text-white font-bold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs transition-all duration-150"
                    :title="t('modifiers.pedalEndDesc')"
                  >
                    <span class="font-serif font-bold text-xs w-4 text-center shrink-0">*</span>
                    <span>{{ t('modifiers.pedalEnd') }}</span>
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Ligaduras (Ties & Slurs) ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('ligatures')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" d="M3 16 C 8 8, 16 8, 21 16"/></svg>
                    {{ t('ligatures.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.ligatures ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.ligatures" class="px-2 pb-2 flex flex-col gap-0.5">
                  <button
                    @click="toggleTieActiveNote"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('ligatures.tieDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2.2"><path stroke-linecap="round" d="M4 14 Q 12 7, 20 14"/></svg>
                    <span>{{ t('ligatures.tie') }}</span>
                  </button>
                  <button
                    @click="toggleSlurActiveNote"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-150"
                    :title="t('ligatures.slurDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2.2"><path stroke-linecap="round" d="M3 16 C 8 7, 16 7, 21 16"/></svg>
                    <span>{{ t('ligatures.slur') }}</span>
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Articulações TAB ──── -->
              <div v-if="activeVoice && (activeVoice.showTab || distinctStaves.some(s => s.showTab))" class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('tabArticulations')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <span class="text-xs">🎸</span>
                    {{ t('tabArticulations.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.tabArticulations ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.tabArticulations" class="px-2 pb-2.5 flex flex-col gap-1.5">
                  <!-- Dica de atalhos e edição -->
                  <div class="px-2 py-1.5 bg-indigo-50/80 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/40 rounded-md text-[10px] text-indigo-700 dark:text-indigo-300 leading-tight">
                    {{ t('tabArticulations.editHint') }}
                  </div>

                  <!-- Seletor de Corda e Traste -->
                  <div class="flex items-center justify-between gap-1 p-1.5 bg-gray-50 dark:bg-gray-800/80 rounded-md border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center gap-1">
                      <span class="text-[10px] font-semibold text-gray-500 dark:text-gray-400">{{ t('tabArticulations.string') }}:</span>
                      <select
                        :value="activeTabString"
                        @change="handleTabStringChange(parseInt($event.target.value, 10))"
                        class="text-xs font-bold py-0.5 px-1 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                      >
                        <option v-for="s in 6" :key="'tab-str-' + s" :value="s">
                          {{ s }}ª ({{ ['E', 'B', 'G', 'D', 'A', 'E'][s - 1] }})
                        </option>
                      </select>
                    </div>
                    <div class="flex items-center gap-1">
                      <span class="text-[10px] font-semibold text-gray-500 dark:text-gray-400">{{ t('tabArticulations.fret') }}:</span>
                      <button
                        @click="stepFret(-1)"
                        class="w-5 h-5 flex items-center justify-center rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-xs font-bold hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 shadow-sm"
                        :title="t('tab.fretDown', {}, '-1 Traste')"
                      >-</button>
                      <span class="w-6 text-center font-extrabold text-xs text-indigo-600 dark:text-indigo-400">
                        {{ currentTabFretDisplay }}
                      </span>
                      <button
                        @click="stepFret(1)"
                        class="w-5 h-5 flex items-center justify-center rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-xs font-bold hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 shadow-sm"
                        :title="t('tab.fretUp', {}, '+1 Traste')"
                      >+</button>
                    </div>
                  </div>

                  <!-- Slides Grid -->
                  <div class="grid grid-cols-2 gap-1">
                    <button
                      @click="toggleTabSlide('up')"
                      :class="activeNoteHasTabSlide('up') ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.slideUpDesc')"
                    >
                      <span class="font-bold text-sm">/</span>
                      <span>{{ t('tabArticulations.slideUp') }}</span>
                    </button>
                    <button
                      @click="toggleTabSlide('down')"
                      :class="activeNoteHasTabSlide('down') ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.slideDownDesc')"
                    >
                      <span class="font-bold text-sm">\</span>
                      <span>{{ t('tabArticulations.slideDown') }}</span>
                    </button>
                  </div>

                  <!-- Bends Grid -->
                  <div class="grid grid-cols-4 gap-1">
                    <button
                      @click="toggleTabBend('full')"
                      :class="activeNoteHasTabBend('full') ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.bendFullDesc')"
                    >
                      <span class="text-[10px] font-bold">⤴</span>
                      <span>Full</span>
                    </button>
                    <button
                      @click="toggleTabBend('1/2')"
                      :class="activeNoteHasTabBend('1/2') ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.bendHalfDesc')"
                    >
                      <span class="text-[10px] font-bold">⤴</span>
                      <span>1/2</span>
                    </button>
                    <button
                      @click="toggleTabBend('1/4')"
                      :class="activeNoteHasTabBend('1/4') ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.bendQuarterDesc')"
                    >
                      <span class="text-[10px] font-bold">⤴</span>
                      <span>1/4</span>
                    </button>
                    <button
                      @click="toggleTabBend('release')"
                      :class="activeNoteHasTabBend('release') ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.bendReleaseDesc')"
                    >
                      <span class="text-[10px] font-bold">↷</span>
                      <span>Rel.</span>
                    </button>
                  </div>

                  <!-- Hammer-on / Pull-off & Vibrato -->
                  <div class="grid grid-cols-2 gap-1">
                    <button
                      @click="toggleTabHammerOrPull"
                      :class="(activeNoteHasTabHammer() || activeNoteHasTabPull()) ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.hammerPullDesc')"
                    >
                      <span class="font-extrabold text-xs">H/P</span>
                      <span>Legato</span>
                    </button>
                    <button
                      @click="toggleTabVibrato"
                      :class="activeNoteHasTabVibrato() ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.vibratoDesc')"
                    >
                      <span class="font-bold text-xs">〰</span>
                      <span>Vibrato</span>
                    </button>
                  </div>

                  <!-- Harmônico, Dead Note, Ghost Note, Palm Mute -->
                  <div class="grid grid-cols-4 gap-1">
                    <button
                      @click="toggleTabHarmonic"
                      :class="activeNoteHasTabHarmonic() ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.harmonicDesc')"
                    >
                      <span class="font-bold text-[10px]">&lt; &gt;</span>
                      <span class="text-[10px]">Harm.</span>
                    </button>
                    <button
                      @click="toggleTabDead"
                      :class="activeNoteHasTabDead() ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.deadNoteDesc')"
                    >
                      <span class="font-bold text-[10px]">✕</span>
                      <span class="text-[10px]">Mute</span>
                    </button>
                    <button
                      @click="toggleTabGhost"
                      :class="activeNoteHasTabGhost() ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.ghostNoteDesc')"
                    >
                      <span class="font-bold text-[10px]">( )</span>
                      <span class="text-[10px]">Ghost</span>
                    </button>
                    <button
                      @click="toggleTabPalmMute"
                      :class="activeNoteHasTabPalmMute() ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex flex-col items-center justify-center py-1 rounded-md text-[11px] transition-all duration-150 shadow-sm"
                      :title="t('tabArticulations.palmMuteDesc')"
                    >
                      <span class="font-bold text-[10px]">P.M.</span>
                      <span class="text-[10px]">Palm</span>
                    </button>
                  </div>

                  <!-- Limpar -->
                  <button
                    @click="clearTabArticulations"
                    class="flex items-center justify-center gap-1 w-full px-2 py-1 rounded-md text-[11px] text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-150"
                    :title="t('tabArticulations.clearDesc')"
                  >
                    <span>✕</span>
                    <span>{{ t('tabArticulations.clear') }}</span>
                  </button>
                </div>
              </div>

              <!-- ──── SECTION: Acordes / Cifras (Chords) ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('chords')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 fill-none stroke-current" viewBox="0 0 24 24" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 4v16M16 4v16M4 8h16M4 16h16"/></svg>
                    {{ t('chords.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.chords ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.chords" class="px-2 pb-2.5 flex flex-col gap-1.5">
                  <div class="flex items-center gap-1">
                    <input
                      type="text"
                      v-model="chordInputText"
                      @keydown.enter="applyChordToSelectedNote"
                      :placeholder="t('chords.placeholder')"
                      class="w-full text-xs p-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-800 dark:text-gray-100 font-bold focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    <button
                      @click="applyChordToSelectedNote"
                      class="px-2 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-semibold shrink-0"
                      :title="t('chords.applyDesc')"
                    >
                      {{ t('chords.apply') }}
                    </button>
                  </div>
                  <!-- Quick Chord Presets -->
                  <div class="grid grid-cols-4 gap-1 pt-1">
                    <button
                      v-for="ch in ['C', 'Dm', 'Em', 'F', 'G', 'Am', 'Bdim', 'G7']"
                      :key="ch"
                      @click="setChordActiveNote(ch)"
                      class="px-1.5 py-1 text-[11px] font-bold bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-indigo-500 rounded text-gray-700 dark:text-gray-200 hover:text-indigo-600 transition-colors shadow-2xs"
                    >
                      {{ ch }}
                    </button>
                  </div>
                  <button
                    @click="setChordActiveNote('')"
                    class="w-full py-1 text-[10px] text-gray-400 hover:text-red-500 transition-colors text-center"
                  >
                    {{ t('chords.remove') }}
                  </button>

                  <!-- Toggle Diagramas de Braço de Violão/Guitarra -->
                  <div class="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700/60 mt-1">
                    <span class="text-[11px] font-medium text-gray-600 dark:text-gray-300">{{ t('chords.guitarDiagrams') }}</span>
                    <button
                      @click="showGuitarFrames = !showGuitarFrames"
                      :class="showGuitarFrames ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'"
                      class="px-2 py-0.5 rounded text-[10px] font-bold transition-colors"
                      :title="t('chords.guitarDiagramsDesc')"
                    >
                      {{ showGuitarFrames ? t('chords.on') : t('chords.off') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- ──── SECTION: Estrutura ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('structure')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/></svg>
                    {{ t('structure.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.structure ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.structure" class="px-2 pb-2 flex flex-col gap-1.5">
                  <!-- Clave -->
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.clef') }}</span>
                    <MusicSheetSelect
                      v-model="selectedClef"
                      :options="clefOptions"
                      button-class="w-full justify-between bg-white dark:bg-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-600 text-xs font-semibold text-gray-800 dark:text-gray-100"
                      menu-class="w-full"
                    />
                  </div>
                  <!-- Compasso -->
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.timeSignature') }}</span>
                    <MusicSheetSelect
                      v-model="selectedTimeSignature"
                      :options="timeSignatureOptions"
                      button-class="w-full justify-between bg-white dark:bg-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-600 text-xs font-semibold text-gray-800 dark:text-gray-100"
                      menu-class="w-full"
                    />
                  </div>
                  <!-- Tom -->
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.keySignature') }}</span>
                    <MusicSheetSelect
                      v-model="selectedKey"
                      :options="keyOptions"
                      button-class="w-full justify-between bg-white dark:bg-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-600 text-xs font-semibold text-gray-800 dark:text-gray-100"
                      menu-class="w-full"
                    />
                  </div>
                  <!-- Mudanças no meio da peça (no compasso da nota selecionada) -->
                  <div class="flex flex-col gap-1">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.midChanges') }}</span>
                    <div class="grid grid-cols-2 gap-1">
                      <button
                        @click="toggleKeyChangeHere"
                        class="px-2 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-gray-600 transition-colors"
                        :class="measureOfSelectedNote() !== null && keyChangeAt(measureOfSelectedNote()) ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        :title="t('structure.midKeyDesc')"
                      >
                        {{ t('structure.midKey') }}
                      </button>
                      <button
                        @click="toggleTimeChangeHere"
                        class="px-2 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-gray-600 transition-colors"
                        :class="measureOfSelectedNote() !== null && timeChangeAt(measureOfSelectedNote()) ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        :title="t('structure.midTimeDesc')"
                      >
                        {{ t('structure.midTime') }}
                      </button>
                    </div>
                    <div v-if="keyChanges.length > 0 || timeChanges.length > 0" class="flex flex-wrap gap-1 pt-0.5">
                      <span
                        v-for="kc in keyChanges"
                        :key="'kchip-' + kc.measure"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                      >
                        C{{ kc.measure + 1 }}:{{ kc.key }}
                        <button @click="removeKeyChange(kc.measure)" class="font-bold hover:text-red-600" :title="t('actions.remove', {}, 'Remover')">×</button>
                      </span>
                      <span
                        v-for="tc in timeChanges"
                        :key="'tchip-' + tc.measure"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200"
                      >
                        C{{ tc.measure + 1 }}:{{ tc.time }}
                        <button @click="removeTimeChange(tc.measure)" class="font-bold hover:text-red-600" :title="t('actions.remove', {}, 'Remover')">×</button>
                      </span>
                    </div>
                  </div>
                  <!-- Marca de ensaio na nota selecionada -->
                  <div class="flex flex-col gap-0.5">
                    <button
                      @click="editRehearsalActiveNote"
                      class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-gray-600 transition-colors bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                      :title="t('structure.rehearsalDesc')"
                    >
                      <span class="inline-flex items-center justify-center w-4 h-4 rounded-sm border border-current text-[9px] font-extrabold shrink-0">A</span>
                      <span>{{ t('structure.rehearsal') }}{{ selectedNote && selectedNote.rehearsal ? `: ${selectedNote.rehearsal}` : '' }}</span>
                    </button>
                  </div>
                  <!-- Andamento no meio da peça (no compasso da nota selecionada) -->
                  <div class="flex flex-col gap-1">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.tempoChanges') }}</span>
                    <div class="grid grid-cols-3 gap-1">
                      <button
                        @click="setBpmHere"
                        class="px-1.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-gray-600 transition-colors"
                        :class="measureOfSelectedNote() !== null && tempoChangeAt(measureOfSelectedNote()) ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        :title="t('structure.tempoBpmDesc')"
                      >
                        {{ t('structure.tempoBpm') }}
                      </button>
                      <button
                        @click="startRampHere('rit')"
                        class="px-1.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-gray-600 transition-colors"
                        :class="measureOfSelectedNote() !== null && tempoRampAt(measureOfSelectedNote()) ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        :title="t('structure.ritDesc')"
                      >
                        {{ t('structure.rit') }}
                      </button>
                      <button
                        @click="startRampHere('accel')"
                        class="px-1.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 dark:border-gray-600 transition-colors"
                        :class="measureOfSelectedNote() !== null && tempoRampAt(measureOfSelectedNote()) ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        :title="t('structure.accelDesc')"
                      >
                        {{ t('structure.accel') }}
                      </button>
                    </div>
                    <div v-if="tempoChanges.length > 0 || tempoRamps.length > 0" class="flex flex-wrap gap-1 pt-0.5">
                      <span
                        v-for="tc in tempoChanges"
                        :key="'bpmchip-' + tc.measure"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
                      >
                        C{{ tc.measure + 1 }}:♩={{ tc.bpm }}
                        <button @click="removeTempoChange(tc.measure)" class="font-bold hover:text-red-600" :title="t('actions.remove', {}, 'Remover')">×</button>
                      </span>
                      <span
                        v-for="tr in tempoRamps"
                        :key="'rampchip-' + tr.fromMeasure"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200"
                      >
                        {{ tr.kind === 'accel' ? t('structure.accel') : t('structure.rit') }} C{{ tr.fromMeasure + 1 }}→{{ tr.toBpm }}
                        <button @click="removeTempoRamp(tr.fromMeasure)" class="font-bold hover:text-red-600" :title="t('actions.remove', {}, 'Remover')">×</button>
                      </span>
                    </div>
                  </div>
                  <!-- Transpor (semitons) -->
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.transpose') }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        @click="transposeScore(-1)"
                        class="flex-1 py-1.5 rounded-lg text-xs font-bold bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('structure.transposeDownDesc')"
                      >
                        − ♭
                      </button>
                      <button
                        @click="transposeScore(1)"
                        class="flex-1 py-1.5 rounded-lg text-xs font-bold bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-colors"
                        :title="t('structure.transposeUpDesc')"
                      >
                        + ♯
                      </button>
                    </div>
                  </div>
                  <!-- Swing (playback feel) -->
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('structure.swing') }}</span>
                    <div class="flex items-center gap-1">
                      <button
                        @click="swing.on = !swing.on; notifyChange()"
                        :class="swing.on ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="flex-1 py-1.5 rounded-lg text-[11px] transition-colors border border-gray-200 dark:border-gray-600"
                        :title="t('structure.swingDesc')"
                      >
                        {{ swing.on ? t('structure.swingOn') : t('structure.swingOff') }}
                      </button>
                      <MusicSheetSelect
                        :model-value="swing.amount"
                        @update:model-value="swing.amount = parseFloat($event); notifyChange()"
                        :options="swingOptions"
                        button-class="w-24 justify-between bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-[11px] font-semibold text-gray-700 dark:text-gray-200"
                        menu-class="w-32"
                        :title="t('structure.swingAmountDesc')"
                      />
                    </div>
                  </div>
                  <!-- Layout -->
                  <div class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase px-1">{{ t('toolbar.wrapMode') }}</span>
                    <div class="flex items-center bg-white dark:bg-gray-700 p-0.5 rounded-lg border border-gray-200 dark:border-gray-600">
                      <button
                        @click="selectedWrapMode = 'single'"
                        :class="selectedWrapMode === 'single' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 rounded-md text-[10px] transition-colors"
                        :title="t('toolbar.wrapSingle')"
                      >
                        <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                        <span>1 Linha</span>
                      </button>
                      <button
                        @click="selectedWrapMode = 'wrap'"
                        :class="selectedWrapMode === 'wrap' ? 'bg-indigo-600 text-white font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'"
                        class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 rounded-md text-[10px] transition-colors"
                        :title="t('toolbar.wrapBreak')"
                      >
                        <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                        <span>{{ t('structure.break') }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ──── SECTION: Letra (Lyrics) ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('lyrics')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/></svg>
                    {{ t('lyrics.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.lyrics ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.lyrics" class="px-2 pb-2.5 flex flex-col gap-1.5">
                  <span class="text-[10px] text-gray-500 dark:text-gray-400">{{ t('lyrics.helper') }}</span>
                  <div class="flex items-center gap-1">
                    <button
                      @click="lyricsVerse = 1"
                      :class="lyricsVerse === 1 ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex-1 py-1 rounded text-[11px] border border-gray-200 dark:border-gray-600 transition-colors"
                      :title="t('lyrics.verse1Desc')"
                    >
                      {{ t('lyrics.verse1') }}
                    </button>
                    <button
                      @click="lyricsVerse = 2"
                      :class="lyricsVerse === 2 ? 'bg-indigo-600 text-white font-bold' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'"
                      class="flex-1 py-1 rounded text-[11px] border border-gray-200 dark:border-gray-600 transition-colors"
                      :title="t('lyrics.verse2Desc')"
                    >
                      {{ t('lyrics.verse2') }}
                    </button>
                  </div>
                  <textarea
                    v-model="lyricsInputText"
                    rows="3"
                    class="w-full text-xs p-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-sans resize-none"
                    :placeholder="t('lyrics.placeholder')"
                  ></textarea>
                  <div class="flex items-center gap-1">
                    <button
                      @click="applyLyricsToActiveVoice"
                      class="flex-1 py-1 px-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[11px] font-semibold transition-colors shadow-xs"
                      :title="t('lyrics.applyDesc')"
                    >
                      {{ t('lyrics.apply') }}
                    </button>
                    <button
                      @click="clearLyricsFromActiveVoice"
                      class="py-1 px-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-[11px] transition-colors"
                      :title="t('lyrics.clearDesc')"
                    >
                      {{ t('lyrics.clear') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- ──── SECTION: Vozes ──── -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <button
                  @click="toggleSection('voices')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    {{ t('voices.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.voices ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.voices" class="px-2 pb-2 flex flex-col gap-0.5">
                  <label class="flex items-center gap-1.5 px-1 pt-1 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span class="shrink-0">{{ t('voices.part') }}</span>
                    <MusicSheetSelect
                      :model-value="partVoiceId || ''"
                      @update:model-value="selectPart($event || null)"
                      :options="partOptions"
                      root-class="flex-1 min-w-0"
                      button-class="w-full justify-between bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 text-[11px] font-semibold text-gray-700 dark:text-gray-200"
                      menu-class="w-48"
                      :title="t('voices.partDesc')"
                    />
                  </label>
                  <div
                    v-for="v in voices"
                    :key="v.id"
                    :class="[
                      activeVoiceId === v.id ? 'bg-indigo-600 text-white shadow-xs' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600',
                      v.visible === false ? 'opacity-50' : ''
                    ]"
                    class="flex flex-col gap-1.5 w-full p-2 rounded-lg text-xs transition-all border border-transparent"
                    :style="activeVoiceId !== v.id ? { borderColor: getVoiceColor(v.id) + '40' } : {}"
                  >
                    <!-- Row 1: Voice name & visibility toggle -->
                    <div class="flex items-center justify-between gap-1.5 w-full">
                      <button
                        @click="setActiveVoice(v.id)"
                        class="flex items-center gap-1.5 flex-1 min-w-0 font-bold focus:outline-none text-left"
                        :title="t('voices.select', { name: v.name || v.id })"
                      >
                        <span class="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-white/30" :style="{ backgroundColor: getVoiceColor(v.id) }"></span>
                        <span class="truncate text-[11px]">{{ v.name || v.id }}</span>
                      </button>
                      <button
                        @click.stop="toggleVoiceVisibility(v.id)"
                        :class="activeVoiceId === v.id ? 'text-indigo-200 hover:text-white' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-100'"
                        class="p-0.5 shrink-0 rounded transition-colors focus:outline-none hover:bg-black/10 dark:hover:bg-white/10"
                        :title="v.visible !== false ? t('voices.hide') : t('voices.show')"
                      >
                        <svg v-if="v.visible !== false" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"/></svg>
                      </button>
                    </div>

                    <!-- Row 2: Controls (Staff, TAB, Capo, Transpose) -->
                    <div class="flex items-center gap-1.5 w-full">
                      <MusicSheetSelect
                        :model-value="v.staffIndex ?? 0"
                        :options="staffOptions"
                        root-class="flex-1 min-w-0"
                        align="left"
                        @update:model-value="setVoiceStaff(v.id, $event)"
                        :button-class="activeVoiceId === v.id ? 'w-full bg-indigo-700/90 hover:bg-indigo-700 text-white border-indigo-400/50 px-1.5 py-0.5 text-[10px]' : 'w-full bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-[10px]'"
                        menu-class="w-32"
                        :title="t('voices.staffDesc')"
                      />
                      <button
                        @click.stop="cycleVoiceTab(v.id)"
                        :class="[
                          v.showTab && v.tabOnly ? 'bg-emerald-600 text-white' : (v.showTab ? 'bg-indigo-500 text-white' : (activeVoiceId === v.id ? 'bg-indigo-700/80 text-indigo-100 hover:text-white border border-indigo-400/40' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-600')),
                        ]"
                        class="px-1.5 py-0.5 shrink-0 rounded text-[9px] font-extrabold italic transition-colors focus:outline-none"
                        :title="v.showTab && v.tabOnly ? t('voices.tabOnlyActive') : t('voices.tabDesc')"
                      >
                        {{ v.showTab && v.tabOnly ? 'TABI' : 'TAB' }}
                      </button>
                      <MusicSheetSelect
                        v-if="v.showTab"
                        :model-value="v.capo || 0"
                        :options="capoOptions"
                        root-class="shrink-0"
                        align="right"
                        @update:model-value="setVoiceCapo(v.id, $event)"
                        :button-class="activeVoiceId === v.id ? 'bg-indigo-700/90 hover:bg-indigo-700 text-white border-indigo-400/50 px-1.5 py-0.5 text-[10px]' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-[10px]'"
                        menu-class="w-28"
                        :title="t('voices.capoDesc')"
                      />
                      <MusicSheetSelect
                        :model-value="v.transpose || 0"
                        :options="transposeOptions"
                        root-class="shrink-0"
                        align="right"
                        @update:model-value="setVoiceTranspose(v.id, $event)"
                        :button-class="activeVoiceId === v.id ? 'bg-indigo-700/90 hover:bg-indigo-700 text-white border-indigo-400/50 px-1.5 py-0.5 text-[10px]' : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-[10px]'"
                        menu-class="w-24"
                        :title="t('voices.transposeDesc')"
                      />
                    </div>
                  </div>
                  <!-- Add / Remove voice buttons -->
                  <div class="flex items-center gap-1 mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                    <button
                      @click="addNewVoice"
                      class="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-md transition-colors font-semibold"
                      :title="t('voices.addDesc')"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                      {{ t('voices.add') }}
                    </button>
                    <button
                      v-if="voices.length > 1"
                      @click="removeCurrentVoice"
                      class="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors font-semibold"
                      :title="t('voices.removeDesc')"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      {{ t('voices.remove') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- ──── SECTION: Ações ──── -->
              <div>
                <button
                  @click="toggleSection('actions')"
                  class="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 uppercase tracking-wider transition-colors"
                >
                  <span class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                    {{ t('actions.title') }}
                  </span>
                  <svg class="w-3 h-3 transition-transform" :class="collapsedSections.actions ? '' : 'rotate-180'" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/></svg>
                </button>
                <div v-show="!collapsedSections.actions" class="px-2 pb-3 flex flex-col gap-0.5">
                  <!-- Undo -->
                  <button
                    @click="undo"
                    :disabled="!canUndo"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    :title="t('actions.undoDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"/></svg>
                    <span>{{ t('actions.undo') }}</span>
                    <span class="ml-auto text-[10px] text-gray-400 font-mono">Ctrl+Z</span>
                  </button>
                  <!-- Redo -->
                  <button
                    @click="redo"
                    :disabled="!canRedo"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    :title="t('actions.redoDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3"/></svg>
                    <span>{{ t('actions.redo') }}</span>
                    <span class="ml-auto text-[10px] text-gray-400 font-mono">Ctrl+Y</span>
                  </button>
                  <!-- Divider -->
                  <div class="h-px bg-gray-200 dark:bg-gray-700 my-0.5"></div>
                  <!-- Clear -->
                  <button
                    @click="clearScore"
                    :disabled="totalNotesCount === 0"
                    class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    :title="t('actions.clearDesc')"
                  >
                    <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    <span>{{ t('actions.clear') }}</span>
                  </button>
                </div>
              </div>

            </div><!-- end scrollable sections -->
          </div><!-- end expanded content -->
        </div>
      </transition>

      <!-- ===== MAIN SCORE VIEW ===== -->
      <div
        ref="scoreScrollContainer"
        class="relative focus:outline-none"
        :class="[
          props.embedded ? (isFittingContainer ? 'w-full min-w-0' : 'shrink-0') : 'flex-1',
          props.embedded
            ? (isFittingContainer
                ? 'bg-transparent p-0 min-h-0 cursor-default w-full h-fit ' + (effectiveZoom > 1 ? 'overflow-x-auto' : 'overflow-hidden')
                : 'bg-transparent p-0 min-h-0 cursor-default overflow-x-auto w-fit max-w-full h-fit')
            : (props.readonly ? 'bg-white dark:bg-gray-900 p-4 min-h-[160px] cursor-default' : 'bg-white dark:bg-gray-900 p-4 min-h-[220px] cursor-crosshair'),
          !props.embedded && selectedWrapMode === 'single' ? 'overflow-x-auto overflow-y-hidden' : '',
          !props.embedded && selectedWrapMode !== 'single' ? 'overflow-x-hidden overflow-y-auto max-h-[600px]' : ''
        ]"
        :style="scoreContainerStyle"
        tabindex="0"
        @mousemove="handleStaffMouseMove"
        @mouseleave="handleStaffMouseLeave"
        @click="handleStaffClick"
      >
      <svg
        :width="isFittingContainer ? '100%' : calculatedSvgWidth"
        :height="isFittingContainer ? 'auto' : calculatedSvgHeight"
        :style="scoreSvgStyle"
        :viewBox="`0 0 ${calculatedSvgWidth} ${calculatedSvgHeight}`"
        :preserveAspectRatio="props.embedded ? 'xMinYMin meet' : undefined"
        class="select-none transition-all duration-150 block"
        :class="isFittingContainer ? 'w-full h-auto max-w-full' : 'overflow-visible'"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.15" />
          </filter>
        </defs>

        <!-- Systems (Pentagram Rows with Staves) -->
        <g v-for="system in systems" :key="'system-' + system.index" :transform="`translate(0, ${system.yOffset})`">
          <!-- Metronome / Tempo Mark (only on System 0, hidden when the first staff is TAB-only) -->
          <g v-if="system.index === 0 && !(system.staves.length > 0 && system.staves[0].showTabOnly)" transform="translate(68, 20)" class="fill-gray-700 dark:fill-gray-300 font-serif text-xs font-semibold">
            <text x="0" y="0">♩ = {{ selectedTempo }}</text>
          </g>

          <!-- Grand Staff Left Connecting Bracket / Line (when system has 2+ staves) -->
          <g v-if="system.staves.length > 1" class="text-gray-800 dark:text-gray-200 pointer-events-none">
            <line
              x1="10"
              :y1="getStaffLineY(0)"
              x2="10"
              :y2="system.staves[system.staves.length - 1].yOffset + getStaffLineY(4)"
              stroke="currentColor"
              stroke-width="3"
            />
            <line x1="6" :y1="getStaffLineY(0)" x2="10" :y2="getStaffLineY(0)" stroke="currentColor" stroke-width="2.5" />
            <line x1="6" :y1="system.staves[system.staves.length - 1].yOffset + getStaffLineY(4)" x2="10" :y2="system.staves[system.staves.length - 1].yOffset + getStaffLineY(4)" stroke="currentColor" stroke-width="2.5" />
          </g>

          <!-- Staves in this System -->
          <g v-for="staff in system.staves" :key="'sys-' + system.index + '-staff-' + staff.index" :transform="`translate(0, ${staff.yOffset})`">
            <!-- Background Staff Lines (5 horizontal lines per staff) -->
            <g v-if="!staff.showTabOnly" class="staff-lines stroke-gray-700 dark:stroke-gray-300" stroke-width="1.5">
              <line v-for="lineIdx in 5" :key="'staff-line-' + system.index + '-' + staff.index + '-' + lineIdx"
                :x1="10"
                :y1="getStaffLineY(lineIdx - 1)"
                :x2="system.width - 10"
                :y2="getStaffLineY(lineIdx - 1)"
              />
            </g>

            <!-- Authentic Music Clef Symbol -->
            <g v-if="!staff.showTabOnly" class="text-gray-900 dark:text-gray-100 fill-current">
              <g v-if="staff.clef === 'treble'" transform="translate(20, 78) scale(1.05)">
                <path :d="gClefPath" fill="currentColor" />
              </g>
              <g v-else-if="staff.clef === 'bass'" transform="translate(20, 58) scale(1.05)">
                <path :d="fClefPath" fill="currentColor" />
              </g>
              <g v-else-if="staff.clef === 'alto' || staff.clef === 'c'" transform="translate(22, 68) scale(0.95)">
                <path :d="cClefPath" fill="currentColor" />
              </g>
              <g v-else-if="staff.clef === 'tenor'" transform="translate(22, 58) scale(0.95)">
                <path :d="cClefPath" fill="currentColor" />
              </g>
              <g v-else-if="staff.clef === 'percussion'" transform="translate(24, 72)">
                <rect x="0" y="0" width="3.5" height="24" fill="currentColor" rx="1" />
                <rect x="6" y="0" width="3.5" height="24" fill="currentColor" rx="1" />
              </g>
            </g>

            <!-- Time Signature Display (Shown on System 0) -->
            <g v-if="!staff.showTabOnly && systemStartTime(system)" transform="translate(58, 48)" class="text-gray-800 dark:text-gray-100 fill-current font-bold text-2xl font-serif">
              <text x="0" y="16" text-anchor="middle">{{ systemStartTime(system).split('/')[0] }}</text>
              <text x="0" y="38" text-anchor="middle">{{ systemStartTime(system).split('/')[1] }}</text>
            </g>

            <!-- Key Signature Display (Sharps/Flats) -->
            <g v-if="!staff.showTabOnly && systemStartKeyAccs(staff, system).length > 0" :transform="`translate(${system.index === 0 ? 76 : 58}, 35)`">
              <text
                v-for="(acc, i) in systemStartKeyAccs(staff, system)"
                :key="'key-acc-' + system.index + '-' + staff.index + '-' + i"
                :x="i * 10 + (smuflAcc(acc.symbol) ? smuflAcc(acc.symbol).dx : 0)"
                :y="acc.y - 4"
                font-family="Bravura"
                :font-size="SMUFL_FONT_SIZE"
                class="fill-gray-800 dark:fill-gray-100"
              >{{ smuflAcc(acc.symbol) ? smuflAcc(acc.symbol).glyph : '♮' }}</text>
            </g>

            <!-- Staff / Instrument Label on System 0 -->
            <text
              v-if="!staff.showTabOnly && system.index === 0 && staff.name && system.staves.length > 1"
              x="12"
              :y="getStaffLineY(0) - 8"
              text-anchor="start"
              class="fill-indigo-600 dark:fill-indigo-400 font-bold text-[10px] tracking-wide"
            >{{ staff.name }}</text>

            <!-- Guitar tablature block under this staff (TAB-enabled voices, staff-local coords) -->
            <g v-if="staff.showTab" class="select-none">
              <g class="stroke-gray-700 dark:stroke-gray-300" stroke-width="1.2">
                <line
                  v-for="s in TAB_STRINGS"
                  :key="'tabline-' + system.index + '-' + staff.index + '-' + s"
                  :x1="10"
                  :y1="staff.tabBase + (s - 1) * TAB_LINE_SPACING"
                  :x2="system.width - 10"
                  :y2="staff.tabBase + (s - 1) * TAB_LINE_SPACING"
                />
                <line
                  v-for="(bar, bIdx) in system.barlines"
                  :key="'tabbar-' + system.index + '-' + staff.index + '-' + bIdx"
                  :x1="bar.x"
                  :y1="staff.tabBase"
                  :x2="bar.x"
                  :y2="staff.tabBase + (TAB_STRINGS - 1) * TAB_LINE_SPACING"
                  stroke-width="1.5"
                />
              </g>
              <text
                x="11"
                :y="staff.tabBase + 18"
                class="font-sans font-extrabold fill-gray-800 dark:fill-gray-100"
                font-size="13"
                font-style="italic"
              >TAB</text>
              <text
                v-for="s in TAB_STRINGS"
                :key="'tabstr-' + system.index + '-' + staff.index + '-' + s"
                x="2"
                :y="staff.tabBase + (s - 1) * TAB_LINE_SPACING + 3"
                text-anchor="middle"
                class="font-sans fill-gray-400 dark:fill-gray-500"
                font-size="7"
              >{{ ['E', 'B', 'G', 'D', 'A', 'E'][s - 1] }}</text>
              <!-- Fret numbers on strings -->
              <g
                v-for="tn in tabNumbersFor(system, staff)"
                :key="'tabn-' + system.index + '-' + staff.index + '-' + tn.key"
                class="cursor-pointer select-none"
                @click.stop="handleTabFretClick(tn)"
              >
                <!-- Selection highlight pill for active selected note in TAB -->
                <rect
                  v-if="selectedNoteIndex === tn.noteIndex && activeVoiceId === tn.voiceId"
                  :x="tn.x - 9"
                  :y="tn.y - 7"
                  width="18"
                  height="14"
                  rx="3"
                  class="fill-indigo-100 dark:fill-indigo-900/60 stroke-indigo-500 dark:stroke-indigo-400"
                  stroke-width="1.5"
                />
                <text
                  :x="tn.x"
                  :y="tn.y + 4"
                  text-anchor="middle"
                  class="font-sans font-bold stroke-white dark:stroke-gray-900"
                  :class="selectedNoteIndex === tn.noteIndex && activeVoiceId === tn.voiceId ? 'fill-indigo-600 dark:fill-indigo-300' : 'fill-gray-900 dark:fill-gray-100'"
                  font-size="11"
                  style="paint-order: stroke; stroke-width: 4px;"
                >{{ tn.fret }}</text>
              </g>

              <!-- TAB Articulations & Decorations (Slides, Bends, Hammer/Pull, Vibrato, Palm Mute) -->
              <g class="tab-decorations pointer-events-none">
                <!-- Slides -->
                <line
                  v-for="sl in tabDecorationsFor(system, staff).slides"
                  :key="sl.id"
                  :x1="sl.x1"
                  :y1="sl.y1"
                  :x2="sl.x2"
                  :y2="sl.y2"
                  class="stroke-indigo-600 dark:stroke-indigo-400"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />

                <!-- Bends -->
                <g v-for="bd in tabDecorationsFor(system, staff).bends" :key="bd.id" class="text-indigo-600 dark:text-indigo-400">
                  <path
                    :d="bd.d"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <!-- Bend Arrowhead -->
                  <polygon
                    :points="`${bd.arrowTipX - 3},${bd.arrowTipY + 4} ${bd.arrowTipX + 3},${bd.arrowTipY + 4} ${bd.arrowTipX},${bd.arrowTipY - 1}`"
                    fill="currentColor"
                  />
                  <!-- Bend text label (Full, 1/2, etc.) -->
                  <text
                    :x="bd.labelX"
                    :y="bd.labelY"
                    text-anchor="middle"
                    class="font-sans font-extrabold fill-current"
                    font-size="8.5"
                  >{{ bd.label }}</text>
                </g>

                <!-- Slurs & Legatos (Hammer-on / Pull-off / Ties) -->
                <g v-for="sl in tabDecorationsFor(system, staff).slurs" :key="sl.id" class="text-indigo-600 dark:text-indigo-400">
                  <path
                    :d="sl.d"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <text
                    v-if="sl.label"
                    :x="sl.labelX"
                    :y="sl.labelY"
                    text-anchor="middle"
                    class="font-sans font-black fill-current"
                    font-size="8"
                  >{{ sl.label }}</text>
                </g>

                <!-- Vibratos -->
                <path
                  v-for="vb in tabDecorationsFor(system, staff).vibratos"
                  :key="vb.id"
                  :d="vb.d"
                  fill="none"
                  class="stroke-indigo-600 dark:stroke-indigo-400"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />

                <!-- Palm Mute -->
                <g v-for="pm in tabDecorationsFor(system, staff).palmMutes" :key="pm.id" class="text-gray-500 dark:text-gray-400 font-sans font-bold text-[8px] fill-current">
                  <text :x="pm.x1" :y="pm.y - 2">{{ pm.label }}</text>
                  <line :x1="pm.x1 + 18" :y1="pm.y" :x2="pm.x2" :y2="pm.y" stroke="currentColor" stroke-dasharray="2,2" stroke-width="1.2" />
                  <line :x1="pm.x2" :y1="pm.y - 3" :x2="pm.x2" :y2="pm.y + 3" stroke="currentColor" stroke-width="1.2" />
                </g>
              </g>
            </g>
          </g>


          <!-- Measure Barlines & Repeat Marks in this System (Connecting through all staves) -->
          <g class="barlines stroke-gray-600 dark:stroke-gray-400">
            <g v-for="(bar, bIdx) in system.barlines" :key="'barline-' + system.index + '-' + bIdx">
              <!-- Regular single barline -->
              <line
                v-if="bar.type === 'single' || !bar.type"
                :x1="bar.x"
                :y1="barTopFor(system)"
                :x2="bar.x"
                :y2="barBottomFor(system)"
                stroke-width="1.5"
              />

              <!-- Start Repeat (|:) Barline: Thick line + Thin line + 2 dots on each staff -->
              <g v-else-if="bar.type === 'start-repeat'">
                <line
                  :x1="bar.x - 3"
                  :y1="barTopFor(system)"
                  :x2="bar.x - 3"
                  :y2="barBottomFor(system)"
                  stroke-width="3"
                />
                <line
                  :x1="bar.x"
                  :y1="barTopFor(system)"
                  :x2="bar.x"
                  :y2="barBottomFor(system)"
                  stroke-width="1.5"
                />
                <!-- Repeat dots in 2nd and 3rd spaces on every staff -->
                <g v-for="st in system.staves" :key="'dots-start-' + st.index" class="fill-gray-900 dark:fill-gray-100 stroke-none">
                  <circle :cx="bar.x + 5" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 18 : st.yOffset + getStaffLineY(1) + 5" r="2.2" />
                  <circle :cx="bar.x + 5" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 27 : st.yOffset + getStaffLineY(2) + 5" r="2.2" />
                </g>
              </g>

              <!-- End Repeat (:|) Barline: 2 dots on each staff + Thin line + Thick line -->
              <g v-else-if="bar.type === 'end-repeat'">
                <line
                  :x1="bar.x"
                  :y1="barTopFor(system)"
                  :x2="bar.x"
                  :y2="barBottomFor(system)"
                  stroke-width="1.5"
                />
                <line
                  :x1="bar.x + 3"
                  :y1="barTopFor(system)"
                  :x2="bar.x + 3"
                  :y2="barBottomFor(system)"
                  stroke-width="3"
                />
                <!-- Repeat dots in 2nd and 3rd spaces on every staff -->
                <g v-for="st in system.staves" :key="'dots-end-' + st.index" class="fill-gray-900 dark:fill-gray-100 stroke-none">
                  <circle :cx="bar.x - 5" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 18 : st.yOffset + getStaffLineY(1) + 5" r="2.2" />
                  <circle :cx="bar.x - 5" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 27 : st.yOffset + getStaffLineY(2) + 5" r="2.2" />
                </g>
              </g>

              <!-- Double Repeat (::) Barline -->
              <g v-else-if="bar.type === 'double-repeat'">
                <line
                  :x1="bar.x - 2"
                  :y1="barTopFor(system)"
                  :x2="bar.x - 2"
                  :y2="barBottomFor(system)"
                  stroke-width="3"
                />
                <line
                  :x1="bar.x + 2"
                  :y1="barTopFor(system)"
                  :x2="bar.x + 2"
                  :y2="barBottomFor(system)"
                  stroke-width="3"
                />
                <g v-for="st in system.staves" :key="'dots-dbl-' + st.index" class="fill-gray-900 dark:fill-gray-100 stroke-none">
                  <circle :cx="bar.x - 6" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 18 : st.yOffset + getStaffLineY(1) + 5" r="2.2" />
                  <circle :cx="bar.x - 6" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 27 : st.yOffset + getStaffLineY(2) + 5" r="2.2" />
                  <circle :cx="bar.x + 6" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 18 : st.yOffset + getStaffLineY(1) + 5" r="2.2" />
                  <circle :cx="bar.x + 6" :cy="st.showTabOnly ? st.yOffset + st.tabBase + 27 : st.yOffset + getStaffLineY(2) + 5" r="2.2" />
                </g>
              </g>

            <!-- Mid-piece key/time signature changes at this barline
                 (skipped on the last barline: the end-of-system courtesy covers it) -->
            <g
              v-if="bar.measure > 0 && !(bIdx === system.barlines.length - 1 && !system.isLastSystem) && (keyChangeAt(bar.measure) || timeChangeAt(bar.measure) || tempoChangeAt(bar.measure) || tempoRampAt(bar.measure))"
              pointer-events="none"
            >
                <g v-for="st in system.staves" :key="'mchange-' + system.index + '-' + bIdx + '-' + st.index">
                <g
                  v-if="keyChangeAt(bar.measure)"
                  :transform="`translate(${bar.x + 12}, 0)`"
                  class="fill-gray-800 dark:fill-gray-100 font-serif text-sm font-bold"
                >
                  <text
                    v-for="(acc, i) in markerKeyAccs(st, bar)"
                    :key="'mkey-' + system.index + '-' + bIdx + '-' + st.index + '-' + i"
                    :x="i * 10 + (smuflAcc(acc.symbol) ? smuflAcc(acc.symbol).dx : 0)"
                    :y="st.yOffset + acc.y - 4"
                    font-family="Bravura"
                    :font-size="SMUFL_FONT_SIZE"
                  >{{ smuflAcc(acc.symbol) ? smuflAcc(acc.symbol).glyph : '♮' }}</text>
                </g>
                <g
                  v-if="timeChangeAt(bar.measure)"
                  :transform="`translate(${bar.x + 12 + markerKeyAccs(st, bar).length * 10 + 6}, 0)`"
                    class="text-gray-800 dark:text-gray-100 fill-current font-bold text-xl font-serif"
                  >
                  <text :x="0" :y="st.yOffset + getStaffLineY(0) + 16" text-anchor="middle">{{ timeChangeAt(bar.measure).split('/')[0] }}</text>
                  <text :x="0" :y="st.yOffset + getStaffLineY(0) + 38" text-anchor="middle">{{ timeChangeAt(bar.measure).split('/')[1] }}</text>
                </g>
                <!-- Sudden tempo + rit./accel. marks above the staff (first staff only) -->
                <g v-if="st.index === system.staves[0].index">
                  <text
                    v-if="tempoChangeAt(bar.measure)"
                    :x="bar.x + 14"
                    :y="st.yOffset + getStaffLineY(0) - 14"
                    class="font-sans font-bold fill-current select-none"
                    font-size="10"
                  >♩={{ tempoChangeAt(bar.measure) }}</text>
                  <text
                    v-else-if="tempoRampAt(bar.measure)"
                    :x="bar.x + 14"
                    :y="st.yOffset + getStaffLineY(0) - 14"
                    class="font-serif italic font-bold fill-current select-none"
                    font-size="11"
                  >{{ tempoRampAt(bar.measure).kind === 'accel' ? 'accel.' : 'rit.' }}</text>
                </g>
                </g>
              </g>
            </g>

            <!-- End-of-system courtesy: next system's clef/key/time (wrap mode) -->
            <g v-for="ct in systemEndCourtesy(system.index)" :key="'courtesy-' + system.index + '-' + ct.staff.index" pointer-events="none" opacity="0.85">
              <g :transform="`translate(${system.width - 70}, ${ct.staff.yOffset}) scale(0.72)`" class="text-gray-800 dark:text-gray-200 fill-current">
                <g v-if="ct.clef === 'treble'" transform="translate(20, 78) scale(1.05)">
                  <path :d="gClefPath" fill="currentColor" />
                </g>
                <g v-else-if="ct.clef === 'bass'" transform="translate(20, 58) scale(1.05)">
                  <path :d="fClefPath" fill="currentColor" />
                </g>
                <g v-else-if="ct.clef === 'alto' || ct.clef === 'c'" transform="translate(22, 68) scale(0.95)">
                  <path :d="cClefPath" fill="currentColor" />
                </g>
                <g v-else-if="ct.clef === 'tenor'" transform="translate(22, 58) scale(0.95)">
                  <path :d="cClefPath" fill="currentColor" />
                </g>
                <g v-else-if="ct.clef === 'percussion'" transform="translate(24, 72)">
                  <rect x="0" y="0" width="3.5" height="24" fill="currentColor" rx="1" />
                  <rect x="6" y="0" width="3.5" height="24" fill="currentColor" rx="1" />
                </g>
              </g>
              <g v-if="ct.accs.length > 0" class="fill-gray-800 dark:fill-gray-100">
                <text
                  v-for="(acc, i) in ct.accs"
                  :key="'ctacc-' + system.index + '-' + ct.staff.index + '-' + i"
                  :x="system.width - 52 + i * 10 + (smuflAcc(acc.symbol) ? smuflAcc(acc.symbol).dx : 0)"
                  :y="ct.staff.yOffset + acc.y - 4"
                  font-family="Bravura"
                  :font-size="SMUFL_FONT_SIZE"
                >{{ smuflAcc(acc.symbol) ? smuflAcc(acc.symbol).glyph : '♮' }}</text>
              </g>
              <g
                v-if="ct.time"
                :transform="`translate(${system.width - 52 + ct.accs.length * 10 + 6}, 0)`"
                class="text-gray-800 dark:text-gray-100 fill-current font-bold text-xl font-serif"
              >
                <text :x="0" :y="ct.staff.yOffset + getStaffLineY(0) + 16" text-anchor="middle">{{ ct.time.split('/')[0] }}</text>
                <text :x="0" :y="ct.staff.yOffset + getStaffLineY(0) + 38" text-anchor="middle">{{ ct.time.split('/')[1] }}</text>
              </g>
            </g>

            <!-- Volta (1ª e 2ª Vez) Brackets above Staff 0 -->
            <g v-for="(vlt, vIdx) in system.voltas" :key="'volta-' + system.index + '-' + vIdx" class="text-indigo-600 dark:text-indigo-400">
              <!-- Top horizontal bracket line -->
              <line :x1="vlt.x1" :y1="vlt.y" :x2="vlt.x2" :y2="vlt.y" stroke="currentColor" stroke-width="1.5" />
              <!-- Left down hook -->
              <line :x1="vlt.x1" :y1="vlt.y" :x2="vlt.x1" :y2="vlt.y + 8" stroke="currentColor" stroke-width="1.5" />
              <!-- Right down hook for 1st volta (open for 2nd/final) -->
              <line v-if="vlt.number === 1" :x1="vlt.x2" :y1="vlt.y" :x2="vlt.x2" :y2="vlt.y + 8" stroke="currentColor" stroke-width="1.5" />
              <!-- Volta Number Label -->
              <text :x="vlt.x1 + 6" :y="vlt.y + 11" font-size="11" font-weight="bold" font-family="sans-serif" fill="currentColor" class="stroke-none">
                {{ vlt.number }}.
              </text>
            </g>

            <!-- Final Double Barline on Last System -->
            <g v-if="system.isLastSystem && totalNotesCount > 0">
              <line
                :x1="system.lastBarlineX"
                :y1="barTopFor(system)"
                :x2="system.lastBarlineX"
                :y2="barBottomFor(system)"
                stroke-width="1.5"
              />
              <line
                :x1="system.lastBarlineX + 4"
                :y1="barTopFor(system)"
                :x2="system.lastBarlineX + 4"
                :y2="barBottomFor(system)"
                stroke-width="3.5"
              />
            </g>
          </g>

          <!-- Beams connecting grouped 8th / 16th notes in this System -->
          <g class="beams-layer pointer-events-none">
            <line
              v-for="beam in system.beams"
              :key="beam.id"
              :x1="beam.x1"
              :y1="beam.y1"
              :x2="beam.x2"
              :y2="beam.y2"
              :stroke="beam.color || 'currentColor'"
              :stroke-width="beam.thickness"
              stroke-linecap="round"
            />
          </g>

          <!-- Ties and Slurs (Ligaduras) Arcs in this System -->
          <g class="curves-layer pointer-events-none text-gray-800 dark:text-gray-200">
            <path
              v-for="curve in system.curves"
              :key="curve.id"
              :d="curve.d"
              fill="none"
              :stroke="curve.color || 'currentColor'"
              :stroke-width="curve.type === 'tie' ? 2 : 1.8"
              stroke-linecap="round"
            />
          </g>

          <!-- Glissando (Linha ondulada entre notas) -->
          <g class="glissando-layer pointer-events-none text-gray-800 dark:text-gray-200">
            <g v-for="gliss in system.glissandi" :key="gliss.id">
              <line
                :x1="gliss.x1"
                :y1="gliss.y1"
                :x2="gliss.x2"
                :y2="gliss.y2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-dasharray="2,3"
                stroke-linecap="round"
              />
              <!-- Gliss. text label -->
              <text
                :x="(gliss.x1 + gliss.x2) / 2"
                :y="(gliss.y1 + gliss.y2) / 2 - 4"
                text-anchor="middle"
                class="font-serif italic font-bold text-[9px] fill-current"
                :transform="`rotate(${gliss.angle}, ${(gliss.x1 + gliss.x2) / 2}, ${(gliss.y1 + gliss.y2) / 2 - 4})`"
              >
                gliss.
              </text>
            </g>
          </g>

          <!-- Tuplets (Tercinas / Quiálteras) Colchetes e Números -->
          <g class="tuplets-layer pointer-events-none text-gray-800 dark:text-gray-200">
            <g v-for="tup in system.tuplets" :key="tup.id">
              <!-- Left bracket hook -->
              <line :x1="tup.x1" :y1="tup.y1" :x2="tup.x1" :y2="tup.y1 + (tup.stemUp ? 5 : -5)" stroke="currentColor" stroke-width="1.5" />
              <!-- Left horizontal line -->
              <line :x1="tup.x1" :y1="tup.y1" :x2="tup.midX - 7" :y2="tup.y1" stroke="currentColor" stroke-width="1.5" />
              <!-- Tuplet Number Label (3) -->
              <text :x="tup.midX" :y="tup.y1 + 3.5" text-anchor="middle" class="font-sans font-bold text-[11px] fill-current stroke-none">
                {{ tup.number }}
              </text>
              <!-- Right horizontal line -->
              <line :x1="tup.midX + 7" :y1="tup.y1" :x2="tup.x2" :y2="tup.y1" stroke="currentColor" stroke-width="1.5" />
              <!-- Right bracket hook -->
              <line :x1="tup.x2" :y1="tup.y1" :x2="tup.x2" :y2="tup.y1 + (tup.stemUp ? 5 : -5)" stroke="currentColor" stroke-width="1.5" />
            </g>
          </g>

          <!-- Hairpins / Reguladores de Expressão (Crescendo < e Diminuendo >) -->
          <g class="hairpins-layer pointer-events-none text-gray-800 dark:text-gray-200">
            <g v-for="hp in system.hairpins" :key="hp.id">
              <!-- Crescendo Hairpin (<) -->
              <path
                v-if="hp.type === 'crescendo'"
                :d="`M ${hp.x1} ${hp.y} L ${hp.x2} ${hp.y - 5.5} M ${hp.x1} ${hp.y} L ${hp.x2} ${hp.y + 5.5}`"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
              <!-- Diminuendo Hairpin (>) -->
              <path
                v-else-if="hp.type === 'diminuendo'"
                :d="`M ${hp.x1} ${hp.y - 5.5} L ${hp.x2} ${hp.y} M ${hp.x1} ${hp.y + 5.5} L ${hp.x2} ${hp.y}`"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </g>
          </g>

          <!-- Ottava brackets (8va / 8vb) above the staff -->
          <g class="ottavas-layer pointer-events-none text-gray-800 dark:text-gray-200">
            <g v-for="ot in (system.ottavas || [])" :key="ot.id">
              <text
                :x="ot.x1"
                :y="ot.y - 3"
                class="font-sans font-bold fill-current"
                font-size="10"
              >{{ ot.type === '8va' ? '8va' : '8vb' }}</text>
              <line
                :x1="ot.x1 + 20"
                :y1="ot.y"
                :x2="ot.x2"
                :y2="ot.y"
                stroke="currentColor"
                stroke-width="1.2"
                stroke-dasharray="4,2"
              />
              <line
                :x1="ot.x2"
                :y1="ot.y"
                :x2="ot.x2"
                :y2="ot.y + 6"
                stroke="currentColor"
                stroke-width="1.2"
              />
            </g>
          </g>
        </g>

        <!-- Playhead Stick (Vertical Cursor on currently played beat) -->
        <g v-if="activePlayingBeat >= 0 && activePlayhead" class="pointer-events-none">
          <!-- Top Triangle Pointer -->
          <polygon
            :points="`${activePlayhead.x - 4},${activePlayhead.topStaffY + activePlayhead.yOffset - 12} ${activePlayhead.x + 4},${activePlayhead.topStaffY + activePlayhead.yOffset - 12} ${activePlayhead.x},${activePlayhead.topStaffY + activePlayhead.yOffset - 4}`"
            fill="#4f46e5"
          />
          <!-- Vertical Line Stick through the entire System Staff -->
          <line
            :x1="activePlayhead.x"
            :y1="activePlayhead.topStaffY + activePlayhead.yOffset - 4"
            :x2="activePlayhead.x"
            :y2="activePlayhead.bottomStaffY + activePlayhead.yOffset + 6"
            stroke="#4f46e5"
            stroke-width="2"
            stroke-linecap="round"
            opacity="0.85"
          />
        </g>

        <!-- Rendered Notes & Rests across all Systems and Voices (hidden for TAB-only voices) -->
        <g
          v-for="(noteItem, index) in visualNotes"
          :key="'note-' + noteItem.voiceId + '-' + noteItem.index"
          v-show="!isTabOnlyVoice(noteItem.voiceId)"
          :class="[
            activePlayingNoteKeys.has(noteItem.globalKey) ? 'font-bold' : '',
            noteItem.voiceId !== activeVoiceId && isMultiVoice ? 'opacity-70 hover:opacity-100' : 'opacity-100',
            hoveredNoteIndex === index ? 'filter drop-shadow' : '',
            isDraggingNote(noteItem) ? 'cursor-grabbing' : ''
          ]"
          :style="{ color: isMultiVoice ? getVoiceColor(noteItem.voiceId) : '' }"
          class="cursor-pointer transition-all duration-75"
          style="touch-action: none;"
          :transform="`translate(0, ${noteItem.totalYOffset})`"
          @mouseenter="hoveredNoteIndex = index"
          @mouseleave="hoveredNoteIndex = null"
          @click.stop="handleNoteClick(noteItem)"
          @contextmenu.prevent="removeNoteItem(noteItem, $event)"
          @pointerdown.stop="onNotePointerDown($event, noteItem)"
        >
          <!-- Selection ring (edit mode): shows which note the sidebar tools act on -->
          <rect
            v-if="isNoteSelected(noteItem)"
            :x="noteItem.x - 16"
            :y="noteItem.y - 24"
            width="32"
            height="52"
            rx="9"
            fill="none"
            stroke="#6366f1"
            stroke-width="1.8"
            stroke-dasharray="5,3"
            pointer-events="none"
          />
          <!-- Navigation Direction Marks (Coda, Segno, Fine, D.C., D.S.) above Staff -->
          <g v-if="noteItem.navigation" class="fill-indigo-600 dark:fill-indigo-400 select-none">
            <!-- Coda symbol 𝄌 -->
            <g v-if="noteItem.navigation === 'coda'" :transform="`translate(${noteItem.x}, ${getStaffLineY(0) - 34})`">
              <circle cx="0" cy="0" r="6" fill="none" stroke="currentColor" stroke-width="1.6" />
              <line x1="-8" y1="0" x2="8" y2="0" stroke="currentColor" stroke-width="1.6" />
              <line x1="0" y1="-8" x2="0" y2="8" stroke="currentColor" stroke-width="1.6" />
            </g>
            <!-- Segno symbol 𝄋 -->
            <g v-else-if="noteItem.navigation === 'segno'" :transform="`translate(${noteItem.x}, ${getStaffLineY(0) - 34})`">
              <text x="0" y="4" text-anchor="middle" class="font-serif font-bold text-base fill-current">𝄋</text>
            </g>
            <!-- Text navigation words (Fine, D.C. al Fine, D.S. al Coda) -->
            <text
              v-else
              :x="noteItem.x"
              :y="getStaffLineY(0) - 32"
              text-anchor="middle"
              class="font-serif italic font-extrabold text-[11px] fill-current"
            >
              {{ noteItem.navigation.toUpperCase() }}
              </text>
          </g>

          <!-- Rehearsal mark (boxed letter above staff) -->
          <g v-if="noteItem.rehearsal" class="select-none" pointer-events="none">
            <rect
              :x="noteItem.x - 9"
              :y="getStaffLineY(0) - 62"
              width="18"
              height="15"
              rx="2"
              fill="none"
              stroke="#4f46e5"
              stroke-width="1.6"
            />
            <text
              :x="noteItem.x"
              :y="getStaffLineY(0) - 50"
              text-anchor="middle"
              class="font-sans font-extrabold fill-indigo-600 dark:fill-indigo-400"
              font-size="11"
            >{{ noteItem.rehearsal }}</text>
          </g>

          <!-- Inline clef change glyph at note position (model: note.inlineClef) -->
          <g
            v-if="noteItem.inlineClef"
            :transform="`translate(${noteItem.x - 26}, 0) scale(0.78)`"
            class="text-gray-900 dark:text-gray-100 fill-current select-none"
            pointer-events="none"
            opacity="0.95"
          >
            <g v-if="noteItem.inlineClef === 'treble'" transform="translate(20, 78) scale(1.05)">
              <path :d="gClefPath" fill="currentColor" />
            </g>
            <g v-else-if="noteItem.inlineClef === 'bass'" transform="translate(20, 58) scale(1.05)">
              <path :d="fClefPath" fill="currentColor" />
            </g>
            <g v-else-if="noteItem.inlineClef === 'alto' || noteItem.inlineClef === 'c'" transform="translate(22, 68) scale(0.95)">
              <path :d="cClefPath" fill="currentColor" />
            </g>
            <g v-else-if="noteItem.inlineClef === 'tenor'" transform="translate(22, 58) scale(0.95)">
              <path :d="cClefPath" fill="currentColor" />
            </g>
            <g v-else-if="noteItem.inlineClef === 'percussion'" transform="translate(24, 72)">
              <rect x="0" y="0" width="3.5" height="24" fill="currentColor" rx="1" />
              <rect x="6" y="0" width="3.5" height="24" fill="currentColor" rx="1" />
            </g>
          </g>

          <!-- Chord Symbol / Cifra above Staff (Rendered on top staff of system) -->
          <text
            v-if="!noteItem.headHidden && noteItem.chord"
            :x="noteItem.x"
            :y="getStaffLineY(0) - (noteItem.navigation ? 44 : (noteItem.articulation === 'fermata' ? 32 : 18))"
            text-anchor="middle"
            class="font-sans font-extrabold text-[13px] fill-indigo-600 dark:fill-indigo-400 select-none tracking-tight"
          >
            {{ noteItem.chord }}
          </text>

          <!-- Guitar fretboard frame above chord symbol (toggle in chords section) -->
          <g
            v-if="showGuitarFrames && noteItem.chord && guitarFrame(noteItem.chord)"
            :transform="`translate(${noteItem.x}, ${getStaffLineY(0) - (noteItem.navigation ? 44 : (noteItem.articulation === 'fermata' ? 32 : 18)) - 48})`"
            class="select-none text-gray-800 dark:text-gray-200"
            pointer-events="none"
          >
            <!-- Barre (drawn under dots) -->
            <line
              v-for="(bar, bi) in (guitarFrame(noteItem.chord).barres || [])"
              :key="'barre-' + index + '-' + bi"
              :x1="(6 - bar.fromString - 2.5) * 5"
              :x2="(6 - bar.toString - 2.5) * 5"
              :y1="(bar.fret - guitarFrame(noteItem.chord).baseFret + 0.5) * 7"
              :y2="(bar.fret - guitarFrame(noteItem.chord).baseFret + 0.5) * 7"
              stroke="currentColor"
              stroke-width="4.5"
              stroke-linecap="round"
              opacity="0.85"
            />
            <!-- Strings (6 vertical) -->
            <line
              v-for="s in 6"
              :key="'fstr-' + index + '-' + s"
              :x1="(s - 1 - 2.5) * 5"
              :y1="0"
              :x2="(s - 1 - 2.5) * 5"
              :y2="28"
              stroke="currentColor"
              stroke-width="1"
            />
            <!-- Frets (5 rows; thick nut when open position) -->
            <line
              v-for="f in 5"
              :key="'ffret-' + index + '-' + f"
              x1="-12.5"
              :y1="(f - 1) * 7"
              x2="12.5"
              :y2="(f - 1) * 7"
              stroke="currentColor"
              :stroke-width="(f === 1 && guitarFrame(noteItem.chord).baseFret === 1) ? 2.5 : 1"
            />
            <!-- Base fret label for non-open positions -->
            <text
              v-if="guitarFrame(noteItem.chord).baseFret > 1"
              x="-17"
              y="12"
              text-anchor="middle"
              class="font-sans font-bold fill-current"
              font-size="8"
            >{{ guitarFrame(noteItem.chord).baseFret }}</text>
            <!-- Per-string markers and dots -->
            <g v-for="s in 6" :key="'fdot-' + index + '-' + s">
              <text
                v-if="guitarFrame(noteItem.chord).frets[s - 1] === 'x'"
                :x="(s - 1 - 2.5) * 5"
                y="-3"
                text-anchor="middle"
                class="font-sans font-bold fill-current"
                font-size="7"
              >✕</text>
              <circle
                v-else-if="guitarFrame(noteItem.chord).frets[s - 1] === 0"
                :cx="(s - 1 - 2.5) * 5"
                cy="-5"
                r="2"
                fill="none"
                stroke="currentColor"
                stroke-width="1.2"
              />
              <g v-else-if="typeof guitarFrame(noteItem.chord).frets[s - 1] === 'number' && (guitarFrame(noteItem.chord).frets[s - 1] - guitarFrame(noteItem.chord).baseFret + 1) >= 1 && (guitarFrame(noteItem.chord).frets[s - 1] - guitarFrame(noteItem.chord).baseFret + 1) <= 5">
                <circle
                  :cx="(s - 1 - 2.5) * 5"
                  :cy="(guitarFrame(noteItem.chord).frets[s - 1] - guitarFrame(noteItem.chord).baseFret + 0.5) * 7"
                  r="3"
                  fill="currentColor"
                />
                <text
                  v-if="guitarFrame(noteItem.chord).fingers && guitarFrame(noteItem.chord).fingers[s - 1] > 0"
                  :x="(s - 1 - 2.5) * 5"
                  :y="(guitarFrame(noteItem.chord).frets[s - 1] - guitarFrame(noteItem.chord).baseFret + 0.5) * 7 + 2"
                  text-anchor="middle"
                  class="font-sans font-bold fill-white dark:fill-gray-900"
                  font-size="4.5"
                >{{ guitarFrame(noteItem.chord).fingers[s - 1] }}</text>
              </g>
            </g>
          </g>

            <!-- Dynamics Mark (Bravura letters, vertically centered) -->
            <g v-if="!noteItem.headHidden && noteItem.dynamic" class="fill-gray-800 dark:fill-gray-200 select-none">
              <text
                v-for="(dl, di) in smuflDynamics(noteItem.dynamic).items"
                :key="'dyn-' + index + '-' + di"
                :x="noteItem.x - (smuflDynamics(noteItem.dynamic).width * 10) / 2 + dl.x * 10"
                :y="belowTabY(noteItem) !== null ? belowTabY(noteItem) + 14 + (noteItem.lyric2 ? 46 : (noteItem.lyric ? 32 : 0)) : getStaffLineY(4) + (noteItem.lyric2 ? 60 : (noteItem.lyric ? 46 : 30)) - 6"
                font-family="Bravura"
                :font-size="SMUFL_FONT_SIZE"
                class="fill-current"
                dominant-baseline="central"
              >{{ dl.glyph }}</text>
            </g>

          <!-- Ornaments: Trill (tr~), Mordent, Turn, Pralltriller -->
          <g v-if="noteItem.ornament" class="fill-current text-gray-800 dark:text-gray-200 select-none">
            <!-- Trill mark tr + wavy extender (tr~~~) -->
            <g v-if="noteItem.ornament === 'trill'" :transform="`translate(${noteItem.x}, ${noteItem.y - (noteItem.stemUp ? 36 : 20)})`">
              <text x="0" y="0" text-anchor="middle" class="font-serif italic font-extrabold text-[13px] fill-current">tr</text>
              <path
                :d="trillWavePath(trillExtenderLength(noteItem))"
                transform="translate(10, -4)"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </g>
            <!-- Turn ~ -->
            <g v-else-if="noteItem.ornament === 'turn'" :transform="`translate(${noteItem.x}, ${noteItem.y - (noteItem.stemUp ? 34 : 18)})`">
              <text x="0" y="0" text-anchor="middle" class="font-serif font-bold text-base fill-current">∽</text>
            </g>
            <!-- Mordent -->
            <g v-else-if="noteItem.ornament === 'mordent'" :transform="`translate(${noteItem.x}, ${noteItem.y - (noteItem.stemUp ? 34 : 18)})`">
              <text x="0" y="0" text-anchor="middle" class="font-serif font-bold text-sm fill-current">≁</text>
            </g>
            <!-- Pralltriller / Inverted mordent -->
            <g v-else-if="noteItem.ornament === 'pralltriller'" :transform="`translate(${noteItem.x}, ${noteItem.y - (noteItem.stemUp ? 34 : 18)})`">
              <text x="0" y="0" text-anchor="middle" class="font-serif font-bold text-sm fill-current">〜</text>
            </g>
          </g>

           <!-- Articulations (Bravura SMuFL, origin at glyph bottom unless noted) -->
           <g v-if="noteItem.articulation" class="fill-current text-gray-900 dark:text-gray-100">
             <!-- Fermata above note or staff (lifted when an ornament shares the space) -->
             <g v-if="noteItem.articulation === 'fermata'" :transform="`translate(${noteItem.x}, ${Math.min(noteItem.y - (noteItem.stemUp ? 40 : 18), getStaffLineY(0) - 10) - (noteItem.ornament ? 18 : 0)})`">
               <text x="-12.1" y="0" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.fermataAbove }}</text>
             </g>
             <!-- Marcato -->
             <text
               v-else-if="noteItem.articulation === 'marcato'"
               :x="noteItem.x - 4.7"
               :y="noteItem.stemUp ? noteItem.y + 19 : noteItem.y - 10"
               font-family="Bravura"
               :font-size="SMUFL_FONT_SIZE"
               class="fill-current"
             >{{ SMUFL.articMarcatoAbove }}</text>
             <!-- Accent -->
             <text
               v-else-if="noteItem.articulation === 'accent'"
               :x="noteItem.x - 6.8"
               :y="noteItem.stemUp ? noteItem.y + 18 : noteItem.y - 8"
               font-family="Bravura"
               :font-size="SMUFL_FONT_SIZE"
               class="fill-current"
             >{{ SMUFL.articAccentAbove }}</text>
             <!-- Staccato dot -->
             <text
               v-else-if="noteItem.articulation === 'staccato'"
               :x="noteItem.x - 1.7"
               :y="(noteItem.stemUp ? noteItem.y + 10 : noteItem.y - 10) + 1.7"
               font-family="Bravura"
               :font-size="SMUFL_FONT_SIZE"
               class="fill-current"
             >{{ SMUFL.articStaccatoAbove }}</text>
             <!-- Tenuto -->
             <text
               v-else-if="noteItem.articulation === 'tenuto'"
               :x="noteItem.x - 6.75"
               :y="(noteItem.stemUp ? noteItem.y + 10 : noteItem.y - 10) + 1"
               font-family="Bravura"
               :font-size="SMUFL_FONT_SIZE"
               class="fill-current"
             >{{ SMUFL.articTenutoAbove }}</text>
             <!-- Down-bow (glyph origin at top) -->
             <text
               v-else-if="noteItem.articulation === 'downbow'"
               :x="noteItem.x - 1.8"
               :y="getStaffLineY(0) - 19"
               font-family="Bravura"
               :font-size="SMUFL_FONT_SIZE"
               class="fill-current"
             >{{ SMUFL.downBow }}</text>
             <!-- Up-bow -->
             <text
               v-else-if="noteItem.articulation === 'upbow'"
               :x="noteItem.x - 1.8"
               :y="getStaffLineY(0) - 10"
               font-family="Bravura"
               :font-size="SMUFL_FONT_SIZE"
               class="fill-current"
             >{{ SMUFL.upBow }}</text>
           </g>

          <!-- Ledger Lines (if note is above or below the 5 staff lines) -->
          <g class="stroke-gray-800 dark:stroke-gray-200" stroke-width="1.5" v-if="!noteItem.isRest">
            <line
              v-for="(ledgerY, lIdx) in noteItem.ledgerLines"
              :key="'ledger-' + index + '-' + lIdx"
              :x1="noteItem.x + (noteItem.headSide || 0) * 11 - 12"
              :y1="ledgerY"
              :x2="noteItem.x + (noteItem.headSide || 0) * 11 + 12"
              :y2="ledgerY"
            />
          </g>

          <!-- Rest Rendering with Precision SVG Paths -->
          <g v-if="noteItem.isRest && !noteItem.isHidden" :transform="`translate(${noteItem.x}, ${noteItem.y})`" class="fill-current text-gray-800 dark:text-gray-200">
            <!-- Multi-measure rest: thick bar spanning N measures + count -->
            <g v-if="noteItem.multiRest > 1">
              <rect
                :x="-8"
                y="-4.5"
                :width="Math.max(24, (noteItem.spanWidth || 120))"
                height="9"
                rx="1"
              />
              <line
                :x1="-8"
                y1="-8"
                :x2="-8"
                y2="8"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <line
                :x1="Math.max(24, (noteItem.spanWidth || 120)) - 8"
                y1="-8"
                :x2="Math.max(24, (noteItem.spanWidth || 120)) - 8"
                y2="8"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <text
                :x="Math.max(24, (noteItem.spanWidth || 120)) / 2 - 8"
                y="-12"
                text-anchor="middle"
                class="font-sans font-extrabold fill-current"
                font-size="13"
              >{{ noteItem.multiRest }}</text>
            </g>
            <!-- Whole Measure Rest or Whole Rest (hangs from line above middle) -->
            <text v-else-if="noteItem.duration === '1'" x="-5.65" y="-10" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.restWhole }}</text>
            <!-- Half Rest (sits on the middle line) -->
            <text v-else-if="noteItem.duration === '2'" x="-5.65" y="0" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.restHalf }}</text>
            <!-- Quarter Rest -->
            <text v-else-if="noteItem.duration === '4'" x="-5.4" y="0" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.restQuarter }}</text>
            <!-- 8th Rest -->
            <text v-else-if="noteItem.duration === '8'" x="-5" y="-4" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.rest8th }}</text>
            <!-- 16th Rest -->
            <text v-else-if="noteItem.duration === '16'" x="-6.4" y="-4" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.rest16th }}</text>
            <!-- 32nd Rest -->
            <text v-else x="-7.25" y="-4" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.rest32nd }}</text>

            <!-- Rest Augmentation Dots (single + double) -->
            <text v-if="noteDotCount(noteItem) >= 1" x="8" y="-2" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.augmentationDot }}</text>
            <text v-if="noteDotCount(noteItem) >= 2" x="14" y="-2" font-family="Bravura" :font-size="SMUFL_FONT_SIZE" class="fill-current">{{ SMUFL.augmentationDot }}</text>
          </g>

          <!-- Note Pitch Rendering -->
          <g v-else-if="!noteItem.isRest">
            <!-- Grace Note mini-head before main note if present -->
            <g v-if="noteItem.grace" :transform="`translate(${noteItem.x - 14}, ${noteItem.y + 4}) scale(0.65)`">
              <ellipse cx="0" cy="0" rx="5.5" ry="4" fill="currentColor" transform="rotate(-20)" />
              <line x1="4.8" y1="-1" x2="4.8" y2="-24" stroke="currentColor" stroke-width="2" />
              <!-- Grace note slash -->
              <line x1="1" y1="-12" x2="9" y2="-18" stroke="currentColor" stroke-width="2" />
            </g>

            <!-- Accidental (Bravura SMuFL for #, b, =; legacy text for 𝄪/𝄫) -->
            <g v-if="noteItem.accidental && smuflAcc(noteItem.accidental)">
              <text
                v-if="noteItem.cautionary"
                :x="noteItem.x - (noteItem.grace ? 22 : 14) - 9"
                :y="noteItem.y + 5"
                class="font-serif text-base fill-current"
                text-anchor="middle"
              >(</text>
              <text
                :x="noteItem.x - (noteItem.grace ? 22 : 14) + smuflAcc(noteItem.accidental).dx"
                :y="noteItem.y"
                font-family="Bravura"
                :font-size="SMUFL_FONT_SIZE"
                class="fill-current"
                text-anchor="start"
              >{{ smuflAcc(noteItem.accidental).glyph }}</text>
              <text
                v-if="noteItem.cautionary"
                :x="noteItem.x - (noteItem.grace ? 22 : 14) + 9"
                :y="noteItem.y + 5"
                class="font-serif text-base fill-current"
                text-anchor="middle"
              >)</text>
            </g>
            <text
              v-else-if="noteItem.accidental"
              :x="noteItem.x - (noteItem.grace ? 22 : 14)"
              :y="noteItem.y + 4"
              class="font-serif font-bold text-base fill-current"
              text-anchor="middle"
            >
              {{ noteItem.cautionary ? '(' : '' }}{{ noteItem.accidental === '##' ? '𝄪' : '𝄫' }}{{ noteItem.cautionary ? ')' : '' }}
            </text>

            <!-- Courtesy reminder (carried alteration, in parentheses) -->
            <g v-if="!noteItem.accidental && noteItem.courtesyAlter">
              <text
                :x="noteItem.x - (noteItem.grace ? 30 : 22)"
                :y="noteItem.y + 5"
                class="font-serif text-sm fill-current"
                text-anchor="middle"
              >(</text>
              <text
                :x="noteItem.x - (noteItem.grace ? 22 : 14) + (smuflAcc(noteItem.courtesyAlter) ? smuflAcc(noteItem.courtesyAlter).dx : 0)"
                :y="noteItem.y"
                font-family="Bravura"
                :font-size="SMUFL_FONT_SIZE"
                class="fill-current"
                text-anchor="start"
                v-if="smuflAcc(noteItem.courtesyAlter)"
              >{{ smuflAcc(noteItem.courtesyAlter).glyph }}</text>
              <text
                v-else
                :x="noteItem.x - (noteItem.grace ? 22 : 14)"
                :y="noteItem.y + 4"
                class="font-serif font-bold text-base fill-current"
                text-anchor="middle"
              >{{ noteItem.courtesyAlter === '##' ? '𝄪' : (noteItem.courtesyAlter === 'bb' ? '𝄫' : '♮') }}</text>
              <text
                :x="noteItem.x - (noteItem.grace ? 22 : 14) + 10"
                :y="noteItem.y + 5"
                class="font-serif text-sm fill-current"
                text-anchor="middle"
              >)</text>
            </g>

            <!-- Note Head (Bravura SMuFL glyphs, vertically centered on origin) -->
            <!-- X notehead (percussion / ghost / muted) -->
            <text
              v-if="!noteItem.headHidden && noteItem.notehead === 'x'"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + smuflHead('x').dx"
              :y="noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current select-none"
            >{{ smuflHead('x').glyph }}</text>
            <!-- Diamond notehead (harmonics; open for whole/half) -->
            <text
              v-else-if="!noteItem.headHidden && noteItem.notehead === 'diamond'"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + smuflHead(noteItem.duration === '1' ? 'diamondWhole' : (noteItem.duration === '2' ? 'diamondHalf' : 'diamondBlack')).dx"
              :y="noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current select-none"
            >{{ smuflHead(noteItem.duration === '1' ? 'diamondWhole' : (noteItem.duration === '2' ? 'diamondHalf' : 'diamondBlack')).glyph }}</text>
            <!-- Slash notehead (rhythmic notation) -->
            <rect
              v-else-if="!noteItem.headHidden && noteItem.notehead === 'slash'"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 - 2.4"
              :y="noteItem.y - 7"
              width="4.8"
              height="14"
              fill="currentColor"
              :transform="`rotate(28 ${noteItem.x + (noteItem.headSide || 0) * 11} ${noteItem.y})`"
            />
            <!-- Whole Note 𝅝 -->
            <text
              v-else-if="!noteItem.headHidden && noteItem.duration === '1'"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + smuflHead('whole').dx"
              :y="noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current select-none"
            >{{ smuflHead('whole').glyph }}</text>
            <!-- Half Note 𝅗𝅥 -->
            <text
              v-else-if="!noteItem.headHidden && noteItem.duration === '2'"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + smuflHead('half').dx"
              :y="noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current select-none"
            >{{ smuflHead('half').glyph }}</text>
            <!-- Quarter / 8th / 16th Note Head (Filled) -->
            <text
              v-else-if="!noteItem.headHidden"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + smuflHead('black').dx"
              :y="noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current select-none"
            >{{ smuflHead('black').glyph }}</text>

            <!-- Cluster extra tones sharing the stem (chord tones) -->
            <g v-if="noteItem.pitches && noteItem.pitches.length" class="select-none">
              <g v-for="(tone, ti) in clusterTones(noteItem)" :key="'tone-' + index + '-' + ti">
                <line
                  v-for="(ledgerY, lIdx) in tone.ledgers"
                  :key="'tone-ledger-' + index + '-' + ti + '-' + lIdx"
                  :x1="noteItem.x + tone.xOff - 12"
                  :y1="ledgerY"
                  :x2="noteItem.x + tone.xOff + 12"
                  :y2="ledgerY"
                  class="stroke-gray-800 dark:stroke-gray-200"
                  stroke-width="1.5"
                />
                <text
                  v-if="tone.accidental && smuflAcc(tone.accidental)"
                  :x="tone.accX + smuflAcc(tone.accidental).dx"
                  :y="tone.y"
                  font-family="Bravura"
                  :font-size="SMUFL_FONT_SIZE"
                  class="fill-current"
                  text-anchor="start"
                >{{ smuflAcc(tone.accidental).glyph }}</text>
                <text
                  v-else-if="tone.accidental"
                  :x="tone.accX"
                  :y="tone.y + 4"
                  class="font-serif font-bold text-base fill-current"
                  text-anchor="middle"
                >{{ accSymbol(tone.accidental) }}</text>
                <text
                  :x="noteItem.x + tone.xOff + smuflHead(noteItem.duration === '1' ? 'whole' : (noteItem.duration === '2' ? 'half' : 'black')).dx"
                  :y="tone.y"
                  font-family="Bravura"
                  :font-size="SMUFL_FONT_SIZE"
                  class="fill-current"
                >{{ smuflHead(noteItem.duration === '1' ? 'whole' : (noteItem.duration === '2' ? 'half' : 'black')).glyph }}</text>
              </g>
            </g>

            <!-- Note Augmentation Dots (Ponto Simples e Duplo) -->
            <text
              v-if="!noteItem.headHidden && noteDotCount(noteItem) >= 1"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + (noteItem.duration === '1' ? 10 : 8.5) - 2"
              :y="((noteItem.y - getStaffLineY(0)) % 10 === 0) ? noteItem.y - 4 : noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current"
            >{{ SMUFL.augmentationDot }}</text>
            <text
              v-if="!noteItem.headHidden && noteDotCount(noteItem) >= 2"
              :x="noteItem.x + (noteItem.headSide || 0) * 11 + (noteItem.duration === '1' ? 10 : 8.5) + 4"
              :y="((noteItem.y - getStaffLineY(0)) % 10 === 0) ? noteItem.y - 4 : noteItem.y"
              font-family="Bravura"
              :font-size="SMUFL_FONT_SIZE"
              class="fill-current"
            >{{ SMUFL.augmentationDot }}</text>

            <!-- Note Stem (Haste) -->
            <g v-if="noteItem.duration !== '1'">
              <!-- Stem for Beamed Note -->
              <line
                v-if="noteItem.isBeamed"
                :x1="noteItem.stemTipX"
                :y1="noteItem.stemUp ? noteItem.y - 1 : noteItem.y + 1"
                :x2="noteItem.stemTipX"
                :y2="noteItem.stemTipY"
                stroke="currentColor"
                stroke-width="1.8"
              />
              <!-- Unbeamed Stem Up -->
              <line
                v-else-if="noteItem.stemUp"
                :x1="noteItem.x + 4.8"
                :y1="noteItem.y - 1"
                :x2="noteItem.x + 4.8"
                :y2="noteItem.y - 32"
                stroke="currentColor"
                stroke-width="1.8"
              />
              <!-- Unbeamed Stem Down -->
              <line
                v-else
                :x1="noteItem.x - 4.8"
                :y1="noteItem.y + 1"
                :x2="noteItem.x - 4.8"
                :y2="noteItem.y + 32"
                stroke="currentColor"
                stroke-width="1.8"
              />

              <!-- Flags for isolated (unbeamed) 8th, 16th and 32nd notes (Bravura) -->
              <g v-if="!noteItem.isBeamed && smuflFlag(noteItem.duration, noteItem.stemUp)">
                <text
                  :x="noteItem.stemUp ? noteItem.x + 4.8 : noteItem.x - 4.8"
                  :y="noteItem.stemUp ? noteItem.y - 32 : noteItem.y + 32"
                  font-family="Bravura"
                  :font-size="SMUFL_FONT_SIZE"
                  class="fill-current"
                >{{ smuflFlag(noteItem.duration, noteItem.stemUp) }}</text>
              </g>
            </g>

            <!-- Arpeggio wavy vertical line left of the chord -->
            <path
              v-if="noteItem.arpeggio"
              :d="arpeggioWavePath(arpeggioExtent(noteItem))"
              :transform="`translate(${noteItem.x - 13}, ${arpeggioExtent(noteItem).top})`"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
            <!-- Tremolo slashes across the stem (or notehead area when stemless) -->
            <g
              v-if="noteItem.tremolo >= 1"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
            >
              <line
                v-for="sl in Math.min(3, noteItem.tremolo)"
                :key="'trem-' + index + '-' + sl"
                :x1="(noteItem.duration === '1' ? noteItem.x : (noteItem.stemUp ? noteItem.x + 4.8 : noteItem.x - 4.8)) - 4.5"
                :y1="(noteItem.duration === '1' ? noteItem.y : (noteItem.stemUp ? noteItem.y - 16 : noteItem.y + 16)) + 3 + (sl - 1) * 5 - (Math.min(3, noteItem.tremolo) - 1) * 2.5"
                :x2="(noteItem.duration === '1' ? noteItem.x : (noteItem.stemUp ? noteItem.x + 4.8 : noteItem.x - 4.8)) + 4.5"
                :y2="(noteItem.duration === '1' ? noteItem.y : (noteItem.stemUp ? noteItem.y - 16 : noteItem.y + 16)) - 3 + (sl - 1) * 5 - (Math.min(3, noteItem.tremolo) - 1) * 2.5"
              />
            </g>
            <!-- Breath mark (comma) after the note, above staff -->
            <path
              v-if="noteItem.breath"
              :d="`M ${noteItem.x + 12} ${getStaffLineY(0) - 6} c 3.5 2 3.5 7 -0.5 10`"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
            <!-- Pedal marks below staff -->
            <text
              v-if="noteItem.pedalStart"
              :x="noteItem.x"
              :y="belowTabY(noteItem) !== null ? belowTabY(noteItem) + 14 + ((noteItem.lyric || noteItem.lyric2) ? 50 : 16) : getStaffLineY(4) + ((noteItem.lyric || noteItem.lyric2) ? 64 : 44)"
              text-anchor="middle"
              class="font-serif italic font-bold fill-current select-none"
              font-size="12"
            >Ped.</text>
            <text
              v-if="noteItem.pedalEnd"
              :x="noteItem.x"
              :y="belowTabY(noteItem) !== null ? belowTabY(noteItem) + 14 + ((noteItem.lyric || noteItem.lyric2) ? 50 : 16) : getStaffLineY(4) + ((noteItem.lyric || noteItem.lyric2) ? 64 : 44)"
              text-anchor="middle"
              class="font-serif font-bold fill-current select-none"
              font-size="13"
            >*</text>

            <!-- Note Name Tag (below the TAB block when the staff shows one) -->
            <text
              :x="noteItem.x"
              :y="belowTabY(noteItem) !== null ? belowTabY(noteItem) : (noteItem.y >= getStaffLineY(2) ? getStaffLineY(4) + 16 : getStaffLineY(0) - 10)"
              text-anchor="middle"
              class="text-[9px] font-mono fill-gray-500 dark:fill-gray-400 font-semibold"
            >
              {{ noteItem.accidental ? noteItem.accidental : '' }}{{ noteItem.pitch }}
            </text>

            <!-- Lyric / Syllable Text below Note (verse 1) -->
            <text
              v-if="!noteItem.headHidden && noteItem.lyric"
              :x="noteItem.x"
              :y="(belowTabY(noteItem) !== null ? belowTabY(noteItem) + 12 : getStaffLineY(4)) + (noteItem.y >= getStaffLineY(2) ? 32 : 24)"
              text-anchor="middle"
              class="text-[12px] font-sans font-bold fill-indigo-700 dark:fill-indigo-300 select-none tracking-tight"
            >
              {{ noteItem.lyric }}
            </text>
            <!-- Lyric verse 2 below verse 1 -->
            <text
              v-if="!noteItem.headHidden && noteItem.lyric2"
              :x="noteItem.x"
              :y="(belowTabY(noteItem) !== null ? belowTabY(noteItem) + 12 : getStaffLineY(4)) + (noteItem.y >= getStaffLineY(2) ? 32 : 24) + 15"
              text-anchor="middle"
              class="text-[11px] font-sans font-semibold fill-teal-700 dark:fill-teal-300 select-none tracking-tight"
            >
              {{ noteItem.lyric2 }}
            </text>
            <!-- Melisma extender lines (trailing-hyphen syllables) -->
            <line
              v-if="lyricExtender(noteItem, 1)"
              :x1="lyricExtender(noteItem, 1).x1"
              :y1="lyricExtender(noteItem, 1).y"
              :x2="lyricExtender(noteItem, 1).x2"
              :y2="lyricExtender(noteItem, 1).y"
              stroke="currentColor"
              stroke-width="1.2"
              class="text-indigo-700 dark:text-indigo-300"
              pointer-events="none"
            />
            <line
              v-if="lyricExtender(noteItem, 2)"
              :x1="lyricExtender(noteItem, 2).x1"
              :y1="lyricExtender(noteItem, 2).y"
              :x2="lyricExtender(noteItem, 2).x2"
              :y2="lyricExtender(noteItem, 2).y"
              stroke="currentColor"
              stroke-width="1.2"
              class="text-teal-700 dark:text-teal-300"
              pointer-events="none"
            />
          </g>
        </g>

        <!-- Ghost Note Preview on Mouse Hover (Interactive Feedback) -->
        <g v-if="!props.readonly && ghostNote.visible" class="pointer-events-none opacity-60 text-indigo-600 dark:text-indigo-400" :transform="`translate(0, ${ghostNote.yOffset})`">
          <!-- Ledger lines for ghost note -->
          <line
            v-for="(ledgerY, gIdx) in ghostNote.ledgerLines"
            :key="'ghost-ledger-' + gIdx"
            :x1="ghostNote.x - 12"
            :y1="ledgerY"
            :x2="ghostNote.x + 12"
            :y2="ledgerY"
            stroke="currentColor"
            stroke-width="1.5"
          />

          <!-- Rest ghost -->
          <g v-if="isRestSelected" :transform="`translate(${ghostNote.x}, ${ghostNote.y})`" class="fill-current">
            <text x="-4" y="6" font-family="serif" font-size="24" font-weight="bold">𝄽</text>
          </g>
          <!-- Pitch ghost -->
          <g v-else>
            <!-- Accidental ghost -->
            <text
              v-if="selectedAccidental"
              :x="ghostNote.x - 14"
              :y="ghostNote.y + 4"
              class="font-serif font-bold text-base fill-current"
              text-anchor="middle"
            >
              {{ selectedAccidental === '#' ? '♯' : (selectedAccidental === 'b' ? '♭' : '♮') }}
            </text>

            <ellipse
              :cx="ghostNote.x"
              :cy="ghostNote.y"
              rx="5.5"
              ry="4"
              fill="currentColor"
              transform="rotate(-20)"
              :transform-origin="`${ghostNote.x} ${ghostNote.y}`"
            />
            <!-- Stem -->
            <line
              v-if="selectedDuration !== '1'"
              :x1="ghostNote.stemUp ? ghostNote.x + 4.8 : ghostNote.x - 4.8"
              :y1="ghostNote.stemUp ? ghostNote.y - 1 : ghostNote.y + 1"
              :x2="ghostNote.stemUp ? ghostNote.x + 4.8 : ghostNote.x - 4.8"
              :y2="ghostNote.stemUp ? ghostNote.y - 30 : ghostNote.y + 30"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <!-- Tooltip with Pitch Name -->
            <g :transform="`translate(${ghostNote.x}, ${ghostNote.y - 20})`">
              <rect x="-16" y="-12" width="32" height="15" rx="3" fill="#4f46e5" />
              <text x="0" y="-1" text-anchor="middle" fill="#fff" font-size="10" font-weight="bold">
                {{ selectedAccidental }}{{ ghostNote.pitch }}
              </text>
            </g>
          </g>
        </g>

        <!-- Ghost TAB Note Preview on Mouse Hover over TAB lines -->
        <g v-if="!props.readonly && ghostTabNote.visible" class="pointer-events-none text-indigo-600 dark:text-indigo-400" :transform="`translate(0, ${ghostTabNote.yOffset})`">
          <rect
            :x="ghostTabNote.x - 9"
            :y="ghostTabNote.y - 7"
            width="18"
            height="14"
            rx="3"
            class="fill-indigo-100/90 dark:fill-indigo-900/70 stroke-indigo-500 dark:stroke-indigo-400"
            stroke-width="1.5"
            stroke-dasharray="2,2"
          />
          <text
            :x="ghostTabNote.x"
            :y="ghostTabNote.y + 4"
            text-anchor="middle"
            class="font-sans font-bold fill-indigo-600 dark:fill-indigo-300 stroke-white dark:stroke-gray-900"
            font-size="11"
            style="paint-order: stroke; stroke-width: 3px;"
          >{{ ghostTabNote.fret }}</text>
        </g>
      </svg>

      <!-- Empty State Helper (Only when not embedded) -->
      <div
        v-if="notes.length === 0 && !props.embedded"
        class="absolute inset-0 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 pointer-events-none"
      >
        <span class="text-xs font-medium">Passe o mouse na pauta e clique para adicionar notas musicalmente</span>
        <span class="text-[11px] opacity-75">Use a barra superior para mudar duração, sustenidos, bemol ou pausas</span>
      </div>
      </div>

    </div><!-- end body: sidebar + score -->

    <!-- Footer Stats / Live ABC preview in small text (Only when not embedded) -->
    <div
      v-if="!props.embedded"
      class="px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400"
    >
      <div class="flex items-center gap-4">
        <span>{{ t('footer.notes') }}: <strong>{{ notes.length }}</strong></span>
        <span>{{ t('footer.measures') }}: <strong>{{ totalMeasures }}</strong></span>
        <span>{{ t('footer.tempo') }}: <strong>{{ selectedTempo }} BPM</strong></span>
        <span v-if="notes.length > 0" class="font-mono text-[11px] truncate max-w-md hidden sm:inline" :title="computedAbc">
          ABC: {{ computedAbc.split('\n').filter(l => !l.match(/^[A-Z]:/)).join(' ') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-[11px] text-gray-400">{{ t('footer.rightClickHint') }}</span>
      </div>
    </div>

    <!-- Import Modal (ABC / MusicXML) -->
    <div v-if="showImportModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" @click.self="showImportModal = false">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-5 flex flex-col gap-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <svg class="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
            {{ t('modal.importTitle') }}
          </h3>
          <button @click="showImportModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg">&times;</button>
        </div>

        <!-- Format tabs -->
        <div class="flex border-b border-gray-200 dark:border-gray-700 gap-4 text-xs font-semibold">
          <button
            @click="importFormat = 'abc'"
            :class="importFormat === 'abc' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 pb-2' : 'text-gray-500 pb-2'"
          >
            ABC Notation
          </button>
          <button
            @click="importFormat = 'musicxml'"
            :class="importFormat === 'musicxml' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 pb-2' : 'text-gray-500 pb-2'"
          >
            MusicXML
          </button>
          <button
            @click="loadSampleMelody"
            class="ml-auto text-indigo-500 hover:underline text-xs"
          >
            {{ t('modal.loadSample') }}
          </button>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ t('modal.pastePrompt', { format: importFormat === 'abc' ? 'ABC Notation' : 'MusicXML' }) }}
          </label>
          <textarea
            v-model="importText"
            rows="6"
            class="w-full font-mono text-xs p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            :placeholder="importFormat === 'abc' ? 'X:1\nT:Melodia\nM:4/4\nL:1/8\nQ:1/4=120\nK:C\nC D E F | G A B c |' : '<?xml version=\'1.0\'?>\n<score-partwise>...</score-partwise>'"
          ></textarea>
        </div>

        <div v-if="importError" class="text-xs text-red-500 font-medium">
          {{ importError }}
        </div>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            @click="showImportModal = false"
            class="px-4 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            {{ t('modal.cancel') }}
          </button>
          <button
            @click="processImport"
            class="px-4 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm"
          >
            {{ t('modal.processAndLoad') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Export Modal (ABC / MusicXML / JSON) -->
    <div v-if="showExportModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" @click.self="showExportModal = false">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-5 flex flex-col gap-4 border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <svg class="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
            {{ t('modal.exportTitle') }}
          </h3>
          <button @click="showExportModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg">&times;</button>
        </div>

        <!-- Format tabs -->
        <div class="flex border-b border-gray-200 dark:border-gray-700 gap-4 text-xs font-semibold">
          <button
            @click="exportFormat = 'abc'"
            :class="exportFormat === 'abc' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 pb-2' : 'text-gray-500 pb-2'"
          >
            ABC Notation
          </button>
          <button
            @click="exportFormat = 'musicxml'"
            :class="exportFormat === 'musicxml' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 pb-2' : 'text-gray-500 pb-2'"
          >
            MusicXML
          </button>
          <button
            @click="exportFormat = 'json'"
            :class="exportFormat === 'json' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 pb-2' : 'text-gray-500 pb-2'"
          >
            JSON Raw
          </button>
        </div>

        <div>
          <textarea
            :value="exportContent"
            readonly
            rows="8"
            class="w-full font-mono text-xs p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none select-all"
          ></textarea>
        </div>

        <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
          <span class="text-xs text-emerald-600 dark:text-emerald-400" v-if="copiedMessage">{{ copiedMessage }}</span>
          <span v-else></span>

          <div class="flex items-center gap-2">
            <button
              @click="copyExportToClipboard"
              class="px-4 py-2 text-xs font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors"
            >
              {{ t('modal.copy') }}
            </button>
            <button
              @click="downloadExportFile"
              class="px-4 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm transition-colors"
            >
              {{ t('modal.downloadFile') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Print root (teleported; only visible while printing) -->
  <teleport to="body">
    <div class="ms-print-root" aria-hidden="true">
      <div class="ms-print-header">
        <div class="ms-print-title">{{ scoreTitle || t('score.untitled') }}</div>
        <div class="ms-print-sub">
          <span v-if="scoreComposer">{{ scoreComposer }}</span>
          <span>♩={{ selectedTempo }} · {{ selectedTimeSignature }} · {{ selectedKey }}</span>
          <span v-if="partVoiceId">· {{ t('voices.part') }}: {{ (voices.find(v => v.id === partVoiceId) || {}).name || partVoiceId }}</span>
        </div>
      </div>
      <div ref="printPagesRef"></div>
    </div>
  </teleport>
</template>

<script setup>
import { getGuitarChordData } from '@/utils/guitarChords';
import { ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import { getMusicSheetLabel } from './musicSheetLocales';
import { SMUFL, smuflAccidental, smuflDynamics } from '@/utils/smufl';
import zoomStore from '@/store/zoom.store';
import { appEvents } from '@/store/app.events';
import MusicSheetSelect from './MusicSheetSelect.vue';

// Bravura staff size: 1em = 4 staff spaces (LINE_SPACING = 10px -> 40px)
const SMUFL_FONT_SIZE = 40;

// SMuFL notehead glyph + horizontal centering offset (px) for a head kind
function smuflHead(kind) {
  const map = {
    black: ['noteheadBlack', 1.18],
    half: ['noteheadHalf', 1.18],
    whole: ['noteheadWhole', 1.69],
    x: ['noteheadXBlack', 1.16],
    diamondBlack: ['noteheadDiamondBlack', 0.98],
    diamondHalf: ['noteheadDiamondHalf', 0.98],
    diamondWhole: ['noteheadDiamondWhole', 1.66]
  };
  const entry = map[kind] || map.black;
  return { glyph: SMUFL[entry[0]], dx: -(entry[1] * 10) / 2 };
}

// SMuFL flag glyph for isolated (unbeamed) notes, or null
function smuflFlag(duration, stemUp) {
  if (duration === '8') return stemUp ? SMUFL.flag8thUp : SMUFL.flag8thDown;
  if (duration === '16') return stemUp ? SMUFL.flag16thUp : SMUFL.flag16thDown;
  if (duration === '32') return stemUp ? SMUFL.flag32ndUp : SMUFL.flag32ndDown;
  return null;
}

// SMuFL accidental glyph + centering offset, or null (keep legacy text: 𝄪/𝄫)
function smuflAcc(char) {
  const glyph = smuflAccidental(char);
  if (!glyph) return null;
  const adv = char === '#' ? 1.0 : (char === 'b' ? 0.9 : 0.67);
  return { glyph, dx: -(adv * 10) / 2 };
}
import {
  getDiatonicIndex,
  getPitchFromDiatonicIndex,
  getMidiNumber,
  transposePitch,
  transposeKey,
  durationToQuarterBeats,
  noteDotCount,
  keyAlterFor,
  keySharps,
  keyFlats,
  notesToAbc,
  abcToNotes,
  notesToMusicXml,
  musicXmlToNotes
} from '@/utils/musicSheetConverter';
import {
  playSoundfontNote,
  preloadNotes,
  playDrumSound,
  drumPieceById,
  drumPieceForPitch,
  DRUM_PIECES as DRUM_PIECE_LIST,
  AVAILABLE_INSTRUMENTS
} from '@/utils/soundfontPlayer';

// Standard Canonical Musical Clef SVG Paths
const gClefPath = "M 9.69 -37.41 c 0.09 -0.09 0.24 -0.06 0.36 0 c 0.12 0.09 0.57 0.6 0.96 1.11 c 1.77 2.34 3.21 5.85 3.57 8.73 c 0.21 1.56 0.03 3.27 -0.45 4.86 c -0.69 2.31 -1.92 4.47 -4.23 7.44 c -0.3 0.39 -0.57 0.72 -0.6 0.75 c -0.03 0.06 0 0.15 0.18 0.78 c 0.54 1.68 1.38 4.44 1.68 5.49 l 0.09 0.42 l 0.39 0 c 1.47 0.09 2.76 0.51 3.96 1.29 c 1.83 1.23 3.06 3.21 3.39 5.52 c 0.09 0.45 0.12 1.29 0.06 1.74 c -0.09 1.02 -0.33 1.83 -0.75 2.73 c -0.84 1.71 -2.28 3.06 -4.02 3.72 l -0.33 0.12 l 0.03 1.26 c 0 1.74 -0.06 3.63 -0.21 4.62 c -0.45 3.06 -2.19 5.49 -4.47 6.21 c -0.57 0.18 -0.9 0.21 -1.59 0.21 c -0.69 0 -1.02 -0.03 -1.65 -0.21 c -1.14 -0.27 -2.13 -0.84 -2.94 -1.65 c -0.99 -0.99 -1.56 -2.16 -1.71 -3.54 c -0.09 -0.81 0.06 -1.53 0.45 -2.13 c 0.63 -0.99 1.83 -1.56 3 -1.53 c 1.5 0.09 2.64 1.32 2.73 2.94 c 0.06 1.47 -0.93 2.7 -2.37 2.97 c -0.45 0.06 -0.84 0.03 -1.29 -0.09 l -0.21 -0.09 l 0.09 0.12 c 0.39 0.54 0.78 0.93 1.32 1.26 c 1.35 0.87 3.06 1.02 4.35 0.36 c 1.44 -0.72 2.52 -2.28 2.97 -4.35 c 0.15 -0.66 0.24 -1.5 0.3 -3.03 c 0.03 -0.84 0.03 -2.94 0 -3 c -0.03 0 -0.18 0 -0.36 0.03 c -0.66 0.12 -0.99 0.12 -1.83 0.12 c -1.05 0 -1.71 -0.06 -2.61 -0.3 c -4.02 -0.99 -7.11 -4.35 -7.8 -8.46 c -0.12 -0.66 -0.12 -0.99 -0.12 -1.83 c 0 -0.84 0 -1.14 0.15 -1.92 c 0.36 -2.28 1.41 -4.62 3.3 -7.29 l 2.79 -3.6 c 0.54 -0.66 0.96 -1.2 0.96 -1.23 c 0 -0.03 -0.09 -0.33 -0.18 -0.69 c -0.96 -3.21 -1.41 -5.28 -1.59 -7.68 c -0.12 -1.38 -0.15 -3.09 -0.06 -3.96 c 0.33 -2.67 1.38 -5.07 3.12 -7.08 c 0.36 -0.42 0.99 -1.05 1.17 -1.14 z m 2.01 4.71 c -0.15 -0.3 -0.3 -0.54 -0.3 -0.54 c -0.03 0 -0.18 0.09 -0.3 0.21 c -2.4 1.74 -3.87 4.2 -4.26 7.11 c -0.06 0.54 -0.06 1.41 -0.03 1.89 c 0.09 1.29 0.48 3.12 1.08 5.22 c 0.15 0.42 0.24 0.78 0.24 0.81 c 0 0.03 0.84 -1.11 1.23 -1.68 c 1.89 -2.73 2.88 -5.07 3.15 -7.53 c 0.09 -0.57 0.12 -1.74 0.06 -2.37 c -0.09 -1.23 -0.27 -1.92 -0.87 -3.12 z m -2.94 20.7 c -0.21 -0.72 -0.39 -1.32 -0.42 -1.32 c 0 0 -1.2 1.47 -1.86 2.37 c -2.79 3.63 -4.02 6.3 -4.35 9.3 c -0.03 0.21 -0.03 0.69 -0.03 1.08 c 0 0.69 0 0.75 0.06 1.11 c 0.12 0.54 0.27 0.99 0.51 1.47 c 0.69 1.38 1.83 2.55 3.42 3.42 c 0.96 0.54 2.07 0.9 3.21 1.08 c 0.78 0.12 2.04 0.12 2.94 -0.03 c 0.51 -0.06 0.45 -0.03 0.42 -0.3 c -0.24 -3.33 -0.72 -6.33 -1.62 -10.08 c -0.09 -0.39 -0.18 -0.75 -0.18 -0.78 c -0.03 -0.03 -0.42 0 -0.81 0.09 c -0.9 0.18 -1.65 0.57 -2.22 1.14 c -0.72 0.72 -1.08 1.65 -1.05 2.64 c 0.06 0.96 0.48 1.83 1.23 2.58 c 0.36 0.36 0.72 0.63 1.17 0.9 c 0.33 0.18 0.36 0.21 0.42 0.33 c 0.18 0.42 -0.18 0.9 -0.6 0.87 c -0.18 -0.03 -0.84 -0.36 -1.26 -0.63 c -0.78 -0.51 -1.38 -1.11 -1.86 -1.83 c -1.77 -2.7 -0.99 -6.42 1.71 -8.19 c 0.3 -0.21 0.81 -0.48 1.17 -0.63 c 0.3 -0.09 1.02 -0.3 1.14 -0.3 c 0.06 0 0.09 0 0.09 -0.03 c 0.03 -0.03 -0.51 -1.92 -1.23 -4.26 z m 3.78 7.41 c -0.18 -0.03 -0.36 -0.06 -0.39 -0.06 c -0.03 0 0 0.21 0.18 1.02 c 0.75 3.18 1.26 6.3 1.5 9.09 c 0.06 0.72 0 0.69 0.51 0.42 c 0.78 -0.36 1.44 -0.96 1.98 -1.77 c 1.08 -1.62 1.2 -3.69 0.3 -5.55 c -0.81 -1.62 -2.31 -2.79 -4.08 -3.15 z";

const fClefPath = "M 6.3 -7.8 c 0.36 -0.03 1.65 0 2.13 0.03 c 3.6 0.42 6.03 2.1 6.93 4.86 c 0.27 0.84 0.36 1.5 0.36 2.58 c 0 0.9 -0.03 1.35 -0.18 2.16 c -0.78 3.78 -3.54 7.08 -8.37 9.96 c -1.74 1.05 -3.87 2.13 -6.18 3.12 c -0.39 0.18 -0.75 0.33 -0.81 0.36 c -0.06 0.03 -0.15 0.06 -0.18 0.06 c -0.15 0 -0.33 -0.18 -0.33 -0.33 c 0 -0.15 0.06 -0.21 0.51 -0.48 c 3 -1.77 5.13 -3.21 6.84 -4.74 c 0.51 -0.45 1.59 -1.5 1.95 -1.95 c 1.89 -2.19 2.88 -4.32 3.15 -6.78 c 0.06 -0.42 0.06 -1.77 0 -2.19 c -0.24 -2.01 -0.93 -3.63 -2.04 -4.71 c -0.63 -0.63 -1.29 -1.02 -2.07 -1.2 c -1.62 -0.39 -3.36 0.15 -4.56 1.44 c -0.54 0.6 -1.05 1.47 -1.32 2.22 l -0.09 0.21 l 0.24 -0.12 c 0.39 -0.21 0.63 -0.24 1.11 -0.24 c 0.3 0 0.45 0 0.66 0.06 c 1.92 0.48 2.85 2.55 1.95 4.38 c -0.45 0.99 -1.41 1.62 -2.46 1.71 c -1.47 0.09 -2.91 -0.87 -3.39 -2.25 c -0.18 -0.57 -0.21 -1.32 -0.03 -2.28 c 0.39 -2.25 1.83 -4.2 3.81 -5.19 c 0.69 -0.36 1.59 -0.6 2.37 -0.69 z m 11.58 2.52 c 0.84 -0.21 1.71 0.3 1.89 1.14 c 0.3 1.17 -0.72 2.19 -1.89 1.89 c -0.99 -0.21 -1.5 -1.32 -1.02 -2.25 c 0.18 -0.39 0.6 -0.69 1.02 -0.78 z m 0 7.5 c 0.84 -0.21 1.71 0.3 1.89 1.14 c 0.21 0.87 -0.3 1.71 -1.14 1.89 c -0.87 0.21 -1.71 -0.3 -1.89 -1.14 c -0.21 -0.84 0.3 -1.71 1.14 -1.89 z";

// Canonical C-Clef (Clave de Dó) SVG Path
const cClefPath = "M 4.2 -16.5 L 4.2 16.5 L 7.2 16.5 L 7.2 -16.5 Z M 0.5 -16.5 L 0.5 16.5 L 2.3 16.5 L 2.3 -16.5 Z M 7.2 -13.5 C 10.5 -13.5 13.5 -11.5 13.5 -7.2 C 13.5 -3.2 10.2 -1.2 7.2 -0.5 C 10.2 0.2 13.5 2.2 13.5 6.2 C 13.5 10.5 10.5 12.5 7.2 12.5 L 7.2 9.5 C 9.2 9.5 10.8 8.2 10.8 5.8 C 10.8 3.5 9.2 2.2 7.2 2.2 L 7.2 -2.2 C 9.2 -2.2 10.8 -3.5 10.8 -5.8 C 10.8 -8.2 9.2 -9.5 7.2 -9.5 Z";

const props = defineProps({
  modelValue: {
    type: [String, Array],
    default: ''
  },
  format: {
    type: String,
    default: 'abc' // 'abc' | 'musicxml' | 'json'
  },
  title: {
    type: String,
    default: 'Minha Partitura'
  },
  timeSignature: {
    type: String,
    default: '4/4'
  },
  keySignature: {
    type: String,
    default: 'C'
  },
  keyChanges: {
    type: Array,
    default: () => []
  },
  timeChanges: {
    type: Array,
    default: () => []
  },
  tempoChanges: {
    type: Array,
    default: () => []
  },
  tempoRamps: {
    type: Array,
    default: () => []
  },
  swing: {
    type: Object,
    default: () => ({ on: false, amount: 0.67 })
  },
  tempo: {
    type: Number,
    default: 120
  },
  clef: {
    type: String,
    default: 'treble' // 'treble' | 'bass'
  },
  wrapMode: {
    type: String,
    default: 'wrap' // 'wrap' | 'single'
  },
  readonly: {
    type: Boolean,
    default: false
  },
  embedded: {
    type: Boolean,
    default: false
  },
  fitContainer: {
    type: Boolean,
    default: false
  },
  widthPercent: {
    type: [Number, String],
    default: null
  },
  locale: {
    type: String,
    default: null
  },
  zoom: {
    type: [Number, String],
    default: 1
  },
  labels: {
    type: Object,
    default: () => ({})
  }
});

let i18nInstance = null;
try {
  i18nInstance = useI18n();
} catch (e) {
  // outside vue-i18n tree
}

const effectiveLocale = computed(() => {
  return props.locale || (i18nInstance && i18nInstance.locale && i18nInstance.locale.value) || localStorage.getItem('language') || 'pt-BR';
});

const t = (key, params, fallback) => {
  const text = getMusicSheetLabel(key, params, effectiveLocale.value, props.labels);
  return (text === key && fallback) ? fallback : text;
};

const emit = defineEmits([
  'playback-start',
  'playback-end',
  'playback-state','update:modelValue', 'change', 'export']);

// State
const showGuitarFrames = ref(true);
const scoreTitle = ref(props.title);
const scoreComposer = ref('');
// Computed proxy reativo bidirecional para a clave da voz ativa
const selectedClef = computed({
  get: () => {
    return (activeVoice.value && activeVoice.value.clef) || props.clef || 'treble';
  },
  set: (newClef) => {
    if (activeVoice.value) {
      activeVoice.value.clef = newClef;
      pushHistory();
      notifyChange();
    }
  }
});
const selectedTimeSignature = ref(props.timeSignature);
const selectedKey = ref(props.keySignature);
const selectedTempo = ref(props.tempo);
const selectedWrapMode = ref(props.wrapMode || 'wrap');

const internalZoom = ref(Number(props.zoom) || 1);

const effectiveZoom = computed(() => {
  const z = (props.zoom !== undefined && props.zoom !== null && props.zoom !== 1)
    ? Number(props.zoom)
    : (Number(zoomStore?.state?.zoom) || internalZoom.value || 1);
  return Math.max(0.4, Math.min(2.5, z || 1));
});

function zoomInScore() {
  internalZoom.value = Number(Math.min(effectiveZoom.value + 0.1, 2.5).toFixed(1));
}

function zoomOutScore() {
  internalZoom.value = Number(Math.max(effectiveZoom.value - 0.1, 0.4).toFixed(1));
}

function resetZoomScore() {
  internalZoom.value = 1;
}

watch(() => props.zoom, (newZ) => {
  if (newZ !== undefined && newZ !== null) {
    internalZoom.value = Number(newZ);
  }
}, { immediate: true });

// Mid-piece key/time signature changes: [{ measure, key }] / [{ measure, time }]
// measure is 0-based; measure 0 is covered by selectedKey/selectedTimeSignature.
const keyChanges = ref(Array.isArray(props.keyChanges) ? [...props.keyChanges] : []);
const timeChanges = ref(Array.isArray(props.timeChanges) ? [...props.timeChanges] : []);

function beatsForTimeSig(t) {
  const parts = String(t || '4/4').split('/');
  const b = parseInt(parts[0], 10) || 4;
  const bt = parseInt(parts[1], 10) || 4;
  return (b * 4) / bt;
}

// Quarter beats in measure m, following mid-piece time changes
function measureBeats(m) {
  let b = beatsForTimeSig(selectedTimeSignature.value);
  timeChanges.value
    .filter(e => e && e.measure <= m && /^\d+\/\d+$/.test(e.time || ''))
    .sort((a, b2) => a.measure - b2.measure)
    .forEach(e => { b = beatsForTimeSig(e.time); });
  return b;
}

function keyChangeAt(m) {
  const e = keyChanges.value.find(e => e && e.measure === m);
  return e ? e.key : null;
}

// Key signature active at measure m (for ornament auxiliaries in playback)
function keyForMeasure(m) {
  let k = selectedKey.value;
  keyChanges.value
    .filter(e => e && e.measure <= m)
    .sort((a, b2) => a.measure - b2.measure)
    .forEach(e => { k = e.key; });
  return k;
}

// Time signature active at measure m
function timeForMeasure(m) {
  let t = selectedTimeSignature.value;
  timeChanges.value
    .filter(e => e && e.measure <= m && /^\d+\/\d+$/.test(e.time || ''))
    .sort((a, b2) => a.measure - b2.measure)
    .forEach(e => { t = e.time; });
  return t;
}

function timeChangeAt(m) {
  const e = timeChanges.value.find(e => e && e.measure === m);
  return e ? e.time : null;
}

// Absolute quarter-beat offset where measure m starts
function measureStartBeat(m) {
  let s = 0;
  for (let i = 0; i < m; i++) s += measureBeats(i);
  return s;
}

// --- Mid-piece tempo -------------------------------------------------------
// tempoChanges: [{ measure, bpm }] sudden changes (0-based measure)
// tempoRamps: [{ fromMeasure, toMeasure, toBpm }] linear rit./accel. spans
const tempoChanges = ref(Array.isArray(props.tempoChanges) ? [...props.tempoChanges] : []);
const tempoRamps = ref(Array.isArray(props.tempoRamps) ? [...props.tempoRamps] : []);

function clampBpm(b) {
  return Math.max(30, Math.min(300, Math.round(b) || 120));
}

// Sorted tempo points in score-beat space: { beat, bpm, slide }
// slide=true means ramp from the previous point's bpm to this one.
function buildTempoPoints() {
  const base = clampBpm(selectedTempo.value || 120);
  const pts = [{ beat: 0, bpm: base, slide: false }];
  const endBeat = measureStartBeat(totalMeasures.value);
  tempoChanges.value.forEach(e => {
    if (!e || e.measure < 0) return;
    pts.push({ beat: measureStartBeat(e.measure), bpm: clampBpm(e.bpm), slide: false });
  });
  tempoRamps.value.forEach(r => {
    if (!r || r.toMeasure <= r.fromMeasure) return;
    const fb = measureStartBeat(Math.max(0, r.fromMeasure));
    const tb = Math.min(measureStartBeat(r.toMeasure + 1), Math.max(fb + 0.001, endBeat));
    if (tb <= fb) return;
    pts.push({ beat: fb, bpm: atBeatWith(pts, base, fb), slide: false });
    pts.push({ beat: tb, bpm: clampBpm(r.toBpm), slide: true });
  });
  // Sort; same beat: sudden (slide=false) wins over ramp ends
  pts.sort((a, b) => a.beat - b.beat || (a.slide ? 1 : -1));
  const deduped = [];
  pts.forEach(p => {
    const last = deduped[deduped.length - 1];
    if (last && Math.abs(last.beat - p.beat) < 1e-9) {
      if (!p.slide) deduped[deduped.length - 1] = p;
    } else {
      deduped.push(p);
    }
  });
  return deduped;
}

function atBeatWith(pts, base, b) {
  let bpm = base;
  pts.filter(p => p.beat <= b).sort((a, c) => a.beat - c.beat).forEach(p => { bpm = p.bpm; });
  return bpm;
}

function tempoAtBeat(b, pts) {
  let i = 0;
  while (i < pts.length - 1 && pts[i + 1].beat <= b + 1e-9) i++;
  const p0 = pts[i];
  const p1 = pts[i + 1];
  if (p1 && p1.slide && p1.beat > p0.beat && b >= p0.beat) {
    const t = Math.min(1, Math.max(0, (b - p0.beat) / (p1.beat - p0.beat)));
    return p0.bpm + (p1.bpm - p0.bpm) * t;
  }
  return p0.bpm;
}

// Integrate beats -> milliseconds under the tempo map
function msAtBeat(b, pts) {
  let ms = 0;
  for (let i = 0; i < pts.length; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const segEnd = p1 ? Math.min(p1.beat, b) : b;
    if (segEnd <= p0.beat) break;
    const segBeats = segEnd - p0.beat;
    let avg = p0.bpm;
    if (p1 && p1.slide && p1.beat > p0.beat) {
      const t = (segEnd - p0.beat) / (p1.beat - p0.beat);
      avg = (p0.bpm + (p0.bpm + (p1.bpm - p0.bpm) * t)) / 2;
    }
    ms += (segBeats / avg) * 60000;
    if (p1 && b <= p1.beat + 1e-9) break;
  }
  return ms;
}

function tempoChangeAt(m) {
  const e = tempoChanges.value.find(e => e && e.measure === m);
  return e ? clampBpm(e.bpm) : null;
}

function tempoRampAt(m) {
  const r = tempoRamps.value.find(r => r && r.fromMeasure === m);
  return r || null;
}

// Swing feel (playback only): straight eighth pairs -> long-short.
// amount = first-note fraction of a quarter (0.5 straight .. 0.75 hard).
const swing = ref({
  on: !!(props.swing && props.swing.on),
  amount: Math.min(0.75, Math.max(0.5, (props.swing && props.swing.amount) || 0.67))
});

// First global measure index rendered in a system
function systemFirstMeasure(system) {
  if (selectedWrapMode.value !== 'wrap') return 0;
  return system.index * MEASURES_PER_SYSTEM;
}

// Full key-signature set at a system start (with cancelling naturals)
function systemStartKeyAccs(staff, system) {
  const firstM = systemFirstMeasure(system);
  const nk = dispKey(keyForMeasure(firstM));
  const oldK = firstM > 0 ? dispKey(keyForMeasure(firstM - 1)) : nk;
  return keySigWithCancel(staff.clef, nk, oldK);
}

// Time signature string at a system start, or null when it matches the default
function systemStartTime(system) {
  if (system.index === 0) return selectedTimeSignature.value;
  const firstM = systemFirstMeasure(system);
  const t = timeForMeasure(firstM);
  return t !== selectedTimeSignature.value ? t : null;
}

// Combined key-change accidental set for a barline (with cancelling naturals)
function markerKeyAccs(staff, bar) {
  const nk = keyChangeAt(bar.measure);
  if (!nk) return [];
  return keySigWithCancel(staff.clef, dispKey(nk), dispKey(keyForMeasure(bar.measure - 1)));
}

function effectiveBpmAtMeasure(m) {
  return tempoAtBeat(measureStartBeat(m), buildTempoPoints());
}

// Measure index holding the explicitly selected note (mirrors layout splitting)
function measureOfSelectedNote() {
  const v = activeVoice.value;
  if (!v || !v.notes || selectedNoteIndex.value === null) return null;
  let m = 0;
  let cur = 0;
  for (let i = 0; i < selectedNoteIndex.value && i < v.notes.length; i++) {
    const n = v.notes[i];
    const q = durationToQuarterBeats(n.duration, n.isDotted, 1, n.dots || 0);
    if (cur > 0 && cur + q > measureBeats(m) + 0.0001) {
      m++;
      cur = 0;
    }
    cur += q;
    if (cur >= measureBeats(m) - 0.0001) {
      m++;
      cur = 0;
    }
  }
  return m;
}

function toggleKeyChangeHere() {
  if (props.readonly) return;
  const m = measureOfSelectedNote();
  if (m === null || m <= 0) return;
  pushHistory();
  const existing = keyChanges.value.findIndex(e => e.measure === m);
  if (existing >= 0 && keyChanges.value[existing].key === selectedKey.value) {
    keyChanges.value.splice(existing, 1);
  } else if (existing >= 0) {
    keyChanges.value[existing].key = selectedKey.value;
  } else {
    keyChanges.value.push({ measure: m, key: selectedKey.value });
  }
  notifyChange();
}

function toggleTimeChangeHere() {
  if (props.readonly) return;
  const m = measureOfSelectedNote();
  if (m === null || m <= 0) return;
  pushHistory();
  const existing = timeChanges.value.findIndex(e => e.measure === m);
  if (existing >= 0 && timeChanges.value[existing].time === selectedTimeSignature.value) {
    timeChanges.value.splice(existing, 1);
  } else if (existing >= 0) {
    timeChanges.value[existing].time = selectedTimeSignature.value;
  } else {
    timeChanges.value.push({ measure: m, time: selectedTimeSignature.value });
  }
  notifyChange();
}

function removeKeyChange(m) {
  if (props.readonly) return;
  pushHistory();
  keyChanges.value = keyChanges.value.filter(e => e.measure !== m);
  notifyChange();
}

function removeTimeChange(m) {
  if (props.readonly) return;
  pushHistory();
  timeChanges.value = timeChanges.value.filter(e => e.measure !== m);
  notifyChange();
}

function removeTempoChange(m) {
  if (props.readonly) return;
  pushHistory();
  tempoChanges.value = tempoChanges.value.filter(e => e.measure !== m);
  notifyChange();
}

function removeTempoRamp(fromMeasure) {
  if (props.readonly) return;
  pushHistory();
  tempoRamps.value = tempoRamps.value.filter(r => r.fromMeasure !== fromMeasure);
  notifyChange();
}

// Sudden BPM change at the selected note's measure (toggle off with same value)
function setBpmHere() {
  if (props.readonly) return;
  const m = measureOfSelectedNote();
  if (m === null || m < 0) return;
  const current = effectiveBpmAtMeasure(m);
  const val = prompt(t('structure.tempoBpmPrompt'), String(tempoChangeAt(m) ?? current));
  if (val === null) return;
  const bpm = clampBpm(parseInt(val, 10));
  if (!bpm) return;
  pushHistory();
  const existing = tempoChanges.value.findIndex(e => e.measure === m);
  if (existing >= 0 && tempoChanges.value[existing].bpm === bpm) {
    tempoChanges.value.splice(existing, 1);
  } else if (existing >= 0) {
    tempoChanges.value[existing].bpm = bpm;
  } else {
    tempoChanges.value.push({ measure: m, bpm });
  }
  notifyChange();
}

// Gradual rit./accel. from the selected measure to the last one
function startRampHere(kind) {
  if (props.readonly) return;
  const m = measureOfSelectedNote();
  if (m === null || m < 0) return;
  const last = Math.max(m + 1, totalMeasures.value - 1);
  const current = effectiveBpmAtMeasure(m);
  const def = kind === 'rit' ? Math.round(current * 0.65) : Math.round(current * 1.4);
  const val = prompt(t(kind === 'rit' ? 'structure.ritPrompt' : 'structure.accelPrompt'), String(def));
  if (val === null) return;
  const toBpm = clampBpm(parseInt(val, 10));
  if (!toBpm) return;
  pushHistory();
  tempoRamps.value = tempoRamps.value.filter(r => r.fromMeasure !== m);
  tempoRamps.value.push({ fromMeasure: m, toMeasure: last, toBpm, kind });
  notifyChange();
}

// Multi-Voice / Multi-Instrument Model
const voices = ref([
  {
    id: 'V1',
    name: 'Melodia',
    clef: props.clef || 'treble',
    instrument: 'acoustic_grand_piano',
    staffIndex: 0,
    stemDefault: 'auto',
    visible: true,
    notes: []
  }
]);
const activeVoiceId = ref('V1');

// Part extraction (view-only): when set, render/print/export/playback isolate
// this voice. Never persisted into score payloads or history.
const partVoiceId = ref(null);

function includedVoices(list) {
  const arr = list || [];
  if (partVoiceId.value) {
    const pv = arr.find(v => v.id === partVoiceId.value);
    return pv ? [pv] : [];
  }
  return arr.some(v => v.visible !== false) ? arr.filter(v => v.visible !== false) : arr;
}

function selectPart(id) {
  partVoiceId.value = id || null;
  if (id) activeVoiceId.value = id;
}

const activeVoice = computed(() => {
  return voices.value.find(v => v.id === activeVoiceId.value) || voices.value[0];
});

// Computed two-way proxy for the active voice's instrument
const selectedInstrument = computed({
  get: () => {
    return (activeVoice.value && activeVoice.value.instrument) || 'acoustic_grand_piano';
  },
  set: (newVal) => {
    if (activeVoice.value) {
      activeVoice.value.instrument = newVal;
      handleVoiceInstrumentChange(activeVoice.value);
    }
  }
});

const activeInstrumentIcon = computed(() => {
  return getInstrumentIcon(selectedInstrument.value);
});

function getInstrumentIcon(instId) {
  const inst = AVAILABLE_INSTRUMENTS.find(i => i.id === instId);
  return inst ? inst.icon : '🎵';
}


function handleVoiceInstrumentChange(voice) {
  if (!voice) return;
  if (voice.instrument === 'drum_kit' && voice.clef !== 'percussion') {
    voice.clef = 'percussion';
    if (voice.id === activeVoiceId.value) selectedClef.value = 'percussion';
  }
  preloadNotes(voice.notes || [], voice.instrument || 'acoustic_grand_piano');
  notifyChange();
}

function handleInstrumentChange() {
  if (activeVoice.value) {
    handleVoiceInstrumentChange(activeVoice.value);
  }
}

const isMultiVoice = computed(() => voices.value.length > 1);

// Backward-compatible computed proxy for active voice notes
const notes = computed({
  get: () => activeVoice.value ? activeVoice.value.notes : [],
  set: (val) => {
    if (activeVoice.value) activeVoice.value.notes = val;
  }
});

const totalNotesCount = computed(() => {
  return voices.value.reduce((acc, v) => acc + (v.notes ? v.notes.length : 0), 0);
});

// Full Undo / Redo History Stack Management
const undoStack = ref([]);
const redoStack = ref([]);
const isApplyingHistory = ref(false);

// Sidebar UI state
const sidebarCollapsed = ref(false);
const collapsedSections = ref({
  notes: false,
  rests: false,
  accidentals: false,
  modifiers: false,
  ligatures: false,
  structure: false,
  lyrics: false,
  chords: false,
  voices: false,
  actions: false,
  drums: false,
  tabArticulations: false,
});
function toggleSection(key) {
  collapsedSections.value[key] = !collapsedSections.value[key];
}

const isDottedSelected = ref(false);

const lyricsInputText = ref('');
const lyricsVerse = ref(1);

function applyLyricsToActiveVoice() {
  if (!activeVoice.value || !activeVoice.value.notes) return;
  pushHistory();
  const rawText = lyricsInputText.value.trim();
  const key = lyricsVerse.value === 2 ? 'lyric2' : 'lyric';
  if (!rawText) {
    // Clear lyrics of the selected verse only
    activeVoice.value.notes.forEach(n => { delete n[key]; });
    notifyChange();
    return;
  }

  // Tokenize words or syllables. A trailing hyphen marks melisma (extended syllable):
  // e.g. "Do- Re Mi" -> [{Do, melisma}, Re, Mi]; "Do-Re-Mi" -> Do, Re, Mi
  const words = rawText.split(/\s+/).filter(s => s.trim().length > 0);
  const syllables = [];
  words.forEach(w => {
    const hasTrailing = /-+$/.test(w);
    const parts = w.replace(/-+$/, '').split(/-+/).filter(p => p.length > 0);
    parts.forEach((p, i) => syllables.push({ text: p, melisma: hasTrailing && i === parts.length - 1 }));
  });
  let sIdx = 0;
  activeVoice.value.notes.forEach(note => {
    if (!note.isRest) {
      if (sIdx < syllables.length) {
        note[key] = syllables[sIdx].text;
        if (syllables[sIdx].melisma) note[key + 'Melisma'] = true;
        else delete note[key + 'Melisma'];
        sIdx++;
      } else {
        delete note[key];
        delete note[key + 'Melisma'];
      }
    }
  });
  notifyChange();
}

function clearLyricsFromActiveVoice() {
  if (!activeVoice.value || !activeVoice.value.notes) return;
  pushHistory();
  lyricsInputText.value = '';
  activeVoice.value.notes.forEach(n => { delete n.lyric; delete n.lyric2; });
  notifyChange();
}

const canUndo = computed(() => undoStack.value.length > 0);
const canRedo = computed(() => redoStack.value.length > 0);

function getSnapshot() {
  return JSON.stringify({
    voices: voices.value,
    activeVoiceId: activeVoiceId.value,
    selectedClef: selectedClef.value,
    selectedTimeSignature: selectedTimeSignature.value,
    selectedKey: selectedKey.value,
    selectedTempo: selectedTempo.value,
    scoreTitle: scoreTitle.value,
    keyChanges: keyChanges.value,
    timeChanges: timeChanges.value,
    tempoChanges: tempoChanges.value,
    tempoRamps: tempoRamps.value,
    swing: { ...swing.value }
  });
}

function restoreSnapshot(jsonStr) {
  try {
    const state = JSON.parse(jsonStr);
    isApplyingHistory.value = true;
    if (state.voices) voices.value = state.voices;
    if (state.activeVoiceId) activeVoiceId.value = state.activeVoiceId;
    if (state.selectedClef) selectedClef.value = state.selectedClef;
    if (state.selectedTimeSignature) selectedTimeSignature.value = state.selectedTimeSignature;
    if (state.selectedKey) selectedKey.value = state.selectedKey;
    if (state.selectedTempo) selectedTempo.value = state.selectedTempo;
    if (state.scoreTitle) scoreTitle.value = state.scoreTitle;
    if (Array.isArray(state.keyChanges)) keyChanges.value = state.keyChanges;
    if (Array.isArray(state.timeChanges)) timeChanges.value = state.timeChanges;
    if (Array.isArray(state.tempoChanges)) tempoChanges.value = state.tempoChanges;
    if (Array.isArray(state.tempoRamps)) tempoRamps.value = state.tempoRamps;
    if (state.swing) swing.value = { on: !!state.swing.on, amount: Math.min(0.75, Math.max(0.5, state.swing.amount || 0.67)) };
    isApplyingHistory.value = false;
    notifyChange();
  } catch (err) {
    console.error('Failed to restore score snapshot:', err);
    isApplyingHistory.value = false;
  }
}

function pushHistory() {
  if (isApplyingHistory.value) return;
  const currentSnapshot = getSnapshot();
  const lastSnapshot = undoStack.value[undoStack.value.length - 1];
  if (lastSnapshot !== currentSnapshot) {
    undoStack.value.push(currentSnapshot);
    if (undoStack.value.length > 50) {
      undoStack.value.shift();
    }
  }
  // Clear redo stack on new action
  redoStack.value = [];
}

function undo() {
  if (!canUndo.value) return;
  const currentState = getSnapshot();
  const previousState = undoStack.value.pop();
  redoStack.value.push(currentState);
  restoreSnapshot(previousState);
}

function redo() {
  if (!canRedo.value) return;
  const currentState = getSnapshot();
  const nextState = redoStack.value.pop();
  undoStack.value.push(currentState);
  restoreSnapshot(nextState);
}

const selectedDuration = ref('4'); // 4 = Quarter note
const isRestSelected = ref(false);
const selectedAccidental = ref(''); // '', '#', 'b'

const isFocused = ref(false);
const isPlaying = ref(false);
const activePlayingBeat = ref(-1);
const activePlayingNoteKeys = ref(new Set());
const hoveredNoteIndex = ref(null);
const selectedNoteIndex = ref(null);
const selectedNote = computed(() => {
  if (!activeVoice.value || !activeVoice.value.notes || activeVoice.value.notes.length === 0) return null;
  const idx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  return activeVoice.value.notes[idx] || null;
});

// Dots on the explicitly selected note (0/1/2). Used by the dotted button.
const selectedDotsCount = computed(() => {
  if (selectedNoteIndex.value === null || !activeVoice.value || !activeVoice.value.notes) return 0;
  return noteDotCount(activeVoice.value.notes[selectedNoteIndex.value]);
});

// Dotted button: cycles 0 -> 1 -> 2 -> 0 on the selected note;
// with no explicit selection it toggles the pending default for inserts.
function cycleDotsSelected() {
  if (!props.readonly && activeVoice.value && activeVoice.value.notes && selectedNoteIndex.value !== null) {
    const note = activeVoice.value.notes[selectedNoteIndex.value];
    if (note) {
      pushHistory();
      const nd = (noteDotCount(note) + 1) % 3;
      if (nd === 0) {
        delete note.dots;
        note.isDotted = false;
      } else {
        note.dots = nd;
        note.isDotted = true;
      }
      isDottedSelected.value = nd >= 1;
      notifyChange();
      return;
    }
  }
  isDottedSelected.value = !isDottedSelected.value;
}

const showImportModal = ref(false);
const importFormat = ref('abc');
const importText = ref('');
const importError = ref('');

const showExportModal = ref(false);
const exportFormat = ref('abc');
const copiedMessage = ref('');

const scoreScrollContainer = ref(null);

// Playback timeouts
let playTimeouts = [];

const tempoPresets = [
  { label: 'Largo', bpm: 60 },
  { label: 'Andante', bpm: 84 },
  { label: 'Moderato', bpm: 108 },
  { label: 'Allegro', bpm: 120 },
  { label: 'Vivace', bpm: 144 },
  { label: 'Presto', bpm: 168 }
];

// Voice Colors
const voiceColors = ['#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#ea580c'];
function getVoiceColor(voiceId) {
  const idx = voices.value.findIndex(v => v.id === voiceId);
  return voiceColors[(idx >= 0 ? idx : 0) % voiceColors.length];
}

function setActiveVoice(id) {
  activeVoiceId.value = id;
  if (activeVoice.value) {
    selectedClef.value = activeVoice.value.clef;
  }
}

// Options for custom select components
const instrumentGroups = computed(() => [
  {
    label: t('instruments.keys', {}, 'Teclas'),
    options: [
      { value: 'acoustic_grand_piano', label: t('instruments.grandPiano', {}, 'Piano de Cauda') },
      { value: 'bright_acoustic_piano', label: t('instruments.brightPiano', {}, 'Piano Brilhante') },
      { value: 'harpsichord', label: t('instruments.harpsichord', {}, 'Cravo (Harpsichord)') },
      { value: 'church_organ', label: t('instruments.churchOrgan', {}, 'Órgão de Tubos') }
    ]
  },
  {
    label: t('instruments.strings', {}, 'Cordas'),
    options: [
      { value: 'acoustic_guitar_nylon', label: t('instruments.nylonGuitar', {}, 'Violão (Nylon)') },
      { value: 'acoustic_guitar_steel', label: t('instruments.steelGuitar', {}, 'Violão (Aço)') },
      { value: 'violin', label: t('instruments.violin', {}, 'Violino') },
      { value: 'cello', label: t('instruments.cello', {}, 'Violoncelo') },
      { value: 'string_ensemble_1', label: t('instruments.stringEnsemble', {}, 'Orquestra de Cordas') }
    ]
  },
  {
    label: t('instruments.winds', {}, 'Sopros & Metais'),
    options: [
      { value: 'flute', label: t('instruments.flute', {}, 'Flauta Transversal') },
      { value: 'trumpet', label: t('instruments.trumpet', {}, 'Trompete') }
    ]
  },
  {
    label: t('instruments.percussion', {}, 'Percussão'),
    options: [
      { value: 'drum_kit', label: t('instruments.drumKit', {}, 'Bateria') }
    ]
  }
]);

const clefOptions = computed(() => [
  { value: 'treble', label: t('structure.clefTreble') },
  { value: 'bass', label: t('structure.clefBass') },
  { value: 'alto', label: t('structure.clefAlto') },
  { value: 'tenor', label: t('structure.clefTenor') },
  { value: 'percussion', label: t('structure.clefPercussion') }
]);

const timeSignatureOptions = [
  { value: '4/4', label: '4/4' },
  { value: '3/4', label: '3/4' },
  { value: '2/4', label: '2/4' },
  { value: '6/8', label: '6/8' }
];

const keyOptions = computed(() => [
  { value: 'C', label: `C (${t('structure.clefAlto', {}, 'Dó')} ${t('structure.major')})` },
  { value: 'G', label: `G (${t('clefs.treble', {}, 'Sol')} ${t('structure.major')})` },
  { value: 'D', label: `D (Ré ${t('structure.major')})` },
  { value: 'F', label: `F (${t('clefs.bass', {}, 'Fá')} ${t('structure.major')})` },
  { value: 'Bb', label: `B♭ (Si♭ ${t('structure.major')})` },
  { value: 'Gm', label: `Gm (${t('clefs.treble', {}, 'Sol')} ${t('structure.minor')})` },
  { value: 'Am', label: `Am (Lá ${t('structure.minor')})` },
  { value: 'Em', label: `Em (Mi ${t('structure.minor')})` },
  { value: 'Dm', label: `Dm (Ré ${t('structure.minor')})` }
]);

const swingOptions = computed(() => [
  { value: 0.58, label: t('structure.swingLight') },
  { value: 0.67, label: t('structure.swingMedium') },
  { value: 0.75, label: t('structure.swingHard') }
]);

const partOptions = computed(() => [
  { value: '', label: t('voices.allParts') },
  ...voices.value.map(v => ({ value: v.id, label: v.name || v.id }))
]);

const capoOptions = computed(() => {
  const opts = [{ value: 0, label: t('voices.capoNone') }];
  for (let c = 1; c <= 7; c++) {
    opts.push({ value: c, label: t('voices.capo', { n: c }) });
  }
  return opts;
});

// Staff assignment options: existing staves + one new staff
const staffOptions = computed(() => {
  const indices = new Set();
  voices.value.forEach(v => indices.add(v.staffIndex ?? 0));
  const max = indices.size > 0 ? Math.max(...indices) : 0;
  const opts = [];
  for (let i = 0; i <= max; i++) {
    opts.push({ value: i, label: t('voices.staff', { n: i + 1 }) });
  }
  opts.push({ value: max + 1, label: t('voices.newStaff') });
  return opts;
});

function setVoiceStaff(voiceId, value) {
  const v = voices.value.find(vc => vc.id === voiceId);
  if (!v) return;
  const idx = parseInt(value, 10);
  if (isNaN(idx) || idx < 0 || idx === (v.staffIndex ?? 0)) return;
  pushHistory();
  v.staffIndex = idx;
  notifyChange();
}

const transposeOptions = [
  { value: 0, label: 'C' },
  { value: -2, label: 'Bb' },
  { value: -9, label: 'Eb' },
  { value: -7, label: 'F' }
];

function setVoiceTranspose(voiceId, value) {
  const v = voices.value.find(vc => vc.id === voiceId);
  if (!v) return;
  const t = parseInt(value, 10) || 0;
  if ((v.transpose || 0) === t) return;
  pushHistory();
  if (t === 0) {
    delete v.transpose;
  } else {
    v.transpose = t;
  }
  notifyChange();
}

function setVoiceCapo(voiceId, value) {
  const v = voices.value.find(vc => vc.id === voiceId);
  if (!v) return;
  const capo = Math.max(0, Math.min(7, parseInt(value, 10) || 0));
  if ((v.capo || 0) === capo) return;
  pushHistory();
  if (capo === 0) {
    delete v.capo;
  } else {
    v.capo = capo;
  }
  notifyChange();
}

function toggleVoiceVisibility(voiceId) {
  const v = voices.value.find(item => item.id === voiceId);
  if (v) {
    v.visible = v.visible === false ? true : false;
  }
}

// TAB button cycles: off -> TAB + notation -> TAB only -> off
function cycleVoiceTab(voiceId) {
  if (props.readonly) return;
  const v = voices.value.find(item => item.id === voiceId);
  if (!v) return;
  pushHistory();
  if (!v.showTab) {
    v.showTab = true;
  } else if (!v.tabOnly) {
    v.tabOnly = true;
  } else {
    v.showTab = false;
    delete v.tabOnly;
  }
  notifyChange();
}

function addNewVoice() {
  pushHistory();
  const nextIdx = voices.value.length + 1;
  const newId = `V${nextIdx}`;
  const newClef = selectedClef.value || 'treble';
  const nextStaffIdx = voices.value.length;
  voices.value.push({
    id: newId,
    name: `Voz ${nextIdx}`,
    clef: newClef,
    instrument: 'acoustic_grand_piano',
    staffIndex: nextStaffIdx,
    stemDefault: 'auto',
    visible: true,
    notes: []
  });
  activeVoiceId.value = newId;
  notifyChange();
}

function removeCurrentVoice() {
  if (voices.value.length <= 1) return;
  pushHistory();
  const idx = voices.value.findIndex(v => v.id === activeVoiceId.value);
  if (idx !== -1) {
    voices.value.splice(idx, 1);
    if (partVoiceId.value === activeVoiceId.value) partVoiceId.value = null;
    activeVoiceId.value = voices.value[0].id;
    selectedClef.value = activeVoice.value.clef;
    notifyChange();
  }
}

// Layout Geometry Constants
const STAFF_TOP_Y = 48;
const LINE_SPACING = 10;
const START_X_FIRST = 105;
const START_X_SUBSEQUENT = 75;
const STAFF_SPACING = 90;
const MEASURES_PER_SYSTEM = 4;
const WRAP_SYSTEM_WIDTH = 820;

function getStaffLineY(lineIndexFromTop) {
  return STAFF_TOP_Y + (lineIndexFromTop * LINE_SPACING);
}

// Barline vertical extent for a system (TAB-only staves span their TAB block)
function barTopFor(system) {
  const st = system.staves[0];
  if (st && st.showTabOnly) return st.yOffset + st.tabBase;
  return getStaffLineY(0);
}

function barBottomFor(system) {
  const st = system.staves[system.staves.length - 1];
  if (st && st.showTabOnly) return st.yOffset + st.tabBase + (TAB_STRINGS - 1) * TAB_LINE_SPACING;
  return st.yOffset + getStaffLineY(4);
}

// Distinct Staves across visible voices
// Guitar tablature geometry (standard tuning E2 A2 D3 G3 B3 E4)
// Staff lines span STAFF_TOP_Y (48) .. getStaffLineY(4) (88); TAB sits below.
const TAB_LINE_SPACING = 9;
const TAB_STRINGS = 6;
const TAB_TOP_OFF = 114; // 5-line staff bottom (88) + 26 gap
const TAB_BLOCK_H = TAB_TOP_OFF + (TAB_STRINGS - 1) * TAB_LINE_SPACING + 22;
const TAB_ONLY_H = 18 + (TAB_STRINGS - 1) * TAB_LINE_SPACING + 22;
// Staff-local Y just below a TAB block (for name tags, lyrics, dynamics)
const TAB_OPEN_MIDIS = [40, 45, 50, 55, 59, 64]; // low E .. high E

// Pitch -> { string (1 = high E, top line), fret } or null if unplayable
function tabFretFor(step, octave, accidental = '') {
  let midi;
  try {
    midi = getMidiNumber(step, octave, accidental);
  } catch (e) {
    return null;
  }
  if (midi === null || midi === undefined || isNaN(midi)) return null;
  // Prefer lowest fret in comfortable position (0-4), highest string first
  for (let s = 5; s >= 0; s--) {
    const f = midi - TAB_OPEN_MIDIS[s];
    if (f >= 0 && f <= 4) return { string: 6 - s, fret: f };
  }
  // Fallback: smallest reachable fret anywhere
  let best = null;
  for (let s = 5; s >= 0; s--) {
    const f = midi - TAB_OPEN_MIDIS[s];
    if (f >= 0 && f <= 24 && (!best || f < best.fret)) best = { string: 6 - s, fret: f };
  }
  return best;
}

function tabNumbersFor(system, staff) {
  const out = [];
  const sysNotes = system.notes || [];
  (staff.voices || []).forEach(v => {
    if (!v.showTab) return;
    const capo = Math.max(0, Math.min(7, parseInt(v.capo, 10) || 0));
    sysNotes.forEach(n => {
      if (n.voiceId !== v.id || n.isRest || n.isHidden || n.drum) return;
      const pushTone = (step, octave, acc, explicitString, explicitFret) => {
        let stringVal;
        let fretVal;
        if (explicitString && explicitFret !== undefined && !isNaN(explicitFret)) {
          stringVal = explicitString;
          fretVal = explicitFret;
        } else {
          const tf = tabFretFor(step, octave, acc || '');
          if (!tf) return;
          stringVal = tf.string;
          fretVal = tf.fret;
        }
        const rel = fretVal - capo;
        let displayFret = rel < 0 ? '—' : rel;
        if (n.tabDead) {
          displayFret = 'X';
        } else if (n.tabHarmonic) {
          displayFret = `<${displayFret}>`;
        } else if (n.tabGhost) {
          displayFret = `(${displayFret})`;
        }
        out.push({
          x: n.x,
          y: staff.tabBase + (stringVal - 1) * TAB_LINE_SPACING,
          string: stringVal,
          rawFret: rel,
          fret: displayFret,
          noteRef: n,
          noteIndex: n.index,
          voiceId: n.voiceId,
          key: `${n.globalKey}-${step}${octave}-${stringVal}`
        });
      };
      pushTone(n.step, n.octave, n.accidental, n.tabString, n.tabFret);
      (n.pitches || []).forEach(p => pushTone(p.step, p.octave, p.accidental, p.tabString, p.tabFret));
    });
  });
  return out;
}

const activeTabString = ref(1);
let fretInputTimer = null;
let fretInputBuffer = '';

const currentTabFretDisplay = computed(() => {
  if (selectedNoteIndex.value === null || !activeVoice.value) return '—';
  const n = activeVoice.value.notes[selectedNoteIndex.value];
  if (!n || n.isRest) return '—';
  if (n.tabFret !== undefined && !isNaN(n.tabFret)) return n.tabFret;
  const tf = tabFretFor(n.step, n.octave, n.accidental || '');
  return tf ? tf.fret : '—';
});

function playSelectedNoteSound() {
  if (selectedNoteIndex.value === null || !activeVoice.value) return;
  const n = activeVoice.value.notes[selectedNoteIndex.value];
  if (!n || n.isRest) return;
  const inst = activeVoice.value.instrument || 'acoustic_grand_piano';
  playRealizedNote({ ...n, accidental: n.accidental || keyAlterFor(n.step, selectedKey.value) }, 0.25, inst);
}

function setFretOnSelectedNote(fret, string = activeTabString.value) {
  if (selectedNoteIndex.value === null || !activeVoice.value || !activeVoice.value.showTab) return;
  const note = activeVoice.value.notes[selectedNoteIndex.value];
  if (!note || note.isRest) return;

  pushHistory();

  const capo = Math.max(0, Math.min(7, parseInt(activeVoice.value.capo, 10) || 0));
  const targetString = Math.max(1, Math.min(6, string || 1));
  const openMidi = TAB_OPEN_MIDIS[6 - targetString] + capo;
  const targetMidi = openMidi + fret;
  const p = transposePitch(targetMidi, 0, false);
  const cp = writtenToConcert(p.step, p.octave, p.accidental, activeVoice.value.transpose || 0);

  note.step = cp.step;
  note.octave = cp.octave;
  note.pitch = cp.pitch;
  note.accidental = cp.accidental;
  note.tabString = targetString;
  note.tabFret = fret;
  if (note.tabDead) note.tabDead = false;

  activeTabString.value = targetString;

  const inst = activeVoice.value.instrument || 'acoustic_grand_piano';
  playNoteSound(p.step, p.octave, p.accidental, 0.25, inst);

  notifyChange();
}

function stepFret(delta) {
  if (selectedNoteIndex.value === null || !activeVoice.value) return;
  const n = activeVoice.value.notes[selectedNoteIndex.value];
  if (!n || n.isRest) return;
  let cur = n.tabFret;
  if (cur === undefined || isNaN(cur)) {
    const tf = tabFretFor(n.step, n.octave, n.accidental || '');
    cur = tf ? tf.fret : 0;
  }
  const next = Math.max(0, Math.min(24, cur + delta));
  setFretOnSelectedNote(next, activeTabString.value || n.tabString || 1);
}

function handleTabStringChange(newString) {
  activeTabString.value = newString;
  if (selectedNoteIndex.value === null || !activeVoice.value) return;
  const n = activeVoice.value.notes[selectedNoteIndex.value];
  if (!n || n.isRest) return;
  let curFret = n.tabFret;
  if (curFret === undefined || isNaN(curFret)) {
    const tf = tabFretFor(n.step, n.octave, n.accidental || '');
    curFret = tf ? tf.fret : 0;
  }
  setFretOnSelectedNote(curFret, newString);
}

function handleTabFretClick(tn) {
  if (props.readonly || !tn) return;
  activeVoiceId.value = tn.voiceId;
  selectedNoteIndex.value = tn.noteIndex;
  activeTabString.value = tn.string;
  const v = voices.value.find(vc => vc.id === tn.voiceId);
  const inst = (v && v.instrument) || 'acoustic_grand_piano';
  const raw = v ? v.notes[tn.noteIndex] : null;
  if (raw && !raw.isRest) {
    playRealizedNote({ ...raw, accidental: raw.accidental || keyAlterFor(raw.step, selectedKey.value) }, 0.3, inst);
  }
}

function tabDecorationsFor(system, staff) {
  const decs = {
    slides: [],
    bends: [],
    slurs: [],
    vibratos: [],
    palmMutes: []
  };

  const tones = tabNumbersFor(system, staff);
  if (!tones || tones.length === 0) return decs;

  // Group tones by string for connecting lines (slides, hammer-ons, pull-offs, ties)
  const stringTones = new Map();
  tones.forEach(t => {
    if (!stringTones.has(t.string)) stringTones.set(t.string, []);
    stringTones.get(t.string).push(t);
  });

  // Sort tones per string by x ascending
  stringTones.forEach(list => list.sort((a, b) => a.x - b.x));

  // 1. SLIDES & LIGADURAS (Hammer/Pull/Slur/Tie) per string
  stringTones.forEach((list) => {
    for (let i = 0; i < list.length; i++) {
      const cur = list[i];
      const next = list[i + 1];
      const n = cur.noteRef;

      // Slide
      if (n.tabSlide) {
        if (next && Math.abs(next.x - cur.x) < 180) {
          const isUp = n.tabSlide === 'up' || (n.tabSlide !== 'down' && next.rawFret > cur.rawFret);
          const x1 = cur.x + 9;
          const x2 = next.x - 9;
          const yMid = cur.y;
          const y1 = isUp ? yMid + 3.5 : yMid - 3.5;
          const y2 = isUp ? yMid - 3.5 : yMid + 3.5;
          decs.slides.push({
            id: `slide-${system.index}-${cur.key}`,
            x1, y1, x2, y2,
            isUp
          });
        } else {
          // Slide in/out
          const isUp = n.tabSlide === 'up';
          const x1 = cur.x + 8;
          const x2 = cur.x + 18;
          const yMid = cur.y;
          decs.slides.push({
            id: `slide-single-${system.index}-${cur.key}`,
            x1,
            y1: isUp ? yMid + 3 : yMid - 3,
            x2,
            y2: isUp ? yMid - 3 : yMid + 3,
            isUp
          });
        }
      }

      // Hammer-on / Pull-off / Ligadura (Slur or Tie)
      const hasLigature = n.tabHammer || n.tabPull || n.slurStart || n.hasTie;
      if (hasLigature && next && Math.abs(next.x - cur.x) < 220) {
        const startX = cur.x + 4;
        const endX = next.x - 4;
        const midX = (startX + endX) / 2;
        const baseY = cur.y - 7;
        const dist = Math.abs(endX - startX);
        const arcH = Math.min(16, Math.max(8, dist * 0.16));
        const ctrlY = baseY - arcH;

        let label = '';
        if (n.tabHammer || (n.slurStart && next.rawFret > cur.rawFret)) {
          label = 'H';
        } else if (n.tabPull || (n.slurStart && next.rawFret < cur.rawFret)) {
          label = 'P';
        }

        decs.slurs.push({
          id: `tab-slur-${system.index}-${cur.key}`,
          d: `M ${startX} ${baseY} Q ${midX} ${ctrlY} ${endX} ${baseY}`,
          label,
          labelX: midX,
          labelY: ctrlY - 2
        });
      }
    }
  });

  // 2. BENDS, VIBRATOS & PALM MUTE (per tone / note)
  tones.forEach(t => {
    const n = t.noteRef;

    // Bend
    if (n.tabBend) {
      const x0 = t.x + 7;
      const y0 = t.y - 2;
      const isRelease = n.tabBend === 'release';
      const label = n.tabBend === 'full' || n.tabBend === '1' ? 'Full'
        : n.tabBend === '1/2' || n.tabBend === 'half' ? '1/2'
        : n.tabBend === '1/4' || n.tabBend === 'quarter' ? '1/4'
        : n.tabBend === '1 1/2' ? '1 1/2'
        : n.tabBend === 'release' ? 'Rel.'
        : String(n.tabBend);

      let d = '';
      let arrowTipX = x0 + 12;
      let arrowTipY = y0 - 16;

      if (isRelease) {
        d = `M ${x0} ${y0} Q ${x0 + 6} ${y0 - 18} ${x0 + 12} ${y0 - 18} Q ${x0 + 18} ${y0 - 18} ${x0 + 22} ${y0}`;
        arrowTipX = x0 + 22;
        arrowTipY = y0;
      } else {
        d = `M ${x0} ${y0} Q ${x0 + 6} ${y0 - 16} ${arrowTipX} ${arrowTipY}`;
      }

      decs.bends.push({
        id: `bend-${system.index}-${t.key}`,
        d,
        arrowTipX,
        arrowTipY,
        label,
        labelX: x0 + 12,
        labelY: y0 - 20
      });
    }

    // Vibrato
    if (n.tabVibrato) {
      const vx = t.x - 6;
      const vy = t.y - 7;
      decs.vibratos.push({
        id: `vibrato-${system.index}-${t.key}`,
        d: `M ${vx} ${vy} q 2.5 -2.5 5 0 q 2.5 2.5 5 0 q 2.5 -2.5 5 0 q 2.5 2.5 5 0`
      });
    }

    // Palm Mute
    if (n.tabPalmMute) {
      decs.palmMutes.push({
        id: `pm-${system.index}-${t.key}`,
        x1: t.x - 8,
        x2: t.x + 12,
        y: staff.tabBase - 4,
        label: 'P.M.'
      });
    }
  });

  return decs;
}

// Staff of a note carrying a TAB block, or null
function tabStaffOf(noteItem) {
  const v = voices.value.find(vc => vc.id === noteItem.voiceId);
  if (!v) return null;
  const st = distinctStaves.value.find(s => s.index === (v.staffIndex ?? 0));
  return (st && st.showTab) ? st : null;
}

// Staff-local Y just below the TAB block (for tags/lyrics/dynamics)
function belowTabY(noteItem) {
  const st = tabStaffOf(noteItem);
  return st ? st.tabBase + (TAB_STRINGS - 1) * TAB_LINE_SPACING + 16 : null;
}

// True when the voice renders TAB only (standard notation hidden)
function isTabOnlyVoice(voiceId) {
  const v = voices.value.find(vc => vc.id === voiceId);
  return !!(v && v.showTab && v.tabOnly);
}

// True when a staff-local Y falls inside a TAB block (clicks there are ignored)
function inTabZone(staff, staffY) {
  if (!staff.showTab) return false;
  const top = staff.tabBase - 8;
  const bottom = staff.tabBase + (TAB_STRINGS - 1) * TAB_LINE_SPACING + 16;
  return staffY >= top && staffY <= bottom;
}

const distinctStaves = computed(() => {
  const map = new Map();
  // Filter only visible voices (or fallback to all if all are hidden)
  const activeList = includedVoices(voices.value);

  // Re-index staff positions dynamically so hidden staves collapse seamlessly
  const uniqueStaffIndices = Array.from(new Set(activeList.map(v => v.staffIndex !== undefined ? v.staffIndex : 0))).sort((a, b) => a - b);
  const staffDisplayIndexMap = new Map();
  uniqueStaffIndices.forEach((origIdx, displayIdx) => {
    staffDisplayIndexMap.set(origIdx, displayIdx);
  });

  activeList.forEach(v => {
    const origStaffIdx = v.staffIndex !== undefined ? v.staffIndex : 0;
    const displayStaffIdx = staffDisplayIndexMap.get(origStaffIdx) || 0;
    if (!map.has(origStaffIdx)) {
      const staffClef = (activeVoice.value && (activeVoice.value.staffIndex || 0) === origStaffIdx) ? (activeVoice.value.clef || 'treble') : (v.clef || 'treble');
      map.set(origStaffIdx, {
        index: origStaffIdx,
        displayIndex: displayStaffIdx,
        clef: staffClef,
        name: v.name || `Pauta ${displayStaffIdx + 1}`,
        yOffset: displayStaffIdx * STAFF_SPACING,
        voices: []
      });
    }
    map.get(origStaffIdx).voices.push(v);
  });
  // Second pass in display order: flag tab staves and stack yOffsets so a
  // TAB block pushes subsequent staves down (fixed STAFF_SPACING otherwise)
  const ordered = Array.from(map.values()).sort((a, b) => a.displayIndex - b.displayIndex);
  let runY = 0;
  ordered.forEach(st => {
    st.showTab = st.voices.some(v => v.showTab);
    st.showTabOnly = st.voices.length > 0 && st.voices.every(v => v.showTab && v.tabOnly);
    st.tabBase = st.showTabOnly ? 18 : TAB_TOP_OFF;
    st.yOffset = runY;
    const tabExtra = st.showTab ? (st.showTabOnly ? TAB_ONLY_H : (TAB_BLOCK_H - getStaffLineY(4))) : 0;
    runY += STAFF_SPACING + tabExtra;
  });
  return ordered;
});

const belowTabContent = computed(() => {
  const hasTab = distinctStaves.value.some(st => st.showTab);
  if (!hasTab) return 0;
  let need = 0;
  voices.value.forEach(v => (v.notes || []).forEach(n => {
    if (n.lyric || n.lyric2 || n.dynamic) need = Math.max(need, 70);
    if (n.pedalStart || n.pedalEnd) need = Math.max(need, 90);
  }));
  return need;
});

const systemHeight = computed(() => {
  const sts = distinctStaves.value;
  if (!sts || sts.length === 0) return 140;

  const lastStaff = sts[sts.length - 1];
  let systemContentHeight;
  if (lastStaff.showTab) {
    systemContentHeight = lastStaff.yOffset + (lastStaff.showTabOnly ? TAB_ONLY_H : TAB_BLOCK_H);
  } else {
    systemContentHeight = lastStaff.yOffset + getStaffLineY(4);
  }

  // Inter-system spacing: gap between bottom of one system and top of next system
  const gap = lastStaff.showTab
    ? (lastStaff.showTabOnly ? 45 : 24)
    : 52;

  return systemContentHeight + gap + belowTabContent.value;
});

function getGuitarChord(name) {
  return getGuitarChordData(name);
}

// Cached lookup for fretboard frame rendering (cheap dict access; cached per chord name)
const guitarFrameCache = new Map();
function guitarFrame(chordName) {
  if (!chordName) return null;
  if (guitarFrameCache.has(chordName)) return guitarFrameCache.get(chordName);
  const data = getGuitarChordData(chordName);
  // Bound cache size (chord vocabulary is small, but guard anyway)
  if (guitarFrameCache.size > 200) guitarFrameCache.clear();
  guitarFrameCache.set(chordName, data || null);
  return data || null;
}

function pitchToY(step, octave, clef = selectedClef.value) {
  const diatonic = getDiatonicIndex(step, octave);
  if (clef === 'bass') {
    const bassTopDiatonic = -2;
    return STAFF_TOP_Y + (bassTopDiatonic - diatonic) * (LINE_SPACING / 2);
  } else if (clef === 'alto' || clef === 'c') {
    // Alto Clef: Middle Line (index 2) is C4 (diatonic index 0)
    // Top line (index 0) is F4 (diatonic index 3)
    const altoTopDiatonic = 3;
    return STAFF_TOP_Y + (altoTopDiatonic - diatonic) * (LINE_SPACING / 2);
  } else if (clef === 'tenor') {
    // Tenor Clef: 4th line from bottom / 2nd line from top (index 1) is C4 (diatonic index 0)
    // Top line (index 0) is D4 (diatonic index 1)
    const tenorTopDiatonic = 1;
    return STAFF_TOP_Y + (tenorTopDiatonic - diatonic) * (LINE_SPACING / 2);
  } else {
    const trebleTopDiatonic = 10;
    return STAFF_TOP_Y + (trebleTopDiatonic - diatonic) * (LINE_SPACING / 2);
  }
}

function yToPitch(y, clef = selectedClef.value) {
  const stepHalf = LINE_SPACING / 2;
  const clampedY = Math.round((y - STAFF_TOP_Y) / stepHalf) * stepHalf + STAFF_TOP_Y;

  let diatonic = 0;
  if (clef === 'bass') {
    const bassTopDiatonic = -2;
    diatonic = bassTopDiatonic - Math.round((clampedY - STAFF_TOP_Y) / stepHalf);
  } else if (clef === 'alto' || clef === 'c') {
    const altoTopDiatonic = 3;
    diatonic = altoTopDiatonic - Math.round((clampedY - STAFF_TOP_Y) / stepHalf);
  } else if (clef === 'tenor') {
    const tenorTopDiatonic = 1;
    diatonic = tenorTopDiatonic - Math.round((clampedY - STAFF_TOP_Y) / stepHalf);
  } else {
    const trebleTopDiatonic = 10;
    diatonic = trebleTopDiatonic - Math.round((clampedY - STAFF_TOP_Y) / stepHalf);
  }

  const pitchInfo = getPitchFromDiatonicIndex(diatonic);
  return {
    ...pitchInfo,
    y: clampedY
  };
}

function getLedgerLines(y) {
  const ledgers = [];
  const topStaffY = getStaffLineY(0);
  const bottomStaffY = getStaffLineY(4);

  if (y < topStaffY) {
    for (let currentY = topStaffY - LINE_SPACING; currentY >= y - 2; currentY -= LINE_SPACING) {
      ledgers.push(currentY);
    }
  } else if (y > bottomStaffY) {
    for (let currentY = bottomStaffY + LINE_SPACING; currentY <= y + 2; currentY += LINE_SPACING) {
      ledgers.push(currentY);
    }
  }

  return ledgers;
}

// Ghost Note on mouse hover
const ghostNote = ref({
  visible: false,
  x: 0,
  y: 0,
  yOffset: 0,
  pitch: 'C4',
  step: 'C',
  octave: 4,
  stemUp: true,
  ledgerLines: []
});

const ghostTabNote = ref({
  visible: false,
  x: 0,
  y: 0,
  yOffset: 0,
  string: 1,
  fret: 0
});

function handleStaffMouseMove(e) {
  if (props.readonly || props.embedded || !scoreScrollContainer.value) return;
  const rect = scoreScrollContainer.value.getBoundingClientRect();
  const mouseX = e.clientX - rect.left + scoreScrollContainer.value.scrollLeft;
  const mouseY = e.clientY - rect.top + scoreScrollContainer.value.scrollTop;

  const isWrap = selectedWrapMode.value === 'wrap';
  const sysH = systemHeight.value;
  const numSystems = systems.value.length || 1;
  const systemIndex = isWrap
    ? Math.max(0, Math.min(numSystems - 1, Math.floor(mouseY / sysH)))
    : 0;

  const sysYOffset = systemIndex * sysH;
  const localY = mouseY - sysYOffset;

  // Determine which staff inside the system the mouse is closest to
  const curStaves = distinctStaves.value;
  let targetStaff = curStaves[0];
  let minStaffDist = 9999;

  curStaves.forEach(st => {
    const staffMidY = st.yOffset + getStaffLineY(2);
    const dist = Math.abs(localY - staffMidY);
    if (dist < minStaffDist) {
      minStaffDist = dist;
      targetStaff = st;
    }
  });

  const staffY = localY - targetStaff.yOffset;
  if (inTabZone(targetStaff, staffY)) {
    ghostNote.value.visible = false;
    const relY = staffY - targetStaff.tabBase;
    const rawString = Math.round(relY / TAB_LINE_SPACING) + 1;
    const stringIndex = Math.max(1, Math.min(6, rawString));
    const startX = (systemIndex === 0 ? START_X_FIRST : START_X_SUBSEQUENT);
    const targetX = Math.max(startX + 10, mouseX);
    ghostTabNote.value = {
      visible: true,
      x: targetX,
      y: targetStaff.tabBase + (stringIndex - 1) * TAB_LINE_SPACING,
      yOffset: sysYOffset + targetStaff.yOffset,
      string: stringIndex,
      fret: 0
    };
    return;
  }
  ghostTabNote.value.visible = false;
  if (staffY < 15 || staffY > 125) {
    ghostNote.value.visible = false;
    return;
  }

  const pitchData = yToPitch(staffY, targetStaff.clef);
  const startX = (systemIndex === 0 ? START_X_FIRST : START_X_SUBSEQUENT);
  const targetX = Math.max(startX + 10, mouseX);

  const middleLineY = getStaffLineY(2);
  const stemUp = pitchData.y >= middleLineY;

  ghostNote.value = {
    visible: true,
    x: targetX,
    y: pitchData.y,
    yOffset: sysYOffset + targetStaff.yOffset,
    pitch: pitchData.pitch,
    step: pitchData.step,
    octave: pitchData.octave,
    stemUp,
    ledgerLines: getLedgerLines(pitchData.y)
  };
}

function handleStaffMouseLeave() {
  ghostNote.value.visible = false;
  ghostTabNote.value.visible = false;
}

// Insert a drum piece into a voice (clef forced to percussion)
function insertDrumPiece(pieceId, voice = null) {
  if (props.readonly) return;
  const target = voice || activeVoice.value;
  if (!target || !target.notes) return;
  const piece = drumPieceById(pieceId);
  if (!piece) return;
  pushHistory();
  if (target.clef !== 'percussion') target.clef = 'percussion';
  target.notes.push({
    step: piece.step,
    octave: piece.octave,
    pitch: `${piece.step}${piece.octave}`,
    accidental: '',
    duration: selectedDuration.value,
    isDotted: isDottedSelected.value,
    dots: isDottedSelected.value ? 1 : 0,
    isRest: isRestSelected.value,
    drum: isRestSelected.value ? undefined : piece.id,
    notehead: isRestSelected.value ? undefined : (piece.head === 'x' ? 'x' : undefined)
  });
  if (!isRestSelected.value) {
    playDrumSound(piece.id, { durationSec: 0.2, gainLevel: 0.8 });
  }
  notifyChange();
}

function handleTabZoneClick(e, staff, staffY, systemIndex, mouseX) {
  if (props.readonly || props.embedded) return;
  let voiceToInsert = (staff.voices || []).find(v => v.showTab) || activeVoice.value;
  if (!voiceToInsert || !voiceToInsert.showTab) {
    const matched = voices.value.find(v => v.showTab && v.staffIndex === staff.index);
    if (matched) voiceToInsert = matched;
  }
  if (!voiceToInsert) return;
  activeVoiceId.value = voiceToInsert.id;

  const relY = staffY - staff.tabBase;
  const rawString = Math.round(relY / TAB_LINE_SPACING) + 1;
  const stringIndex = Math.max(1, Math.min(6, rawString));
  activeTabString.value = stringIndex;

  const isWrap = selectedWrapMode.value === 'wrap';
  const sysNotes = (systems.value[systemIndex]?.notes || []).filter(n => n.voiceId === voiceToInsert.id && !n.isHidden && !n.isRest);
  const nearNote = sysNotes.find(n => Math.abs(n.x - mouseX) < 16);
  if (nearNote) {
    selectedNoteIndex.value = nearNote.index;
    playSelectedNoteSound();
    return;
  }

  pushHistory();

  const capo = Math.max(0, Math.min(7, parseInt(voiceToInsert.capo, 10) || 0));
  const fret = 0;
  const openMidi = TAB_OPEN_MIDIS[6 - stringIndex] + capo;
  const noteMidi = openMidi + fret;
  const pitchData = transposePitch(noteMidi, 0, false);
  const concertPitch = writtenToConcert(pitchData.step, pitchData.octave, pitchData.accidental, voiceToInsert.transpose || 0);

  const newNote = {
    step: concertPitch.step,
    octave: concertPitch.octave,
    pitch: concertPitch.pitch,
    accidental: concertPitch.accidental,
    duration: selectedDuration.value,
    isDotted: isDottedSelected.value,
    dots: isDottedSelected.value ? 1 : 0,
    isRest: false,
    tabString: stringIndex,
    tabFret: fret
  };

  let insertIndex = voiceToInsert.notes.length;
  const currentVoiceNotes = visualNotes.value.filter(n => n.voiceId === voiceToInsert.id);
  if (currentVoiceNotes.length > 0) {
    const nextNote = currentVoiceNotes.find(n => {
      if (isWrap) {
        if (n.systemIndex > systemIndex) return true;
        if (n.systemIndex === systemIndex && n.x > mouseX) return true;
        return false;
      } else {
        return n.x > mouseX;
      }
    });
    if (nextNote && typeof nextNote.index === 'number' && nextNote.index >= 0) {
      insertIndex = nextNote.index;
    }
  }

  voiceToInsert.notes.splice(insertIndex, 0, newNote);
  selectedNoteIndex.value = insertIndex;

  const inst = voiceToInsert.instrument || 'acoustic_grand_piano';
  playNoteSound(pitchData.step, pitchData.octave, pitchData.accidental, 0.25, inst);

  notifyChange();
}

function handleStaffClick(e) {
  if (props.readonly || props.embedded || !scoreScrollContainer.value) return;
  const rect = scoreScrollContainer.value.getBoundingClientRect();
  const mouseX = e.clientX - rect.left + scoreScrollContainer.value.scrollLeft;
  const mouseY = e.clientY - rect.top + scoreScrollContainer.value.scrollTop;

  const isWrap = selectedWrapMode.value === 'wrap';
  const sysH = systemHeight.value;
  const numSystems = systems.value.length || 1;
  const systemIndex = isWrap
    ? Math.max(0, Math.min(numSystems - 1, Math.floor(mouseY / sysH)))
    : 0;

  const sysYOffset = systemIndex * sysH;
  const localY = mouseY - sysYOffset;

  const curStaves = distinctStaves.value;
  let targetStaff = curStaves[0];
  let minStaffDist = 9999;

  curStaves.forEach(st => {
    const staffMidY = st.yOffset + getStaffLineY(2);
    const dist = Math.abs(localY - staffMidY);
    if (dist < minStaffDist) {
      minStaffDist = dist;
      targetStaff = st;
    }
  });

  const staffY = localY - targetStaff.yOffset;
  if (inTabZone(targetStaff, staffY)) {
    handleTabZoneClick(e, targetStaff, staffY, systemIndex, mouseX);
    return;
  }
  if (staffY < 15 || staffY > 125) return;

  const pitchData = yToPitch(staffY, targetStaff.clef);

  // Alt+click on the selected note's staff: stack a chord tone instead of inserting.
  // Checked before voice resolution so the tone lands on the selected note's voice.
  if (e.altKey && selectedNoteIndex.value !== null && activeVoice.value && activeVoice.value.notes
      && activeVoice.value.staffIndex === targetStaff.index) {
    const target = activeVoice.value.notes[selectedNoteIndex.value];
    if (target && !target.isRest) {
      const dupMain = target.step === pitchData.step && target.octave === pitchData.octave;
      const dupExtra = (target.pitches || []).some(p => p.step === pitchData.step && p.octave === pitchData.octave);
      if (!dupMain && !dupExtra) {
        pushHistory();
        if (!target.pitches) target.pitches = [];
        {
          const cp = writtenToConcert(pitchData.step, pitchData.octave, selectedAccidental.value, activeVoice.value.transpose || 0);
          target.pitches.push({ step: cp.step, octave: cp.octave, accidental: selectedAccidental.value || cp.accidental });
        }
        playNoteSound(pitchData.step, pitchData.octave, selectedAccidental.value, 0.2, activeVoice.value.instrument || 'acoustic_grand_piano');
        notifyChange();
      }
      return;
    }
  }

  // Ensure active voice matches target staff or use active voice
  let voiceToInsert = activeVoice.value;
  if (voiceToInsert.staffIndex !== targetStaff.index) {
    const matchedVoice = voices.value.find(v => v.staffIndex === targetStaff.index);
    if (matchedVoice) {
      voiceToInsert = matchedVoice;
      activeVoiceId.value = matchedVoice.id;
    }
  }

  // Drum-kit voices: map the clicked pitch to the nearest drum piece
  if (voiceToInsert.instrument === 'drum_kit' && !isRestSelected.value) {
    const piece = drumPieceForPitch(pitchData.step, pitchData.octave);
    insertDrumPiece(piece.id, voiceToInsert);
    return;
  }

  if (!isRestSelected.value) {
    playNoteSound(pitchData.step, pitchData.octave, selectedAccidental.value || keyAlterFor(pitchData.step, selectedKey.value), 0.2, voiceToInsert.instrument || 'acoustic_grand_piano');
  }

  pushHistory();

  const concertPitch = writtenToConcert(pitchData.step, pitchData.octave, selectedAccidental.value, voiceToInsert.transpose || 0);
  const newNote = {
    step: concertPitch.step,
    octave: concertPitch.octave,
    pitch: concertPitch.pitch,
    accidental: selectedAccidental.value || concertPitch.accidental,
    duration: selectedDuration.value,
    isDotted: isDottedSelected.value,
    dots: isDottedSelected.value ? 1 : 0,
    isRest: isRestSelected.value
  };

  // Find insertion index based on horizontal position (mouseX and systemIndex)
  let insertIndex = voiceToInsert.notes.length;
  const currentVoiceNotes = visualNotes.value.filter(n => n.voiceId === voiceToInsert.id);
  if (currentVoiceNotes.length > 0) {
    const nextNote = currentVoiceNotes.find(n => {
      if (isWrap) {
        if (n.systemIndex > systemIndex) return true;
        if (n.systemIndex === systemIndex && n.x > mouseX) return true;
        return false;
      } else {
        return n.x > mouseX;
      }
    });
    if (nextNote && typeof nextNote.index === 'number' && nextNote.index >= 0) {
      insertIndex = nextNote.index;
    }
  }

  voiceToInsert.notes.splice(insertIndex, 0, newNote);
  selectedNoteIndex.value = insertIndex;

  notifyChange();

  setTimeout(() => {
    if (scoreScrollContainer.value && insertIndex === voiceToInsert.notes.length) {
      const container = scoreScrollContainer.value;
      if (!isWrap && mouseX > container.scrollLeft + container.clientWidth - 50) {
        container.scrollLeft = container.scrollWidth;
      }
    }
  }, 50);
}

function handleNoteClick(noteItem) {
  if (props.readonly) return;
  activeVoiceId.value = noteItem.voiceId;
  selectedNoteIndex.value = noteItem.index;
  // Click right after a drag ends: keep selection, skip the replay sound
  if (suppressNoteClickSound) {
    suppressNoteClickSound = false;
    return;
  }
  const v = voices.value.find(vc => vc.id === noteItem.voiceId);
  const inst = (v && v.instrument) || 'acoustic_grand_piano';
  if (!noteItem.isRest) {
    const vc = voices.value.find(vc => vc.id === noteItem.voiceId);
    const raw = vc ? vc.notes[noteItem.index] : null;
    playRealizedNote({ ...(raw || noteItem), accidental: (raw || noteItem).accidental || keyAlterFor((raw || noteItem).step, selectedKey.value) }, 0.3, inst);
  }
}

// --- Select & drag notes directly on the staff (edit mode) -----------------
// Click selects (sidebar tools act on the selection, ring shows it).
// Drag vertically: repitch. Drag horizontally past a neighbor: reorder.
const noteDrag = ref(null);
let suppressNoteClickSound = false;

function isNoteSelected(noteItem) {
  return selectedNoteIndex.value !== null
    && noteItem.voiceId === activeVoiceId.value
    && noteItem.index === selectedNoteIndex.value;
}

function isDraggingNote(noteItem) {
  const d = noteDrag.value;
  return !!d && d.started && d.voiceId === noteItem.voiceId && d.index === noteItem.index;
}

function svgCoordsFromClient(e) {
  const rect = scoreScrollContainer.value.getBoundingClientRect();
  return {
    x: e.clientX - rect.left + scoreScrollContainer.value.scrollLeft,
    y: e.clientY - rect.top + scoreScrollContainer.value.scrollTop
  };
}

function onNotePointerDown(e, noteItem) {
  if (props.readonly || props.embedded || !e.isPrimary) return;
  if (e.button !== undefined && e.button !== 0) return;
  // Select on press for immediate feedback
  activeVoiceId.value = noteItem.voiceId;
  selectedNoteIndex.value = noteItem.index;
  const v = voices.value.find(vc => vc.id === noteItem.voiceId);
  const noteRef = v ? v.notes[noteItem.index] : null;
  if (!v || !noteRef || !scoreScrollContainer.value) return;
  const p = svgCoordsFromClient(e);
  noteDrag.value = {
    voiceId: noteItem.voiceId,
    noteRef,
    index: noteItem.index,
    startX: p.x,
    startY: p.y,
    startNoteY: noteItem.y,
    systemIndex: noteItem.systemIndex,
    started: false
  };
  ghostNote.value.visible = false;
  window.addEventListener('pointermove', onNotePointerMove);
  window.addEventListener('pointerup', onNotePointerUp, { once: true });
  window.addEventListener('pointercancel', onNotePointerUp, { once: true });
  // NOTE: no preventDefault here — it would cancel the click event (selection + sound).
  // It is applied once the drag threshold is crossed in onNotePointerMove.
}

function onNotePointerMove(e) {
  const d = noteDrag.value;
  if (!d || !e.isPrimary) return;
  const p = svgCoordsFromClient(e);
  const dx = p.x - d.startX;
  const dy = p.y - d.startY;
  if (!d.started) {
    if (Math.hypot(dx, dy) < 5) return;
    d.started = true;
    pushHistory();
    e.preventDefault();
  }
  const v = voices.value.find(vc => vc.id === d.voiceId);
  if (!v || !v.notes.includes(d.noteRef)) {
    endNoteDragListeners();
    noteDrag.value = null;
    return;
  }
  // Vertical: change pitch (rests have no pitch)
  if (!d.noteRef.isRest) {
    const minY = getStaffLineY(0) - 45;
    const maxY = getStaffLineY(4) + 45;
    const newY = Math.min(maxY, Math.max(minY, d.startNoteY + dy));
    const clef = v.clef || 'treble';
    const pd = yToPitch(newY, clef);
    const cp = writtenToConcert(pd.step, pd.octave, '', v.transpose || 0);
    if (cp.step !== d.noteRef.step || cp.octave !== d.noteRef.octave) {
      d.noteRef.step = cp.step;
      d.noteRef.octave = cp.octave;
      d.noteRef.pitch = `${cp.step}${cp.octave}`;
    }
  }
  // Horizontal: reorder past neighbor midpoints (same voice + system)
  reorderDraggedNote(v, d, p.x);
}

function reorderDraggedNote(v, d, pointerX) {
  const peers = visualNotes.value
    .filter(n => n.voiceId === d.voiceId && n.systemIndex === d.systemIndex)
    .sort((a, b) => a.x - b.x);
  const pos = peers.findIndex(n => n.index === d.index);
  if (pos < 0) return;
  let dir = 0;
  if (pos < peers.length - 1 && pointerX > (peers[pos].x + peers[pos + 1].x) / 2) dir = 1;
  else if (pos > 0 && pointerX < (peers[pos - 1].x + peers[pos].x) / 2) dir = -1;
  if (dir === 0) return;
  const arr = v.notes;
  const from = arr.indexOf(d.noteRef);
  const targetPos = peers[pos + dir].index;
  if (from < 0 || targetPos < 0 || targetPos >= arr.length) return;
  arr.splice(from, 1);
  arr.splice(targetPos, 0, d.noteRef);
  d.index = arr.indexOf(d.noteRef);
  selectedNoteIndex.value = d.index;
}

function endNoteDragListeners() {
  window.removeEventListener('pointermove', onNotePointerMove);
  window.removeEventListener('pointerup', onNotePointerUp);
  window.removeEventListener('pointercancel', onNotePointerUp);
}

function onNotePointerUp() {
  endNoteDragListeners();
  const d = noteDrag.value;
  noteDrag.value = null;
  if (!d || !d.started) return;
  // Suppress the click sound that follows the drag
  suppressNoteClickSound = true;
  setTimeout(() => { suppressNoteClickSound = false; }, 50);
  if (!d.noteRef.isRest) {
    const v = voices.value.find(vc => vc.id === d.voiceId);
    const inst = (v && v.instrument) || 'acoustic_grand_piano';
    playRealizedNote({ ...d.noteRef, accidental: d.noteRef.accidental || keyAlterFor(d.noteRef.step, selectedKey.value) }, 0.3, inst);
  }
  notifyChange();
}

function toggleArticulationActiveNote(artType) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.articulation === artType) {
    delete note.articulation;
  } else {
    note.articulation = artType;
  }
  notifyChange();
}

function toggleOrnamentActiveNote(ornType) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.ornament === ornType) {
    delete note.ornament;
  } else {
    note.ornament = ornType;
  }
  notifyChange();
}

function toggleGraceActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  note.grace = note.grace ? null : 'g';
  notifyChange();
}

function toggleGlissandoActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  note.glissando = !note.glissando;
  notifyChange();
}

function toggleCautionaryActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  note.cautionary = !note.cautionary;
  notifyChange();
}

function toggleHairpinActiveNote(hpType) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.hairpin === hpType) {
    delete note.hairpin;
  } else {
    note.hairpin = hpType;
  }
  notifyChange();
}

function toggleOttavaStartActiveNote(type) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.ottavaStart === type) {
    delete note.ottavaStart;
  } else {
    note.ottavaStart = type;
  }
  notifyChange();
}

function toggleOttavaEndActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.ottavaEnd) {
    delete note.ottavaEnd;
  } else {
    note.ottavaEnd = true;
  }
  notifyChange();
}

function toggleArpeggioActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  if (note.arpeggio) {
    delete note.arpeggio;
  } else {
    note.arpeggio = true;
  }
  notifyChange();
}

function cycleTremoloActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  const next = ((note.tremolo || 0) + 1) % 4;
  if (next === 0) {
    delete note.tremolo;
  } else {
    note.tremolo = next;
  }
  notifyChange();
}

function toggleBreathActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.breath) {
    delete note.breath;
  } else {
    note.breath = true;
  }
  notifyChange();
}

function togglePedalActiveNote(which) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  const key = which === 'end' ? 'pedalEnd' : 'pedalStart';
  if (note[key]) {
    delete note[key];
  } else {
    note[key] = true;
  }
  notifyChange();
}

const chordInputText = ref('');

function applyChordToSelectedNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  const val = chordInputText.value.trim();
  if (val) {
    note.chord = val;
  } else {
    delete note.chord;
  }
  chordInputText.value = '';
  notifyChange();
}


function setInlineClef(clefType) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes || activeVoice.value.notes.length === 0) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.inlineClef === clefType) {
    delete note.inlineClef;
  } else {
    note.inlineClef = clefType;
  }
  notifyChange();
}

function setNoteheadType(type) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes || activeVoice.value.notes.length === 0) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.notehead === type) {
    delete note.notehead;
  } else {
    note.notehead = type;
  }
  notifyChange();
}

// Arpeggio vertical extent (main head + cluster tones) and wavy path
function arpeggioExtent(noteItem) {
  let top = noteItem.y;
  let bottom = noteItem.y;
  (noteItem.pitches || []).forEach(p => {
    if (!p || !p.step) return;
    const y = pitchToY(p.step, p.octave || 4, noteItem.clef || 'treble');
    if (y < top) top = y;
    if (y > bottom) bottom = y;
  });
  return { top: top - 8, len: Math.max(20, bottom - top + 16) };
}

function arpeggioWavePath(extent) {
  const segs = Math.max(3, Math.round(extent.len / 7));
  let d = 'M 0 0 q -3 2 0 4';
  for (let i = 1; i < segs; i++) d += ' t 0 4';
  return d;
}

// Trill wavy extender: spans toward the next note (same voice + system),
// capped so it never collides; falls back to a fixed length at phrase ends.
function trillExtenderLength(noteItem) {
  const peers = visualNotes.value
    .filter(n => n.voiceId === noteItem.voiceId && n.systemIndex === noteItem.systemIndex && n.x > noteItem.x + 4)
    .sort((a, b) => a.x - b.x);
  const gap = peers.length > 0 ? peers[0].x - noteItem.x - 24 : 44;
  return Math.max(16, Math.min(64, gap));
}

function trillWavePath(len) {
  const segs = Math.max(2, Math.round(len / 8));
  let d = 'M 0 0 q 2 -3.2 4 0';
  for (let i = 1; i < segs; i++) d += ' t 4 0';
  return d;
}

// Same-staff multi-voice collisions: force opposite stems by voice order
// (respecting explicit stemDefault), share unison heads (hide duplicates),
// offset seconds. Call per system BEFORE beams are calculated.
function resolveStaffCollisions(notesArray) {
  const groups = new Map();
  notesArray.forEach(n => {
    if (n.isRest || n.isHidden) return;
    const key = `${n.staffYOffset}|${n.x}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(n);
  });
  const order = new Map();
  voices.value.forEach((v, i) => order.set(v.id, i));
  groups.forEach(g => {
    const byVoice = new Map();
    g.forEach(n => {
      if (!byVoice.has(n.voiceId)) byVoice.set(n.voiceId, []);
      byVoice.get(n.voiceId).push(n);
    });
    if (byVoice.size < 2) return;
    const sortedVoices = [...byVoice.keys()].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    sortedVoices.forEach((vid, vi) => {
      byVoice.get(vid).forEach(n => {
        if (n.stemDefault !== 'up' && n.stemDefault !== 'down') {
          n.stemUp = vi === 0;
        }
      });
    });
    const ref = byVoice.get(sortedVoices[0])[0];
    const refDia = getDiatonicIndex(ref.step, ref.octave);
    sortedVoices.slice(1).forEach(vid => {
      byVoice.get(vid).forEach(n => {
        const dia = getDiatonicIndex(n.step, n.octave);
        if (dia === refDia) {
          n.headHidden = true;
        } else if (Math.abs(dia - refDia) === 1) {
          n.headSide = dia > refDia ? 1 : -1;
        }
      });
    });
  });
}

// Cluster tones: extra pitches sharing the note's stem (note.pitches).
// Returns positioned heads with ledger lines; seconds are split left/right.
function clusterTones(noteItem) {
  const extras = noteItem.pitches;
  if (!extras || !extras.length || noteItem.isRest) return [];
  const clef = noteItem.clef || 'treble';
  const mainDia = getDiatonicIndex(noteItem.step, noteItem.octave);
  const items = extras
    .filter(p => p && p.step)
    .map(p => {
      const oct = p.octave || 4;
      const y = pitchToY(p.step, oct, clef);
      return { step: p.step, octave: oct, accidental: p.accidental || '', y, dia: getDiatonicIndex(p.step, oct) };
    })
    .sort((a, b) => b.dia - a.dia);
  const placed = [{ dia: mainDia, side: 0 }];
  let accSlot = (noteItem.accidental ? 1 : 0) + (noteItem.grace ? 1 : 0);
  return items.map(p => {
    let side = 0;
    const c0 = placed.find(q => q.side === 0 && Math.abs(q.dia - p.dia) === 1);
    if (c0) {
      side = noteItem.stemUp ? (p.dia > c0.dia ? 0 : -1) : (p.dia > c0.dia ? 1 : 0);
    } else {
      const c1 = placed.find(q => Math.abs(q.dia - p.dia) === 1);
      if (c1) side = c1.side === 0 ? (noteItem.stemUp ? -1 : 1) : -c1.side;
    }
    placed.push({ dia: p.dia, side });
    let accX = null;
    if (p.accidental) {
      accX = noteItem.x - 14 - 10 * accSlot;
      accSlot++;
    }
    return { ...p, xOff: side * 11, accX, ledgers: getLedgerLines(p.y) };
  });
}

function accSymbol(acc) {
  if (acc === '##') return '𝄪';
  if (acc === 'bb') return '𝄫';
  if (acc === '#') return '♯';
  if (acc === 'b') return '♭';
  return '♮';
}

// Melisma extender: horizontal line from a hyphenated syllable toward the
// next lyric note (same voice + system + verse); fixed length at phrase ends.
function lyricExtender(noteItem, verse) {
  const hasLyric = verse === 2 ? noteItem.lyric2 : noteItem.lyric;
  const hasMel = verse === 2 ? noteItem.lyric2Melisma : noteItem.lyricMelisma;
  if (!hasLyric || !hasMel || noteItem.isRest) return null;
  const baseY = getStaffLineY(4) + (noteItem.y >= getStaffLineY(2) ? 32 : 24) + (verse === 2 ? 15 : 0) - 4;
  const key = verse === 2 ? 'lyric2' : 'lyric';
  const peers = visualNotes.value
    .filter(n => n.voiceId === noteItem.voiceId && n.systemIndex === noteItem.systemIndex && n.x > noteItem.x + 4 && n[key])
    .sort((a, b) => a.x - b.x);
  const x2 = peers.length > 0 ? Math.min(peers[0].x - 12, noteItem.x + 76) : noteItem.x + 40;
  if (x2 <= noteItem.x + 14) return null;
  return { x1: noteItem.x + 14, x2, y: baseY };
}

// Multi-measure rest on the selected rest (prompt for count; empty/1 removes).
function editMultiRestActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || !note.isRest) return;
  const current = note.multiRest > 1 ? note.multiRest : 2;
  const val = prompt(t('rests.multiPrompt'), String(current));
  if (val === null) return;
  pushHistory();
  const n = parseInt(val, 10);
  if (n >= 2 && n <= 32) {
    note.multiRest = n;
  } else {
    delete note.multiRest;
  }
  notifyChange();
}

function printScore() {
  buildPrintPages();
  document.body.classList.add('printing-score');
  setTimeout(() => window.print(), 120);
}

function cleanupPrint() {
  document.body.classList.remove('printing-score');
  if (printPagesRef.value) printPagesRef.value.innerHTML = '';
}

// Paginated print: slice the score SVG into whole-system pages via viewBox.
// Each page clones the live SVG (real nodes, no shadow-DOM quirks).
const printPagesRef = ref(null);
const PRINT_PAGE_HEIGHT = 900; // svg units per printed page

function buildPrintPages() {
  const c = printPagesRef.value;
  if (!c) return;
  c.innerHTML = '';
  const src = scoreScrollContainer.value?.querySelector('svg');
  const syss = systems.value;
  if (!src || !syss.length) return;
  const W = parseFloat(src.getAttribute('width')) || calculatedSvgWidth.value || 800;
  const totalH = calculatedSvgHeight.value || 600;
  // Greedy whole-system packing
  const pages = [];
  let cur = [];
  let curTop = syss[0].yOffset || 0;
  let curEnd = curTop;
  syss.forEach(sys => {
    const sysTop = sys.yOffset || 0;
    const sysEnd = sysTop + (systemHeight.value || 160);
    if (cur.length > 0 && sysEnd - curTop > PRINT_PAGE_HEIGHT) {
      pages.push({ systems: cur, top: curTop, height: curEnd - curTop });
      cur = [];
      curTop = sysTop;
    }
    cur.push(sys);
    curEnd = Math.max(curEnd, sysEnd);
  });
  if (cur.length > 0) pages.push({ systems: cur, top: curTop, height: Math.max(curEnd - curTop, totalH - curTop) });
  const NS = 'http://www.w3.org/2000/svg';
  pages.forEach((pg, pi) => {
    const wrap = document.createElement('div');
    wrap.className = 'ms-print-page';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', `0 ${pg.top} ${W} ${pg.height}`);
    svg.setAttribute('xmlns', NS);
    svg.setAttribute('class', 'ms-print-svg');
    const clone = src.cloneNode(true);
    clone.setAttribute('width', String(W));
    clone.setAttribute('height', String(totalH));
    clone.removeAttribute('id');
    svg.appendChild(clone);
    wrap.appendChild(svg);
    const foot = document.createElement('div');
    foot.className = 'ms-print-footer';
    foot.textContent = t('print.pageOf', { n: pi + 1, total: pages.length });
    wrap.appendChild(foot);
    c.appendChild(wrap);
  });
}

function handleBeforePrint() {
  buildPrintPages();
  document.body.classList.add('printing-score');
}

// Transpose the whole score by semitones (spelling follows the key signature;
// the key signature itself shifts within vocabulary when possible)
function transposeScore(semitones) {
  if (props.readonly || !semitones) return;
  pushHistory();
  const useFlats = ['F', 'Bb', 'Dm', 'Gm'].includes(selectedKey.value);
  const shiftNote = (n) => {
    if (!n || n.isRest || !n.step) return;
    const t = transposePitch(getMidiNumber(n.step, n.octave || 4, n.accidental || ''), semitones, useFlats);
    n.step = t.step;
    n.octave = t.octave;
    n.accidental = t.accidental;
    n.pitch = `${t.step}${t.octave}`;
  };
  voices.value.forEach(v => {
    (v.notes || []).forEach(n => {
      shiftNote(n);
      (n.pitches || []).forEach(p => shiftNote(p));
    });
  });
  const nk = transposeKey(selectedKey.value, semitones);
  if (nk) selectedKey.value = nk;
  keyChanges.value.forEach(e => {
    const ek = transposeKey(e.key, semitones);
    if (ek) e.key = ek;
  });
  selectedNoteIndex.value = null;
  notifyChange();
}

// Drum pieces for the palette (labels via drums.<id>)
const drumPieces = computed(() => DRUM_PIECE_LIST);

// Palette pick (notes & rests sections): if a note is explicitly selected,
// replace/convert it in place; otherwise set the default for the next insert.
// Rest -> note reuses the previous pitch when present, else middle-line pitch
// (drag vertically to repitch afterwards).
function chooseDuration(duration, rest) {
  selectedDuration.value = duration;
  isRestSelected.value = rest;
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  if (selectedNoteIndex.value === null) return;
  const idx = selectedNoteIndex.value;
  const note = activeVoice.value.notes[idx];
  if (!note) return;
  if (note.duration === duration && !!note.isRest === rest) return;
  pushHistory();
  note.duration = duration;
  // A duration pick resets any multi-measure span
  delete note.multiRest;
  if (rest) {
    note.isRest = true;
  } else {
    note.isRest = false;
    if (!note.step || !note.octave) {
      const clef = activeVoice.value.clef || 'treble';
      const pd = yToPitch(getStaffLineY(2), clef);
      const cp = writtenToConcert(pd.step, pd.octave, '', activeVoice.value.transpose || 0);
      note.step = cp.step;
      note.octave = cp.octave;
      note.pitch = `${cp.step}${cp.octave}`;
      note.accidental = selectedAccidental.value || '';
    }
  }
  notifyChange();
}

function setChordActiveNote(ch) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (ch) {
    note.chord = ch;
  } else {
    delete note.chord;
  }
  notifyChange();
}

// Rehearsal mark on the selected note (boxed letter). Prompt suggests the
// next letter based on existing marks; empty input removes the mark.
function editRehearsalActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  const used = new Set();
  voices.value.forEach(v => (v.notes || []).forEach(n => { if (n.rehearsal) used.add(n.rehearsal); }));
  let suggestion = 'A';
  for (let c = 65; c <= 90; c++) {
    if (!used.has(String.fromCharCode(c))) {
      suggestion = String.fromCharCode(c);
      break;
    }
  }
  const val = prompt(t('structure.rehearsalPrompt'), note.rehearsal || suggestion);
  if (val === null) return;
  pushHistory();
  if (val.trim()) {
    note.rehearsal = val.trim().substring(0, 8);
  } else {
    delete note.rehearsal;
  }
  notifyChange();
}

function toggleNavigationActiveNote(navType) {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.navigation === navType) {
    delete note.navigation;
  } else {
    note.navigation = navType;
  }
  notifyChange();
}

function toggleTupletActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note) return;
  pushHistory();
  if (note.tuplet) {
    delete note.tuplet;
    delete note.tupletStart;
    delete note.tupletEnd;
  } else {
    note.tuplet = 3;
    note.tupletStart = true;
  }
  notifyChange();
}

function toggleTieActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  note.hasTie = !note.hasTie;
  notifyChange();
}

function toggleSlurActiveNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return;
  pushHistory();
  if (!note.slurStart && !note.slurEnd) {
    note.slurStart = true;
  } else if (note.slurStart && !note.slurEnd) {
    note.slurStart = false;
    note.slurEnd = true;
  } else {
    note.slurStart = false;
    note.slurEnd = false;
  }
  notifyChange();
}

function getSelectedOrLastNote() {
  if (props.readonly || !activeVoice.value || !activeVoice.value.notes) return null;
  const targetIdx = selectedNoteIndex.value !== null ? selectedNoteIndex.value : activeVoice.value.notes.length - 1;
  if (targetIdx < 0 || targetIdx >= activeVoice.value.notes.length) return null;
  const note = activeVoice.value.notes[targetIdx];
  if (!note || note.isRest) return null;
  return note;
}

function activeNoteHasTabSlide(dir) {
  const n = getSelectedOrLastNote();
  if (!n) return false;
  return dir ? n.tabSlide === dir : !!n.tabSlide;
}

function activeNoteHasTabBend(amt) {
  const n = getSelectedOrLastNote();
  if (!n) return false;
  return amt ? n.tabBend === amt : !!n.tabBend;
}

function activeNoteHasTabHammer() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabHammer);
}

function activeNoteHasTabPull() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabPull);
}

function activeNoteHasTabVibrato() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabVibrato);
}

function activeNoteHasTabHarmonic() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabHarmonic);
}

function activeNoteHasTabDead() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabDead);
}

function activeNoteHasTabGhost() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabGhost);
}

function activeNoteHasTabPalmMute() {
  const n = getSelectedOrLastNote();
  return !!(n && n.tabPalmMute);
}

function toggleTabSlide(direction = 'up') {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  if (note.tabSlide === direction) {
    delete note.tabSlide;
  } else {
    note.tabSlide = direction;
  }
  notifyChange();
}

function toggleTabBend(amount = 'full') {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  if (note.tabBend === amount) {
    delete note.tabBend;
  } else {
    note.tabBend = amount;
  }
  notifyChange();
}

function toggleTabHammerOrPull() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  if (note.tabHammer || note.tabPull) {
    delete note.tabHammer;
    delete note.tabPull;
  } else {
    note.tabHammer = true;
  }
  notifyChange();
}

function toggleTabVibrato() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  note.tabVibrato = !note.tabVibrato;
  if (!note.tabVibrato) delete note.tabVibrato;
  notifyChange();
}

function toggleTabHarmonic() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  note.tabHarmonic = !note.tabHarmonic;
  if (!note.tabHarmonic) delete note.tabHarmonic;
  notifyChange();
}

function toggleTabDead() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  note.tabDead = !note.tabDead;
  if (!note.tabDead) delete note.tabDead;
  if (note.tabDead) delete note.tabGhost;
  notifyChange();
}

function toggleTabGhost() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  note.tabGhost = !note.tabGhost;
  if (!note.tabGhost) delete note.tabGhost;
  if (note.tabGhost) delete note.tabDead;
  notifyChange();
}

function toggleTabPalmMute() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  note.tabPalmMute = !note.tabPalmMute;
  if (!note.tabPalmMute) delete note.tabPalmMute;
  notifyChange();
}

function clearTabArticulations() {
  const note = getSelectedOrLastNote();
  if (!note) return;
  pushHistory();
  delete note.tabSlide;
  delete note.tabBend;
  delete note.tabHammer;
  delete note.tabPull;
  delete note.tabVibrato;
  delete note.tabHarmonic;
  delete note.tabDead;
  delete note.tabGhost;
  delete note.tabPalmMute;
  notifyChange();
}

function removeNoteItem(noteItem, e) {
  if (props.readonly) return;
  const v = voices.value.find(vc => vc.id === noteItem.voiceId);
  if (!v || !v.notes[noteItem.index]) return;
  // Alt+right-click: remove the closest stacked chord tone instead of the note
  if (e && e.altKey) {
    const note = v.notes[noteItem.index];
    if (note && note.pitches && note.pitches.length > 0 && scoreScrollContainer.value) {
      const rect = scoreScrollContainer.value.getBoundingClientRect();
      const staffY = e.clientY - rect.top + scoreScrollContainer.value.scrollTop - noteItem.totalYOffset;
      const clef = noteItem.clef || v.clef || 'treble';
      let best = -1;
      let bestDist = Infinity;
      note.pitches.forEach((p, i) => {
        if (!p || !p.step) return;
        const py = pitchToY(p.step, p.octave || 4, clef);
        const dist = Math.abs(py - staffY);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      if (best >= 0) {
        pushHistory();
        note.pitches.splice(best, 1);
        if (note.pitches.length === 0) delete note.pitches;
        notifyChange();
      }
    }
    return;
  }
  pushHistory();
  v.notes.splice(noteItem.index, 1);
  if (selectedNoteIndex.value === noteItem.index) {
    selectedNoteIndex.value = null;
  }
  notifyChange();
}

function clearScore() {
  if (totalNotesCount.value === 0) return;
  pushHistory();
  voices.value.forEach(v => { v.notes = []; });
  notifyChange();
}

// Beats per measure
const beatsPerMeasure = computed(() => {
  const [beatsStr, beatTypeStr] = selectedTimeSignature.value.split('/');
  const beats = parseInt(beatsStr, 10) || 4;
  const beatType = parseInt(beatTypeStr, 10) || 4;
  return (beats * 4) / beatType;
});

// Beams Helpers
function assignBeamGroup(groupNotes, groupId) {
  const upCount = groupNotes.filter(n => n.stemUp).length;
  const commonStemUp = upCount >= groupNotes.length / 2;

  const firstNote = groupNotes[0];
  const lastNote = groupNotes[groupNotes.length - 1];

  let yDiff = lastNote.y - firstNote.y;
  const maxSlope = 12;
  if (Math.abs(yDiff) > maxSlope) {
    yDiff = Math.sign(yDiff) * maxSlope;
  }

  const baseFirstTipY = commonStemUp
    ? Math.min(...groupNotes.map(n => n.y)) - 28
    : Math.max(...groupNotes.map(n => n.y)) + 28;

  const firstStemTipY = baseFirstTipY;
  const lastStemTipY = baseFirstTipY + yDiff;

  groupNotes.forEach((noteItem) => {
    noteItem.isBeamed = true;
    noteItem.beamGroupId = groupId;
    noteItem.stemUp = commonStemUp;

    const ratio = (noteItem.x - firstNote.x) / Math.max(1, (lastNote.x - firstNote.x));
    noteItem.stemTipX = commonStemUp ? noteItem.x + 4.8 : noteItem.x - 4.8;
    noteItem.stemTipY = firstStemTipY + ratio * (lastStemTipY - firstStemTipY);
  });
}

function calculateSystemBeams(systemNotes) {
  let currentGroup = [];
  let groupCounter = 0;

  systemNotes.forEach((noteItem) => {
    const isBeamable = !noteItem.isRest && (noteItem.duration === '8' || noteItem.duration === '16' || noteItem.duration === '32');

    if (isBeamable) {
      currentGroup.push(noteItem);
    } else {
      if (currentGroup.length > 1) {
        assignBeamGroup(currentGroup, groupCounter++);
      }
      currentGroup = [];
    }
  });

  if (currentGroup.length > 1) {
    assignBeamGroup(currentGroup, groupCounter++);
  }

  systemNotes.forEach((noteItem) => {
    if (!noteItem.isBeamed) {
      if (noteItem.stemUp) {
        noteItem.stemTipX = noteItem.x + 4.8;
        noteItem.stemTipY = noteItem.y - 32;
      } else {
        noteItem.stemTipX = noteItem.x - 4.8;
        noteItem.stemTipY = noteItem.y + 32;
      }
    }
  });
}

function buildBeamsForNotes(systemNotes, systemIndex) {
  const groupsMap = new Map();
  systemNotes.forEach(note => {
    if (note.isBeamed) {
      if (!groupsMap.has(note.beamGroupId)) {
        groupsMap.set(note.beamGroupId, []);
      }
      groupsMap.get(note.beamGroupId).push(note);
    }
  });

  const beams = [];
  groupsMap.forEach((notesInGroup, groupId) => {
    if (notesInGroup.length < 2) return;
    const first = notesInGroup[0];
    const last = notesInGroup[notesInGroup.length - 1];
    const stemUp = first.stemUp;
    const beamThickness = 3.5;
    const beamOffset = stemUp ? beamThickness : -beamThickness;
    const color = isMultiVoice.value ? getVoiceColor(first.voiceId) : null;

    // Primary Beam (8th, 16th, 32nd)
    beams.push({
      id: `sys-${systemIndex}-primary-${first.voiceId}-${groupId}`,
      x1: first.stemTipX,
      y1: first.stemTipY + first.staffYOffset,
      x2: last.stemTipX,
      y2: last.stemTipY + last.staffYOffset,
      thickness: beamThickness,
      color,
      isDouble: false
    });

    // Secondary Beam (16th and 32nd)
    let sub16 = [];
    notesInGroup.forEach((n, idx) => {
      if (n.duration === '16' || n.duration === '32') {
        sub16.push(n);
      } else {
        if (sub16.length >= 2) {
          beams.push(createSecondaryBeam(sub16, `sys-${systemIndex}-${first.voiceId}-${groupId}-16`, beamOffset, beamThickness, color, 1));
        } else if (sub16.length === 1) {
          beams.push(createBeamHook(sub16[0], idx > 0 ? -1 : 1, beamOffset, beamThickness, color, 1));
        }
        sub16 = [];
      }
    });
    if (sub16.length >= 2) {
      beams.push(createSecondaryBeam(sub16, `sys-${systemIndex}-${first.voiceId}-${groupId}-16`, beamOffset, beamThickness, color, 1));
    } else if (sub16.length === 1) {
      beams.push(createBeamHook(sub16[0], -1, beamOffset, beamThickness, color, 1));
    }

    // Tertiary Beam (32nd)
    let sub32 = [];
    notesInGroup.forEach((n, idx) => {
      if (n.duration === '32') {
        sub32.push(n);
      } else {
        if (sub32.length >= 2) {
          beams.push(createSecondaryBeam(sub32, `sys-${systemIndex}-${first.voiceId}-${groupId}-32`, beamOffset, beamThickness, color, 2));
        } else if (sub32.length === 1) {
          beams.push(createBeamHook(sub32[0], idx > 0 ? -1 : 1, beamOffset, beamThickness, color, 2));
        }
        sub32 = [];
      }
    });
    if (sub32.length >= 2) {
      beams.push(createSecondaryBeam(sub32, `sys-${systemIndex}-${first.voiceId}-${groupId}-32`, beamOffset, beamThickness, color, 2));
    } else if (sub32.length === 1) {
      beams.push(createBeamHook(sub32[0], -1, beamOffset, beamThickness, color, 2));
    }
  });

  return beams;
}

function createSecondaryBeam(notesSubgroup, groupId, offset, thickness, color, tier = 1) {
  const f = notesSubgroup[0];
  const l = notesSubgroup[notesSubgroup.length - 1];
  const yShift = offset * 1.8 * tier;
  return {
    id: `secondary-${groupId}-${f.index}`,
    x1: f.stemTipX,
    y1: f.stemTipY + f.staffYOffset + yShift,
    x2: l.stemTipX,
    y2: l.stemTipY + l.staffYOffset + yShift,
    thickness: thickness,
    color,
    isDouble: true
  };
}

function createBeamHook(note, direction, offset, thickness, color, tier = 1) {
  const yShift = offset * 1.8 * tier;
  const hookLen = 10 * direction;
  return {
    id: `hook-${note.index}`,
    x1: note.stemTipX,
    y1: note.stemTipY + note.staffYOffset + yShift,
    x2: note.stemTipX + hookLen,
    y2: note.stemTipY + note.staffYOffset + yShift,
    thickness: thickness,
    color,
    isDouble: true
  };
}

function buildCurvesForNotes(systemNotes, systemIndex) {
  const curves = [];
  const voiceGroups = new Map();

  systemNotes.forEach(n => {
    if (!voiceGroups.has(n.voiceId)) voiceGroups.set(n.voiceId, []);
    voiceGroups.get(n.voiceId).push(n);
  });

  voiceGroups.forEach((vNotes, voiceId) => {
    // 1. TIES: Connect note with hasTie to the immediate next note in the same voice
    for (let i = 0; i < vNotes.length - 1; i++) {
      const cur = vNotes[i];
      const next = vNotes[i + 1];
      if (cur.hasTie && !cur.isRest && !next.isRest) {
        // Curve placement: opposite to stem or below noteheads if stemUp
        const stemUp = cur.stemUp;
        // Direction: if stemUp -> arc bows downwards (under notehead), if stemDown -> arc bows upwards (above notehead)
        const bowDir = stemUp ? 1 : -1;
        const startX = cur.x + 4;
        const startY = cur.y + cur.staffYOffset + (bowDir * 9);
        const endX = next.x - 4;
        const endY = next.y + next.staffYOffset + (bowDir * 9);
        const midX = (startX + endX) / 2;
        const dist = Math.abs(endX - startX);
        const arcHeight = Math.min(18, Math.max(10, dist * 0.18)) * bowDir;
        const ctrlY = (startY + endY) / 2 + arcHeight;

        curves.push({
          id: `tie-sys-${systemIndex}-${voiceId}-${cur.index}`,
          type: 'tie',
          d: `M ${startX} ${startY} Q ${midX} ${ctrlY} ${endX} ${endY}`,
          color: isMultiVoice.value ? getVoiceColor(voiceId) : null
        });
      }
    }

    // 2. SLURS: Connect note with slurStart to the subsequent note with slurEnd (or slur span)
    let openSlurNote = null;
    for (let i = 0; i < vNotes.length; i++) {
      const cur = vNotes[i];
      if (cur.slurStart && !cur.isRest) {
        openSlurNote = cur;
      }
      if (openSlurNote && (cur.slurEnd || (cur !== openSlurNote && i === vNotes.length - 1)) && !cur.isRest) {
        const startN = openSlurNote;
        const endN = cur;
        if (startN !== endN) {
          const stemUp = startN.stemUp;
          const bowDir = stemUp ? -1 : 1; // Slurs typically arc over stem tips or under noteheads
          const startX = startN.x;
          const startY = startN.y + startN.staffYOffset + (bowDir * (stemUp ? 26 : 14));
          const endX = endN.x;
          const endY = endN.y + endN.staffYOffset + (bowDir * (stemUp ? 26 : 14));
          const midX = (startX + endX) / 2;
          const dist = Math.abs(endX - startX);
          const arcHeight = Math.min(24, Math.max(12, dist * 0.14)) * bowDir;
          const ctrlY = ((startY + endY) / 2) + arcHeight;

          curves.push({
            id: `slur-sys-${systemIndex}-${voiceId}-${startN.index}`,
            type: 'slur',
            d: `M ${startX} ${startY} Q ${midX} ${ctrlY} ${endX} ${endY}`,
            color: isMultiVoice.value ? getVoiceColor(voiceId) : null
          });
        }
        openSlurNote = null;
      }
    }
  });

  return curves;
}

function buildGlissandiForNotes(systemNotes, systemIndex) {
  const glissandi = [];
  const voiceGroups = new Map();

  systemNotes.forEach(n => {
    if (!voiceGroups.has(n.voiceId)) voiceGroups.set(n.voiceId, []);
    voiceGroups.get(n.voiceId).push(n);
  });

  voiceGroups.forEach((vNotes, voiceId) => {
    for (let i = 0; i < vNotes.length - 1; i++) {
      const cur = vNotes[i];
      const next = vNotes[i + 1];
      if (cur.glissando && !cur.isRest && !next.isRest) {
        const startX = cur.x + 8;
        const startY = cur.y + (cur.staffYOffset || 0);
        const endX = next.x - 8;
        const endY = next.y + (next.staffYOffset || 0);
        const angle = Math.atan2(endY - startY, endX - startX) * (180 / Math.PI);

        glissandi.push({
          id: `gliss-sys-${systemIndex}-${voiceId}-${cur.index}`,
          x1: startX,
          y1: startY,
          x2: endX,
          y2: endY,
          angle
        });
      }
    }
  });

  return glissandi;
}

function buildTupletsForNotes(systemNotes, systemIndex) {
  const tuplets = [];
  const voiceGroups = new Map();

  systemNotes.forEach(n => {
    if (!voiceGroups.has(n.voiceId)) voiceGroups.set(n.voiceId, []);
    voiceGroups.get(n.voiceId).push(n);
  });

  voiceGroups.forEach((vNotes, voiceId) => {
    let currentTupletNotes = [];
    vNotes.forEach(n => {
      if (n.tuplet || n.tupletStart) {
        currentTupletNotes.push(n);
        if (n.tupletEnd || currentTupletNotes.length === (n.tuplet || 3)) {
          if (currentTupletNotes.length >= 2) {
            const first = currentTupletNotes[0];
            const last = currentTupletNotes[currentTupletNotes.length - 1];
            const stemUp = first.stemUp;
            const staffYOff = first.staffYOffset || 0;
            const y = stemUp
              ? Math.min(...currentTupletNotes.map(tn => tn.y)) + staffYOff - 24
              : Math.max(...currentTupletNotes.map(tn => tn.y)) + staffYOff + 24;

            tuplets.push({
              id: `tup-sys-${systemIndex}-${voiceId}-${first.index}`,
              x1: first.x - 6,
              x2: last.x + 6,
              midX: (first.x + last.x) / 2,
              y1: y,
              number: first.tuplet || 3,
              stemUp
            });
          }
          currentTupletNotes = [];
        }
      } else {
        currentTupletNotes = [];
      }
    });
  });

  return tuplets;
}

function buildHairpinsForNotes(systemNotes, systemIndex) {
  const hairpins = [];
  const voiceGroups = new Map();

  systemNotes.forEach(n => {
    if (!voiceGroups.has(n.voiceId)) voiceGroups.set(n.voiceId, []);
    voiceGroups.get(n.voiceId).push(n);
  });

  voiceGroups.forEach((vNotes, voiceId) => {
    let openHairpin = null;
    vNotes.forEach(n => {
      if (n.hairpin === 'crescendo-start' || n.hairpin === 'diminuendo-start') {
        openHairpin = {
          type: n.hairpin === 'crescendo-start' ? 'crescendo' : 'diminuendo',
          startNote: n
        };
      } else if (openHairpin && (n.hairpin === 'crescendo-end' || n.hairpin === 'diminuendo-end' || n.hairpin === 'crescendo-start' || n.hairpin === 'diminuendo-start')) {
        const startN = openHairpin.startNote;
        const endN = n;
        if (startN !== endN) {
          const staffYOff = startN.staffYOffset || 0;
          const y = getStaffLineY(4) + staffYOff + (startN.lyric ? 42 : 28);
          hairpins.push({
            id: `hp-sys-${systemIndex}-${voiceId}-${startN.index}`,
            type: openHairpin.type,
            x1: startN.x,
            x2: endN.x,
            y
          });
        }
        if (n.hairpin === 'crescendo-start' || n.hairpin === 'diminuendo-start') {
          openHairpin = {
            type: n.hairpin === 'crescendo-start' ? 'crescendo' : 'diminuendo',
            startNote: n
          };
        } else {
          openHairpin = null;
        }
      }
    });
  });

  return hairpins;
}

// Ottava spans (8va / 8vb): dashed bracket above the staff from a start note
// to an end note (same voice, same system). Unclosed spans run to system end.
function buildOttavaSpansForNotes(systemNotes, systemIndex) {
  const spans = [];
  const voiceGroups = new Map();
  systemNotes.forEach(n => {
    if (!voiceGroups.has(n.voiceId)) voiceGroups.set(n.voiceId, []);
    voiceGroups.get(n.voiceId).push(n);
  });
  voiceGroups.forEach((vNotes, voiceId) => {
    let openSpan = null;
    const closeSpan = (endN) => {
      if (openSpan && openSpan.startNote !== endN) {
        const staffYOff = openSpan.startNote.staffYOffset || 0;
        spans.push({
          id: `ot-sys-${systemIndex}-${voiceId}-${openSpan.startNote.index}`,
          type: openSpan.type,
          x1: openSpan.startNote.x,
          x2: Math.max(endN.x + 10, openSpan.startNote.x + 24),
          y: getStaffLineY(0) + staffYOff - 26
        });
      }
      openSpan = null;
    };
    vNotes.forEach(n => {
      if (n.ottavaStart === '8va' || n.ottavaStart === '8vb') {
        if (openSpan) closeSpan(n);
        openSpan = { type: n.ottavaStart, startNote: n };
      } else if (openSpan && n.ottavaEnd) {
        closeSpan(n);
      }
    });
    if (openSpan) {
      const lastN = vNotes[vNotes.length - 1];
      closeSpan({ ...lastN, x: lastN.x + 24 });
    }
  });
  return spans;
}

// Transposing instruments: model stores concert pitch; display transposes.
// voice.transpose = sounding offset (written + transpose = sounding).
// E.g. Bb trumpet (-2): written D sounds C.
function useFlatsForTranspose() {
  return ['F', 'Bb', 'Dm', 'Gm'].includes(selectedKey.value);
}

function transposeNoteForDisplay(n, t) {
  if (!t || !n || n.isRest || !n.step) return n;
  const w = transposePitch(getMidiNumber(n.step, n.octave || 4, n.accidental || ''), -t, useFlatsForTranspose());
  const out = { ...n, step: w.step, octave: w.octave, accidental: w.accidental, pitch: `${w.step}${w.octave}` };
  if (n.pitches) {
    out.pitches = n.pitches.map(p => {
      if (!p || !p.step) return p;
      const pw = transposePitch(getMidiNumber(p.step, p.octave || 4, p.accidental || ''), -t, useFlatsForTranspose());
      return { ...p, step: pw.step, octave: pw.octave, accidental: pw.accidental };
    });
  }
  return out;
}

// Written (clicked/dragged) pitch -> concert pitch for a transposed voice
function writtenToConcert(step, octave, accidental, t) {
  if (!t) return { step, octave, accidental: accidental || '', pitch: `${step}${octave}` };
  const w = transposePitch(getMidiNumber(step, octave, accidental || ''), t, useFlatsForTranspose());
  return { step: w.step, octave: w.octave, accidental: w.accidental, pitch: `${w.step}${w.octave}` };
}

// Key name as displayed (transposed in single-voice part mode)
function dispKey(concertKey) {
  if (!partVoiceId.value) return concertKey;
  const v = voices.value.find(vc => vc.id === partVoiceId.value);
  if (!v || !v.transpose) return concertKey;
  return transposeKey(concertKey, -v.transpose) || concertKey;
}

// Compute Systems (Grand Staff Rows) with barlines and notes
const systems = computed(() => {
  const isWrap = selectedWrapMode.value === 'wrap';
  const staves = distinctStaves.value;
  const sysH = systemHeight.value;

  // Visible voices list (or fallback to all if all are hidden).
  // Transposing voices render transposed copies; originalIndex still
  // addresses the raw concert-pitch notes for editing.
  const visibleVoicesList = includedVoices(voices.value).map(v => (
    v.transpose ? { ...v, notes: (v.notes || []).map(n => transposeNoteForDisplay(n, v.transpose)) } : v
  ));

  // 1. Group notes into measures per voice
  const voiceMeasuresMap = new Map();
  let maxMeasuresCount = 1;

  visibleVoicesList.forEach(v => {
    const vMeasures = [];
    let curMeasure = [];
    let curBeats = 0;

    v.notes.forEach((note, origIdx) => {
      let qBeats = durationToQuarterBeats(note.duration, note.isDotted, 1, note.dots || 0);
      // Multi-measure rest: spans N full measures from the current one
      const mrN = (note.isRest && note.multiRest && note.multiRest > 1) ? Math.floor(note.multiRest) : 0;
      if (mrN > 0) qBeats = mrN * measureBeats(vMeasures.length);
      if (curBeats > 0 && (curBeats + qBeats > measureBeats(vMeasures.length) + 0.0001)) {
        vMeasures.push(curMeasure);
        curMeasure = [];
        curBeats = 0;
      }
      curMeasure.push({
        ...note,
        originalIndex: origIdx,
        voiceId: v.id,
        voiceName: v.name,
        staffIndex: v.staffIndex,
        stemDefault: v.stemDefault,
        clef: v.clef,
        startBeatInMeasure: curBeats,
        qBeats
      });
      curBeats += qBeats;
      if (curBeats >= measureBeats(vMeasures.length) - 0.0001) {
        vMeasures.push(curMeasure);
        curMeasure = [];
        curBeats = 0;
      }
      // Pad the remaining spanned measures so other voices stay aligned
      if (mrN > 1) {
        for (let k = 1; k < mrN; k++) vMeasures.push([]);
      }
    });
    if (curMeasure.length > 0) {
      vMeasures.push(curMeasure);
    }
    if (vMeasures.length === 0) vMeasures.push([]);

    voiceMeasuresMap.set(v.id, vMeasures);
    if (vMeasures.length > maxMeasuresCount) maxMeasuresCount = vMeasures.length;
  });

  // Compute repeat metadata and volta brackets for each measure index
  const measureMetadataMap = new Map();
  for (let m = 0; m < maxMeasuresCount; m++) {
    let startRepeat = false;
    let endRepeat = false;
    let volta = null;

    visibleVoicesList.forEach(v => {
      const vMeasures = voiceMeasuresMap.get(v.id) || [];
      const mNotes = vMeasures[m] || [];
      if (mNotes.some(n => n.startRepeat)) startRepeat = true;
      if (mNotes.some(n => n.endRepeat)) endRepeat = true;
      const vVolta = mNotes.find(n => n.volta !== null && n.volta !== undefined);
      if (vVolta) volta = vVolta.volta;
    });

    measureMetadataMap.set(m, {
      startRepeat,
      endRepeat,
      volta
    });
  }

  const resultSystems = [];

  if (!isWrap) {
    // Single Continuous Line Mode
    const measureWidth = 180;
    const sysWidth = Math.max(700, START_X_FIRST + (maxMeasuresCount * measureWidth) + 80);
    const barlines = [];
    const voltas = [];
    const allSystemNotes = [];

    for (let m = 0; m < maxMeasuresCount; m++) {
      const mStartX = START_X_FIRST + 20 + (m * measureWidth);
      const mBarX = mStartX + measureWidth;
      const meta = measureMetadataMap.get(m) || {};

      // Start repeat on measure start
      if (meta.startRepeat && m > 0) {
        // Tag previous barline or add start repeat
        const prevBar = barlines[barlines.length - 1];
        if (prevBar) {
          prevBar.type = prevBar.type === 'end-repeat' ? 'double-repeat' : 'start-repeat';
        }
      } else if (meta.startRepeat && m === 0) {
        barlines.push({ x: mStartX, type: 'start-repeat' });
      }

      // Barline at measure end (tagged with the measure starting at this line)
      if (m < maxMeasuresCount - 1) {
        let barType = 'single';
        if (meta.endRepeat) {
          barType = 'end-repeat';
        }
        barlines.push({ x: mBarX, type: barType, measure: m + 1 });
      } else if (meta.endRepeat) {
        barlines.push({ x: mBarX, type: 'end-repeat', measure: m + 1 });
      }

      // Volta bracket
      if (meta.volta) {
        voltas.push({
          number: meta.volta,
          x1: mStartX,
          x2: mBarX,
          y: getStaffLineY(0) - 18
        });
      }

      visibleVoicesList.forEach(v => {
        const staffObj = staves.find(st => st.index === v.staffIndex) || staves[0];
        let currentVoiceClef = v.clef || (staffObj && staffObj.clef) || 'treble';
        const vMeasures = voiceMeasuresMap.get(v.id) || [];
        const mNotes = vMeasures[m] || [];
        const middleLineY = getStaffLineY(2);
        const courtesyState = {};

        mNotes.forEach(mNote => {
          if (mNote.inlineClef) currentVoiceClef = mNote.inlineClef;
          const x = mStartX + 16 + (mNote.startBeatInMeasure / measureBeats(m)) * (measureWidth - 28);
          const y = mNote.isRest ? middleLineY : pitchToY(mNote.step, mNote.octave, currentVoiceClef);

          let stemUp = true;
          if (mNote.stemDefault === 'up') stemUp = true;
          else if (mNote.stemDefault === 'down') stemUp = false;
          else stemUp = y >= middleLineY;

          const ledgerLines = mNote.isRest ? [] : getLedgerLines(y);

          // Courtesy reminder: no explicit accidental, but an earlier explicit
          // alteration on this pitch still sounds in this measure
          let courtesyAlter = null;
          if (!mNote.isRest) {
            const pc = (mNote.step || '').toUpperCase();
            if (mNote.accidental) {
              courtesyState[pc] = mNote.accidental;
            } else if (courtesyState[pc] !== undefined) {
              courtesyAlter = courtesyState[pc];
            }
          }

          allSystemNotes.push({
            ...mNote,
            index: mNote.originalIndex,
            globalKey: `${v.id}-${mNote.originalIndex}`,
            clef: currentVoiceClef,
            spanWidth: (mNote.multiRest > 1 ? measureWidth * Math.floor(mNote.multiRest) - 56 : 0),
            courtesyAlter,
            systemIndex: 0,
            yOffset: staffObj.yOffset,
            totalYOffset: staffObj.yOffset,
            staffYOffset: staffObj.yOffset,
            x,
            y,
            stemUp,
            ledgerLines,
            isBeamed: false,
            beamGroupId: -1,
            stemTipX: 0,
            stemTipY: 0
          });
        });
      });
    }

    // Same-staff collisions first (adjusts stems/heads before beams)
    resolveStaffCollisions(allSystemNotes);
    // Beams and Curves per voice
    const sysBeams = [];
    visibleVoicesList.forEach(v => {
      if (isTabOnlyVoice(v.id)) return;
      const vNotes = allSystemNotes.filter(n => n.voiceId === v.id);
      calculateSystemBeams(vNotes);
      sysBeams.push(...buildBeamsForNotes(vNotes, 0));
    });
    const sysCurves = buildCurvesForNotes(allSystemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), 0);
    const sysGlissandi = buildGlissandiForNotes(allSystemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), 0);
    const sysTuplets = buildTupletsForNotes(allSystemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), 0);
    const sysHairpins = buildHairpinsForNotes(allSystemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), 0);
    const sysOttavas = buildOttavaSpansForNotes(allSystemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), 0);

    const lastBarX = START_X_FIRST + 20 + (maxMeasuresCount * measureWidth);

    resultSystems.push({
      index: 0,
      yOffset: 0,
      width: sysWidth,
      staves: staves.map(st => ({ ...st })),
      barlines,
      voltas,
      isLastSystem: true,
      lastBarlineX: lastBarX,
      notes: allSystemNotes,
      beams: sysBeams,
      curves: sysCurves,
      glissandi: sysGlissandi,
      tuplets: sysTuplets,
      hairpins: sysHairpins,
      ottavas: sysOttavas
    });
  } else {
    // Multi-System Wrapped Lines Mode
    const numSystems = Math.max(1, Math.ceil(maxMeasuresCount / MEASURES_PER_SYSTEM));

    for (let s = 0; s < numSystems; s++) {
      const isFirst = s === 0;
      const isLast = s === numSystems - 1;
      const sysStartX = isFirst ? START_X_FIRST : START_X_SUBSEQUENT;
      const sysWidth = WRAP_SYSTEM_WIDTH;
      const availWidth = sysWidth - sysStartX - 30;
      const measureWidth = availWidth / MEASURES_PER_SYSTEM;
      const barlines = [];
      const voltas = [];
      const systemNotes = [];

      const startMeasureIdx = s * MEASURES_PER_SYSTEM;
      for (let m = 0; m < MEASURES_PER_SYSTEM; m++) {
        const globalMeasureIdx = startMeasureIdx + m;
        if (globalMeasureIdx >= maxMeasuresCount) break;

        const mStartX = sysStartX + (m * measureWidth);
        const mBarX = mStartX + measureWidth;
        const meta = measureMetadataMap.get(globalMeasureIdx) || {};

        if (meta.startRepeat && m === 0) {
          barlines.push({ x: mStartX, type: 'start-repeat' });
        } else if (meta.startRepeat && m > 0) {
          const prevBar = barlines[barlines.length - 1];
          if (prevBar) {
            prevBar.type = prevBar.type === 'end-repeat' ? 'double-repeat' : 'start-repeat';
          }
        }

        if (m < MEASURES_PER_SYSTEM - 1 && globalMeasureIdx < maxMeasuresCount - 1) {
          let barType = 'single';
          if (meta.endRepeat) {
            barType = 'end-repeat';
          }
          barlines.push({ x: mBarX, type: barType, measure: globalMeasureIdx + 1 });
        } else if (meta.endRepeat) {
          barlines.push({ x: mBarX, type: 'end-repeat', measure: globalMeasureIdx + 1 });
        }

        if (meta.volta) {
          voltas.push({
            number: meta.volta,
            x1: mStartX,
            x2: mBarX,
            y: getStaffLineY(0) - 18
          });
        }

        visibleVoicesList.forEach(v => {
          const staffObj = staves.find(st => st.index === v.staffIndex) || staves[0];
          let currentVoiceClef = v.clef || (staffObj && staffObj.clef) || 'treble';
          const vMeasures = voiceMeasuresMap.get(v.id) || [];
          const mNotes = vMeasures[globalMeasureIdx] || [];
          const middleLineY = getStaffLineY(2);
          const courtesyState = {};

          mNotes.forEach(mNote => {
            if (mNote.inlineClef) currentVoiceClef = mNote.inlineClef;
            const x = mStartX + 16 + (mNote.startBeatInMeasure / measureBeats(globalMeasureIdx)) * (measureWidth - 28);
            const y = mNote.isRest ? middleLineY : pitchToY(mNote.step, mNote.octave, currentVoiceClef);

            let stemUp = true;
            if (mNote.stemDefault === 'up') stemUp = true;
            else if (mNote.stemDefault === 'down') stemUp = false;
            else stemUp = y >= middleLineY;

            const ledgerLines = mNote.isRest ? [] : getLedgerLines(y);

            // Courtesy reminder (same rule as single-line branch)
            let courtesyAlterWrap = null;
            if (!mNote.isRest) {
              const pcw = (mNote.step || '').toUpperCase();
              if (mNote.accidental) {
                courtesyState[pcw] = mNote.accidental;
              } else if (courtesyState[pcw] !== undefined) {
                courtesyAlterWrap = courtesyState[pcw];
              }
            }

            systemNotes.push({
              ...mNote,
              index: mNote.originalIndex,
              globalKey: `${v.id}-${mNote.originalIndex}`,
              clef: currentVoiceClef,
              spanWidth: (mNote.multiRest > 1 ? measureWidth * Math.floor(mNote.multiRest) - 56 : 0),
              courtesyAlter: courtesyAlterWrap,
              systemIndex: s,
              yOffset: staffObj.yOffset,
              totalYOffset: (s * sysH) + staffObj.yOffset,
              staffYOffset: staffObj.yOffset,
              x,
              y,
              stemUp,
              ledgerLines,
              isBeamed: false,
              beamGroupId: -1,
              stemTipX: 0,
              stemTipY: 0
            });
          });
        });
      }

      resolveStaffCollisions(systemNotes);
      const sysBeams = [];
      visibleVoicesList.forEach(v => {
        if (isTabOnlyVoice(v.id)) return;
        const vNotes = systemNotes.filter(n => n.voiceId === v.id);
        calculateSystemBeams(vNotes);
        sysBeams.push(...buildBeamsForNotes(vNotes, s));
      });
      const sysCurves = buildCurvesForNotes(systemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), s);
      const sysGlissandi = buildGlissandiForNotes(systemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), s);
      const sysTuplets = buildTupletsForNotes(systemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), s);
      const sysHairpins = buildHairpinsForNotes(systemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), s);
      const sysOttavas = buildOttavaSpansForNotes(systemNotes.filter(n => !isTabOnlyVoice(n.voiceId)), s);

      const lastBarX = sysStartX + (Math.min(MEASURES_PER_SYSTEM, (maxMeasuresCount - startMeasureIdx)) * measureWidth);

      resultSystems.push({
        index: s,
        yOffset: s * sysH,
        width: sysWidth,
        staves: staves.map(st => ({ ...st })),
        barlines,
        voltas,
        isLastSystem: isLast,
        lastBarlineX: Math.min(sysWidth - 15, lastBarX),
        notes: systemNotes,
        beams: sysBeams,
        curves: sysCurves,
        glissandi: sysGlissandi,
        tuplets: sysTuplets,
        hairpins: sysHairpins,
        ottavas: sysOttavas
      });
    }
  }

  return resultSystems;
});

// All flattened notes for SVG rendering
const visualNotes = computed(() => {
  const all = [];
  systems.value.forEach(sys => {
    all.push(...sys.notes);
  });
  return all;
});

const totalMeasures = computed(() => {
  let maxBeats = 0;
  voices.value.forEach(v => {
    let beats = 0;
    v.notes.forEach(n => { beats += durationToQuarterBeats(n.duration, n.isDotted, 1, n.dots || 0); });
    if (beats > maxBeats) maxBeats = beats;
  });
  return Math.max(1, Math.ceil(maxBeats / beatsPerMeasure.value));
});

const calculatedSvgWidth = computed(() => {
  if (selectedWrapMode.value === 'wrap') {
    return WRAP_SYSTEM_WIDTH;
  }
  const measureWidth = 180;
  return Math.max(700, START_X_FIRST + (totalMeasures.value * measureWidth) + 100);
});

const calculatedSvgHeight = computed(() => {
  const sysH = systemHeight.value;
  const numSystems = systems.value.length || 1;
  
  if (props.embedded) {
    let lowestY = 0;
    let hasLyrics = false;
    let hasVerse2 = false;
    visualNotes.value.forEach(n => {
      const noteBottom = n.y + (n.stemUp ? 0 : 35);
      if (noteBottom > lowestY) lowestY = noteBottom;
      if (n.lyric) hasLyrics = true;
      if (n.lyric2) { hasLyrics = true; hasVerse2 = true; }
    });
    const sts = distinctStaves.value;
    // Last staff baseline (cumulative yOffsets already include TAB blocks above)
    const lastStaff = sts.length > 0 ? sts[sts.length - 1] : { yOffset: 0, showTab: false };
    const stavesTotalHeight = lastStaff.showTab
      ? (lastStaff.yOffset + (lastStaff.showTabOnly ? TAB_ONLY_H : TAB_BLOCK_H) + belowTabContent.value)
      : (lastStaff.yOffset + getStaffLineY(4));
    const bottomStaffBaseline = ((numSystems - 1) * sysH) + stavesTotalHeight;
    const lyricPad = hasVerse2 ? 58 : (hasLyrics ? 40 : 25);
    const extraPadding = Math.max(lyricPad, lowestY - bottomStaffBaseline + (hasLyrics ? 35 : 20));
    return Math.max(120, (numSystems - 1) * sysH + stavesTotalHeight + extraPadding);
  }

  const hasAnyLyrics = visualNotes.value.some(n => n.lyric);
  const hasAnyVerse2 = visualNotes.value.some(n => n.lyric2);
  return Math.max(160, numSystems * sysH + (hasAnyVerse2 ? 58 : (hasAnyLyrics ? 40 : 20)));
});

const isFittingContainer = computed(() => {
  return props.fitContainer || (props.embedded && selectedWrapMode.value === 'wrap');
});

const targetWidthPercent = computed(() => {
  if (props.widthPercent !== null && props.widthPercent !== undefined && props.widthPercent !== '') {
    const num = Number(props.widthPercent);
    if (!isNaN(num) && num > 0) return num;
  }
  return 100;
});

const scoreContainerStyle = computed(() => {
  if (isFittingContainer.value) {
    const baseW = targetWidthPercent.value;
    if (effectiveZoom.value !== 1) {
      return {
        width: (baseW * effectiveZoom.value) + '%',
        maxWidth: 'none'
      };
    }
    return {
      width: baseW + '%',
      maxWidth: baseW + '%'
    };
  }
  if (props.embedded && effectiveZoom.value !== 1) {
    return {
      width: (calculatedSvgWidth.value * effectiveZoom.value) + 'px',
      height: (calculatedSvgHeight.value * effectiveZoom.value) + 'px'
    };
  }
  return undefined;
});

const scoreSvgStyle = computed(() => {
  if (isFittingContainer.value) {
    return {
      width: '100%',
      height: 'auto',
      maxWidth: 'none'
    };
  }
  if (effectiveZoom.value !== 1) {
    return {
      transform: 'scale(' + effectiveZoom.value + ')',
      transformOrigin: 'top left'
    };
  }
  return undefined;
});

// Staff position of a key-signature accidental step, reusing the
// canonical layouts (F#/C# from sharp keys, Bb/Eb from flat keys)
function keyAccidentalY(step, isSharp, clef) {
  if (isSharp) {
    if (step === 'F') return getKeyAccidentalsForClef(clef, 'G')[0]?.y;
    if (step === 'C') return getKeyAccidentalsForClef(clef, 'D')[1]?.y;
  } else {
    if (step === 'B') return getKeyAccidentalsForClef(clef, 'F')[0]?.y;
    if (step === 'E') return getKeyAccidentalsForClef(clef, 'Bb')[1]?.y;
  }
  return null;
}

// Naturals cancelling old-key alterations absent from the new key
function keyCancelNaturals(oldKey, newKey, clef) {
  const out = [];
  keySharps(oldKey).forEach(st => {
    if (!keySharps(newKey).includes(st)) {
      const y = keyAccidentalY(st, true, clef);
      if (y !== null && y !== undefined) out.push({ symbol: '=', y });
    }
  });
  keyFlats(oldKey).forEach(st => {
    if (!keyFlats(newKey).includes(st)) {
      const y = keyAccidentalY(st, false, clef);
      if (y !== null && y !== undefined) out.push({ symbol: '=', y });
    }
  });
  return out;
}

// Full key-signature accidental set for a change: cancelling naturals
// followed by the new sharps/flats (symbols: '=' natural, '#', 'b')
function keySigWithCancel(clef, newKey, oldKey) {
  const accs = getKeyAccidentalsForClef(clef, newKey).map(a => ({
    symbol: a.symbol === '♯' ? '#' : 'b',
    y: a.y
  }));
  if (!oldKey || oldKey === newKey) return accs;
  return [...keyCancelNaturals(oldKey, newKey, clef), ...accs];
}

// End-of-system courtesy (wrap mode): clef/key/time of the next system
// when they differ from this system's ending state. Per staff.
function systemEndCourtesy(sysIdx) {
  if (selectedWrapMode.value !== 'wrap') return [];
  if (systems.value.length < 2 || sysIdx >= systems.value.length - 1) return [];
  const sys = systems.value[sysIdx];
  const next = systems.value[sysIdx + 1];
  if (!sys || !next) return [];
  const firstNext = (sysIdx + 1) * MEASURES_PER_SYSTEM;
  const lastCur = firstNext - 1;
  const newKey = keyForMeasure(firstNext);
  const oldKey = keyForMeasure(lastCur);
  const out = [];
  sys.staves.forEach(st => {
    const here = (sys.notes || []).filter(n => !n.isRest && n.staffYOffset === st.yOffset).sort((a, b) => a.x - b.x);
    const there = (next.notes || []).filter(n => !n.isRest && n.staffYOffset === st.yOffset).sort((a, b) => a.x - b.x);
    const endClef = [...here].reverse().find(n => n.inlineClef)?.inlineClef || st.clef;
    const startClef = there.find(n => n.inlineClef)?.inlineClef || st.clef;
    const item = { staff: st, clef: null, accs: [], time: null };
    if (endClef !== startClef) item.clef = startClef;
    if (oldKey !== newKey) {
      item.accs = [...keyCancelNaturals(dispKey(oldKey), dispKey(newKey), st.clef), ...getKeyAccidentalsForClef(st.clef, dispKey(newKey))];
    }
    if (timeChangeAt(firstNext) || measureBeats(firstNext) !== measureBeats(lastCur)) {
      item.time = timeForMeasure(firstNext);
    }
    if (item.clef || item.accs.length > 0 || item.time) out.push(item);
  });
  return out;
}

// Key signature accidentals helper (keyOverride for mid-piece changes)
function getKeyAccidentalsForClef(clef, keyOverride) {
  const mapTreble = {
    'G': [{ symbol: '♯', y: getStaffLineY(0) + 4 }],
    'D': [{ symbol: '♯', y: getStaffLineY(0) + 4 }, { symbol: '♯', y: getStaffLineY(1) + 8 }],
    'F': [{ symbol: '♭', y: getStaffLineY(2) + 4 }],
    'Bb': [{ symbol: '♭', y: getStaffLineY(2) + 4 }, { symbol: '♭', y: getStaffLineY(0) + 8 }],
    'Gm': [{ symbol: '♭', y: getStaffLineY(2) + 4 }, { symbol: '♭', y: getStaffLineY(0) + 8 }],
    'Am': [],
    'Em': [{ symbol: '♯', y: getStaffLineY(0) + 4 }],
    'Dm': [{ symbol: '♭', y: getStaffLineY(2) + 4 }]
  };
  const mapBass = {
    'G': [{ symbol: '♯', y: getStaffLineY(1) + 4 }],
    'D': [{ symbol: '♯', y: getStaffLineY(1) + 4 }, { symbol: '♯', y: getStaffLineY(2) + 8 }],
    'F': [{ symbol: '♭', y: getStaffLineY(3) + 4 }],
    'Bb': [{ symbol: '♭', y: getStaffLineY(3) + 4 }, { symbol: '♭', y: getStaffLineY(1) + 8 }],
    'Gm': [{ symbol: '♭', y: getStaffLineY(3) + 4 }, { symbol: '♭', y: getStaffLineY(1) + 8 }],
    'Am': [],
    'Em': [{ symbol: '♯', y: getStaffLineY(1) + 4 }],
    'Dm': [{ symbol: '♭', y: getStaffLineY(3) + 4 }]
  };
  const mapAlto = {
    'G': [{ symbol: '♯', y: getStaffLineY(0) + 8 }],
    'D': [{ symbol: '♯', y: getStaffLineY(0) + 8 }, { symbol: '♯', y: getStaffLineY(2) + 4 }],
    'F': [{ symbol: '♭', y: getStaffLineY(1) + 8 }],
    'Bb': [{ symbol: '♭', y: getStaffLineY(1) + 8 }, { symbol: '♭', y: getStaffLineY(0) + 4 }],
    'Gm': [{ symbol: '♭', y: getStaffLineY(1) + 8 }, { symbol: '♭', y: getStaffLineY(0) + 4 }],
    'Am': [],
    'Em': [{ symbol: '♯', y: getStaffLineY(0) + 8 }],
    'Dm': [{ symbol: '♭', y: getStaffLineY(1) + 8 }]
  };

  const k = keyOverride || selectedKey.value;
  if (clef === 'bass') return mapBass[k] || [];
  if (clef === 'alto' || clef === 'c' || clef === 'tenor') return mapAlto[k] || [];
  return mapTreble[k] || [];
}

// Active Playhead Cursor Position
const activePlayhead = computed(() => {
  if (activePlayingBeat.value < 0) return null;
  const isWrap = selectedWrapMode.value === 'wrap';
  const staves = distinctStaves.value;
  if (!staves || staves.length === 0) return null;

  const firstStaff = staves[0];
  const lastStaff = staves[staves.length - 1];

  const topStaffY = (firstStaff?.yOffset || 0) + (firstStaff?.showTabOnly ? firstStaff.tabBase : getStaffLineY(0));
  const bottomStaffY = (lastStaff?.yOffset || 0) + (lastStaff?.showTab
    ? (lastStaff.tabBase + (TAB_STRINGS - 1) * TAB_LINE_SPACING)
    : getStaffLineY(4));

  const beat = activePlayingBeat.value;
  // Map absolute beat -> measure using cumulative variable-meter boundaries
  let measureIdx = 0;
  while (measureIdx < totalMeasures.value - 1 && beat >= measureStartBeat(measureIdx + 1) - 0.0001) {
    measureIdx++;
  }
  const beatInMeasure = Math.max(0, beat - measureStartBeat(measureIdx));
  const beatsThisMeasure = measureBeats(measureIdx);

  let sysIdx = 0;
  let mInSys = measureIdx;
  if (isWrap) {
    sysIdx = Math.floor(measureIdx / MEASURES_PER_SYSTEM);
    mInSys = measureIdx % MEASURES_PER_SYSTEM;
  }

  const sys = systems.value[sysIdx];
  if (!sys) return null;

  const sysStartX = sysIdx === 0 ? START_X_FIRST : START_X_SUBSEQUENT;
  const availWidth = (isWrap ? WRAP_SYSTEM_WIDTH : sys.width) - sysStartX - 30;
  const measureWidth = isWrap ? (availWidth / MEASURES_PER_SYSTEM) : 180;

  const mStartX = isWrap ? (sysStartX + mInSys * measureWidth) : (START_X_FIRST + 20 + measureIdx * measureWidth);
  const x = mStartX + 16 + (beatInMeasure / beatsThisMeasure) * (measureWidth - 28);

  return {
    x,
    yOffset: sys.yOffset,
    topStaffY,
    bottomStaffY
  };
});

// Computed ABC Notation
const computedAbc = computed(() => {
  return notesToAbc(null, {
    voices: voices.value,
    notes: activeVoice.value?.notes || [],
    title: scoreTitle.value,
    composer: scoreComposer.value,
    key: selectedKey.value,
    timeSignature: selectedTimeSignature.value,
    tempo: selectedTempo.value
  });
});

// Computed MusicXML
const computedMusicXml = computed(() => {
  return notesToMusicXml(activeVoice.value?.notes || [], {
    title: scoreTitle.value,
    key: selectedKey.value,
    timeSignature: selectedTimeSignature.value,
    tempo: selectedTempo.value,
    keyChanges: keyChanges.value,
    timeChanges: timeChanges.value,
    tempoChanges: tempoChanges.value,
    tempoRamps: tempoRamps.value,
    measureBeatsAt: (m) => measureBeats(m)
  });
});

const exportContent = computed(() => {
  if (exportFormat.value === 'abc') return partAbc.value;
  if (exportFormat.value === 'musicxml') return partMusicXml.value;
  return partJson.value;
});

// Part-scoped exports (full score when no part is selected)
const partAbc = computed(() => {
  return notesToAbc(null, {
    voices: includedVoices(voices.value),
    notes: activeVoice.value?.notes || [],
    title: scoreTitle.value,
    composer: scoreComposer.value,
    key: selectedKey.value,
    timeSignature: selectedTimeSignature.value,
    tempo: selectedTempo.value
  });
});

const partMusicXml = computed(() => {
  const target = partVoiceId.value
    ? (voices.value.find(v => v.id === partVoiceId.value) || activeVoice.value)
    : activeVoice.value;
  return notesToMusicXml(target?.notes || [], {
    title: scoreTitle.value,
    key: selectedKey.value,
    timeSignature: selectedTimeSignature.value,
    tempo: selectedTempo.value,
    keyChanges: keyChanges.value,
    timeChanges: timeChanges.value,
    tempoChanges: tempoChanges.value,
    tempoRamps: tempoRamps.value,
    measureBeatsAt: (m) => measureBeats(m)
  });
});

const partJson = computed(() => {
  return JSON.stringify(includedVoices(voices.value), null, 2);
});

function copyExportToClipboard() {
  navigator.clipboard.writeText(exportContent.value).then(() => {
    copiedMessage.value = t('modal.copied');
    setTimeout(() => { copiedMessage.value = ''; }, 2500);
  });
}

function downloadExportFile() {
  const ext = exportFormat.value === 'abc' ? 'abc' : (exportFormat.value === 'musicxml' ? 'musicxml' : 'json');
  const blob = new Blob([exportContent.value], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const partSuffix = partVoiceId.value
    ? '-' + (((voices.value.find(v => v.id === partVoiceId.value) || {}).name || partVoiceId.value).toLowerCase().replace(/\s+/g, '_'))
    : '';
  a.download = `${scoreTitle.value.toLowerCase().replace(/\s+/g, '_')}${partSuffix}.${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}

function processImport() {
  importError.value = '';
  try {
    if (!importText.value.trim()) {
      importError.value = t('dialog.emptyScore');
      return;
    }

    pushHistory();

    partVoiceId.value = null;
    if (importFormat.value === 'abc') {
      const parsed = abcToNotes(importText.value);
      if (parsed.voices && parsed.voices.length > 0) {
        voices.value = parsed.voices.map(v => ({
          ...v,
          instrument: v.instrument || 'acoustic_grand_piano'
        }));
        activeVoiceId.value = parsed.voices[0].id;
        selectedClef.value = parsed.voices[0].clef;
      } else {
        voices.value = [{
          id: 'V1',
          name: 'Melodia',
          clef: 'treble',
          instrument: 'acoustic_grand_piano',
          staffIndex: 0,
          stemDefault: 'auto',
          notes: parsed.notes || []
        }];
        activeVoiceId.value = 'V1';
      }
      if (parsed.title) scoreTitle.value = parsed.title;
      if (parsed.composer) scoreComposer.value = parsed.composer;
      if (parsed.timeSignature) selectedTimeSignature.value = parsed.timeSignature;
      if (parsed.key) selectedKey.value = parsed.key;
      keyChanges.value = Array.isArray(parsed.keyChanges) ? [...parsed.keyChanges] : [];
      timeChanges.value = Array.isArray(parsed.timeChanges) ? [...parsed.timeChanges] : [];
      tempoChanges.value = Array.isArray(parsed.tempoChanges) ? [...parsed.tempoChanges] : [];
      tempoRamps.value = Array.isArray(parsed.tempoRamps) ? [...parsed.tempoRamps] : [];
      swing.value = { on: false, amount: 0.67 };
      if (parsed.tempo) selectedTempo.value = parsed.tempo;
    } else {
      const parsed = musicXmlToNotes(importText.value);
      voices.value = [{
        id: 'V1',
        name: 'Melodia',
        clef: 'treble',
        instrument: 'acoustic_grand_piano',
        staffIndex: 0,
        stemDefault: 'auto',
        notes: parsed.notes || []
      }];
      activeVoiceId.value = 'V1';
      if (parsed.title) scoreTitle.value = parsed.title;
      if (parsed.timeSignature) selectedTimeSignature.value = parsed.timeSignature;
      if (parsed.key) selectedKey.value = parsed.key;
      keyChanges.value = Array.isArray(parsed.keyChanges) ? [...parsed.keyChanges] : [];
      timeChanges.value = Array.isArray(parsed.timeChanges) ? [...parsed.timeChanges] : [];
      tempoChanges.value = Array.isArray(parsed.tempoChanges) ? [...parsed.tempoChanges] : [];
      tempoRamps.value = Array.isArray(parsed.tempoRamps) ? [...parsed.tempoRamps] : [];
      swing.value = { on: false, amount: 0.67 };
      if (parsed.tempo) selectedTempo.value = parsed.tempo;
    }

    showImportModal.value = false;
    notifyChange();
  } catch (err) {
    importError.value = 'Erro ao processar: ' + err.message;
  }
}

function loadSampleMelody() {
  if (importFormat.value === 'abc') {
    importText.value = `X:1
T:Brilha Brilha Estrelinha
C:Tradicional
M:4/4
L:1/8
Q:1/4=100
K:C

C2 C2 G2 G2 | A2 A2 G4 | F2 F2 E2 E2 | D2 D2 C4 |
w: Bri- lha bri- lha es- tre- li- nha, que- ro ver vo- cê bri- lhar`;
  } else {
    importText.value = `<?xml version="1.0" encoding="UTF-8"?>
<score-partwise version="4.0">
  <work><work-title>Twinkle Little Star</work-title></work>
  <part-list><score-part id="P1"><part-name>Melody</part-name></score-part></part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>1</divisions>
        <time><beats>4</beats><beat-type>4</beat-type></time>
        <clef><sign>G</sign><line>2</line></clef>
      </attributes>
      <direction placement="above"><sound tempo="100"/></direction>
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric number="1"><text>Twin-</text></lyric></note>
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric number="1"><text>kle</text></lyric></note>
      <note><pitch><step>G</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric number="1"><text>twin-</text></lyric></note>
      <note><pitch><step>G</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric number="1"><text>kle</text></lyric></note>
    </measure>
  </part>
</score-partwise>`;
  }
}

// Web Audio API Polyphonic Soundfont Engine

function playNoteSound(step, octave, accidental, durationSec = 0.3, instId = selectedInstrument.value, gainLevel = 0.8) {
  playSoundfontNote(step, octave, accidental, durationSec, instId, gainLevel);
}

// Dynamic marking -> velocity gain (carried forward per voice in the sequencer)
function dynamicGain(dynamic) {
  const map = { ppp: 0.35, pp: 0.45, p: 0.55, mp: 0.65, mf: 0.75, f: 0.85, ff: 0.95, fff: 1.0, sfz: 0.95, sfp: 0.6, fp: 0.7 };
  return map[dynamic] !== undefined ? map[dynamic] : 0.8;
}

// Key-signature accidental for a step (upper/lower auxiliaries of ornaments)
function keyAccidentalFor(step, key) {
  const s = (step || '').toUpperCase();
  const SHARPS = { G: ['F'], D: ['F', 'C'], Em: ['F'] };
  const FLATS = { F: ['B'], Bb: ['B', 'E'], Dm: ['B'], Gm: ['B', 'E'] };
  if ((SHARPS[key] || []).includes(s)) return '#';
  if ((FLATS[key] || []).includes(s)) return 'b';
  return '';
}

function neighborPitch(v, dir, key) {
  const info = getPitchFromDiatonicIndex(getDiatonicIndex(v.step, v.octave) + dir);
  return { step: info.step, octave: info.octave, accidental: keyAccidentalFor(info.step, key) };
}

// Realized playback: ornaments (trill/mordent/turn/pralltriller), grace notes
// and arpeggios are expanded into scheduled hits. Plain notes (incl. clusters)
// sound simultaneously. tracked=true ties sub-hits to the transport (stop).
function playRealizedNote(note, durationSec = 0.3, instId = selectedInstrument.value, measureNum = null, tracked = false, gainLevel = null) {
  if (!note || note.isRest) return;
  const key = keyForMeasure(measureNum ?? 0);
  const gain = gainLevel !== null && gainLevel !== undefined ? gainLevel : dynamicGain(note.dynamic);
  const later = (fn, ms) => {
    if (ms <= 0) {
      fn();
      return;
    }
    if (tracked) {
      playTimeouts.push(setTimeout(() => { if (isPlaying.value) fn(); }, ms));
    } else {
      setTimeout(fn, ms);
    }
  };
  // Drum notes bypass pitched synthesis entirely
  if (note.drum || instId === 'drum_kit') {
    const piece = note.drum || 'snare';
    later(() => playDrumSound(piece, { durationSec, gainLevel: gain }), 0);
    return;
  }
  const hit = (p, atMs, dur) => later(() => playNoteSound(p.step, p.octave, p.accidental, dur, instId, gain), atMs);
  const all = [
    { step: note.step, octave: note.octave, accidental: note.accidental || '' },
    ...(note.pitches || []).filter(p => p && p.step).map(p => ({ step: p.step, octave: p.octave || 4, accidental: p.accidental || '' }))
  ];
  const D = Math.max(0.06, durationSec);
  const orn = note.ornament;
  const hasOrn = orn === 'trill' || orn === 'mordent' || orn === 'turn' || orn === 'pralltriller';
  const graceMs = note.grace ? Math.min(70, D * 1000 * 0.25) : 0;

  all.forEach(v => {
    const up = neighborPitch(v, 1, key);
    const lo = neighborPitch(v, -1, key);
    const t0 = graceMs;
    if (note.grace) {
      hit(up, 0, Math.max(0.05, graceMs / 1000));
    }
    if (!hasOrn) {
      if (note.arpeggio && all.length > 1) {
        // Strum handled at chord level below; skip individual scheduling here
      } else {
        hit(v, t0, D);
      }
    } else if (orn === 'trill') {
      const n = Math.min(16, Math.max(2, Math.round((D * 1000) / 75)));
      const iv = (D * 1000) / n;
      for (let i = 0; i < n; i++) {
        hit(i % 2 === 0 ? v : up, t0 + i * iv, Math.max(0.05, (iv / 1000) * 1.15));
      }
    } else if (orn === 'mordent') {
      hit(v, t0, D / 3);
      hit(up, t0 + (D * 1000) / 3, D / 3);
      hit(v, t0 + (2 * D * 1000) / 3, D / 3);
    } else if (orn === 'pralltriller') {
      hit(v, t0, D / 3);
      hit(lo, t0 + (D * 1000) / 3, D / 3);
      hit(v, t0 + (2 * D * 1000) / 3, D / 3);
    } else if (orn === 'turn') {
      hit(up, t0, D / 4);
      hit(v, t0 + (D * 1000) / 4, D / 4);
      hit(lo, t0 + (D * 1000) / 2, D / 4);
      hit(v, t0 + (3 * D * 1000) / 4, D / 4);
    }
  });

  // Arpeggio strum (no ornament): low-to-high stagger across the chord
  if (note.arpeggio && !hasOrn && all.length > 1) {
    const ordered = [...all].sort((a, b) => getDiatonicIndex(a.step, a.octave) - getDiatonicIndex(b.step, b.octave));
    ordered.forEach((p, i) => {
      hit(p, graceMs + i * 45, D);
    });
  }
}

// Ottava transposition map: globalKey -> +1 (8va) | -1 (8vb), from start/end spans
const ottavaShiftMap = computed(() => {
  const map = new Map();
  voices.value.forEach(v => {
    let active = null;
    (v.notes || []).forEach((n, i) => {
      if (n.ottavaStart === '8va' || n.ottavaStart === '8vb') active = n.ottavaStart;
      if (active && !n.isRest) map.set(`${v.id}-${i}`, active === '8va' ? 1 : -1);
      if (n.ottavaEnd) active = null;
    });
  });
  return map;
});

function applyOttavaShift(note, globalKey) {
  const delta = ottavaShiftMap.value.get(globalKey);
  if (!delta || !note || note.isRest) return note;
  const shift = (o) => Math.min(8, Math.max(1, (o || 4) + delta));
  return {
    ...note,
    octave: shift(note.octave),
    pitch: `${note.step}${shift(note.octave)}`,
    pitches: (note.pitches || []).map(p => ({ ...p, octave: shift(p.octave), pitch: `${p.step}${shift(p.octave)}` }))
  };
}

function togglePlay() {
  if (isPlaying.value) {
    stopPlay();
  } else {
    startPlay();
  }
}

function stopPlay() {
  if (isPlaying.value) {
    emit('playback-end');
    emit('playback-state', false);
  }
  isPlaying.value = false;
  activePlayingBeat.value = -1;
  activePlayingNoteKeys.value = new Set();
  playTimeouts.forEach(t => clearTimeout(t));
  playTimeouts = [];
}

function startPlay() {
  if (totalNotesCount.value === 0) return;
  if (partVoiceId.value) {
    const pv = voices.value.find(v => v.id === partVoiceId.value);
    if (!pv || !(pv.notes || []).length) return;
  }
  stopPlay();
  isPlaying.value = true;
  emit('playback-start');
  emit('playback-state', true);

  // Preload soundfont samples for each voice in the current part/view
  includedVoices(voices.value).forEach(v => {
    if (v.notes && v.notes.length > 0) {
      preloadNotes(v.notes, v.instrument || 'acoustic_grand_piano');
    }
  });

  const bpm = beatsPerMeasure.value;

  const audibleVoices = includedVoices(voices.value);

  // 1. Group notes into measures per voice
  const voiceMeasuresMap = new Map();
  let maxMeasuresCount = 1;

  audibleVoices.forEach(v => {
    const vMeasures = [];
    let curMeasure = [];
    let curBeats = 0;

    v.notes.forEach((note, origIdx) => {
      const qBeats = durationToQuarterBeats(note.duration, note.isDotted, 1, note.dots || 0);
      if (curBeats > 0 && (curBeats + qBeats > bpm + 0.0001)) {
        vMeasures.push(curMeasure);
        curMeasure = [];
        curBeats = 0;
      }
      curMeasure.push({
        ...note,
        originalIndex: origIdx,
        voiceId: v.id,
        voiceInstrument: v.instrument || 'acoustic_grand_piano',
        startBeatInMeasure: curBeats,
        qBeats
      });
      curBeats += qBeats;
      if (curBeats >= bpm - 0.0001) {
        vMeasures.push(curMeasure);
        curMeasure = [];
        curBeats = 0;
      }
    });
    if (curMeasure.length > 0) vMeasures.push(curMeasure);
    if (vMeasures.length === 0) vMeasures.push([]);

    voiceMeasuresMap.set(v.id, vMeasures);
    if (vMeasures.length > maxMeasuresCount) maxMeasuresCount = vMeasures.length;
  });

  // 2. Identify repeat segments and build play sequence of measure indices
  // Detect repeat boundaries & voltas
  const measureMeta = [];
  let segnoM = -1;
  let codaM = -1;
  for (let m = 0; m < maxMeasuresCount; m++) {
    let startRepeat = false;
    let endRepeat = false;
    let volta = null;
    const navFlags = { toCodaMark: false, fineMark: false, jumpMark: null };
    audibleVoices.forEach(v => {
      const vMeasures = voiceMeasuresMap.get(v.id) || [];
      const mNotes = vMeasures[m] || [];
      if (mNotes.some(n => n.startRepeat)) startRepeat = true;
      if (mNotes.some(n => n.endRepeat)) endRepeat = true;
      const vVolta = mNotes.find(n => n.volta !== null && n.volta !== undefined);
      if (vVolta) volta = vVolta.volta;
      if (navFlags.jumpMark || navFlags.fineMark || navFlags.toCodaMark) return;
      const navNote = mNotes.find(n => n.navigation);
      const nav = navNote ? String(navNote.navigation).toLowerCase() : null;
      if (nav === 'segno') segnoM = m;
      else if (nav === 'coda') codaM = m;
      else if (nav === 'fine') navFlags.fineMark = true;
      else if (nav === 'to coda') navFlags.toCodaMark = true;
      else if (nav && nav.includes('d.s.') && nav.includes('coda')) navFlags.jumpMark = 'dsCoda';
      else if (nav && nav.includes('d.c.') && nav.includes('coda')) navFlags.jumpMark = 'dcCoda';
      else if (nav && nav.includes('d.s.')) navFlags.jumpMark = 'ds';
      else if (nav && nav.includes('d.c.')) navFlags.jumpMark = 'dc';
    });
    measureMeta.push({ startRepeat, endRepeat, volta, ...navFlags });
  }

  // Build playback order: e.g. [0, 1, 2, 0, 1, 3] for a repeat with 1st and 2nd volta
  const playMeasureSequence = [];
  let mIdx = 0;
  let repeatStartMeasure = 0;
  const repeatedSections = new Set();
  const maxPlaybackSteps = Math.max(16, maxMeasuresCount * 4);
  let stepsCount = 0;

  while (mIdx < maxMeasuresCount && stepsCount++ < maxPlaybackSteps) {
    const meta = measureMeta[mIdx];
    if (meta.startRepeat) {
      repeatStartMeasure = mIdx;
    }

    const isRepeated = repeatedSections.has(repeatStartMeasure);
    if (isRepeated && meta.volta === 1) {
      // Skip 1st volta on second pass
      mIdx++;
      continue;
    }

    playMeasureSequence.push(mIdx);

    if (meta.endRepeat && !repeatedSections.has(repeatStartMeasure)) {
      repeatedSections.add(repeatStartMeasure);
      mIdx = repeatStartMeasure;
    } else {
      mIdx++;
    }
  }

  // 2b. Navigation jumps (D.S./D.C. al Fine/Coda) as a post-pass over the
  // repeat-resolved sequence. Each jump mark is taken once; Fine ends the
  // piece after a jump; To Coda diverts to the coda once armed.
  const navMeta = { segnoM, codaM };
  const seqIndexOfMeasure = (mm) => playMeasureSequence.indexOf(mm);
  const finalSequence = [];
  {
    const taken = new Set();
    let alCodaArmed = false;
    let codaDone = false;
    let i = 0;
    let guard = 0;
    const maxSteps = Math.max(64, playMeasureSequence.length * 4);
    while (i < playMeasureSequence.length && guard++ < maxSteps) {
      const m = playMeasureSequence[i];
      finalSequence.push(m);
      const mt = measureMeta[m] || {};
      // To Coda diversion (only after an al-coda jump, once)
      if (mt.toCodaMark && alCodaArmed && !codaDone && navMeta.codaM >= 0) {
        const j = seqIndexOfMeasure(navMeta.codaM);
        if (j >= 0) {
          codaDone = true;
          alCodaArmed = false;
          taken.add('dsAlCoda');
          taken.add('dcAlCoda');
          i = j;
          continue;
        }
      }
      // Jump instructions
      const jumpKind = mt.jumpMark;
      if (jumpKind && !taken.has(jumpKind) && !codaDone) {
        const target = jumpKind === 'ds' || jumpKind === 'dsCoda' ? navMeta.segnoM : 0;
        const j = target >= 0 ? seqIndexOfMeasure(target) : -1;
        if (j >= 0) {
          taken.add(jumpKind);
          if (jumpKind === 'dsCoda' || jumpKind === 'dcCoda') alCodaArmed = true;
          i = j;
          continue;
        }
        taken.add(jumpKind);
      }
      // Fine ends the piece once any jump was taken
      if (mt.fineMark && taken.size > 0) break;
      i++;
    }
  }

  // 3. Build timed events list with support for sustained ties.
  // Timing integrates the tempo map (sudden changes + rit./accel. ramps),
  // so measures under different tempi get their true millisecond length.
  // Score-beat space is used for the map; repeats replay the same span.
  const tempoPts = buildTempoPoints();
  let playTimeMs = 0;
  let maxEndTimeMs = 0;

  // Flatten playback sequence notes per voice to calculate sustained tied durations
  const voicePlaybackNotes = new Map();
  audibleVoices.forEach(v => {
    voicePlaybackNotes.set(v.id, []);
  });

  // Navigation jumps applied: play the final (repeat + D.S./D.C.) sequence
  finalSequence.forEach((measureNum) => {
    const mStartScoreBeat = measureStartBeat(measureNum);
    const mEndScoreBeat = mStartScoreBeat + measureBeats(measureNum);
    const mStartMs = msAtBeat(mStartScoreBeat, tempoPts);
    audibleVoices.forEach(v => {
      const vMeasures = voiceMeasuresMap.get(v.id) || [];
      const mNotes = vMeasures[measureNum] || [];
      mNotes.forEach(mNote => {
        const noteEvents = voicePlaybackNotes.get(v.id);
        const noteStartBeat = mStartScoreBeat + mNote.startBeatInMeasure;
        const noteEndBeat = noteStartBeat + mNote.qBeats;
        noteEvents.push({
          measureNum,
          startBeatInMeasure: mNote.startBeatInMeasure,
          note: mNote,
          voiceId: v.id,
          voiceInstrument: v.instrument || 'acoustic_grand_piano',
          globalKey: `${v.id}-${mNote.originalIndex}`,
          durationMs: msAtBeat(noteEndBeat, tempoPts) - msAtBeat(noteStartBeat, tempoPts),
          measureStartTimeMs: playTimeMs,
          eventTimeMs: playTimeMs + (msAtBeat(noteStartBeat, tempoPts) - mStartMs),
          isTiedContinuation: false,
          totalSustainMs: msAtBeat(noteEndBeat, tempoPts) - msAtBeat(noteStartBeat, tempoPts)
        });
      });
    });
    playTimeMs += msAtBeat(mEndScoreBeat, tempoPts) - mStartMs;
  });

  // Calculate tie chain durations per voice
  audibleVoices.forEach(v => {
    const list = voicePlaybackNotes.get(v.id) || [];
    for (let i = 0; i < list.length; i++) {
      const cur = list[i];
      if (cur.note.hasTie && !cur.note.isRest) {
        let chainDur = cur.durationMs;
        let j = i + 1;
        while (j < list.length) {
          const next = list[j];
          if (!next.note.isRest && next.note.pitch === cur.note.pitch) {
            next.isTiedContinuation = true;
            chainDur += next.durationMs;
            if (!next.note.hasTie) break;
            j++;
          } else {
            break;
          }
        }
        cur.totalSustainMs = chainDur;
      }
    }
  });

  // Fermata holds ~1.75x (sustain only; timeline unshifted)
  // Dynamics carry forward per voice until changed
  // Hairpins scale gain along start->end spans (crescendo up, diminuendo down)
  const isHpStart = (h) => h === 'crescendo-start' || h === 'diminuendo-start';
  const isHpEnd = (h) => h === 'crescendo-end' || h === 'diminuendo-end' || isHpStart(h);
  audibleVoices.forEach(v => {
    const list = voicePlaybackNotes.get(v.id) || [];
    // Collect hairpin spans first (indexes into this voice list)
    const spans = [];
    let open = null;
    list.forEach((item, idx) => {
      const h = item.note.hairpin;
      if (h === 'crescendo-start' || h === 'diminuendo-start') {
        if (open) spans.push({ ...open, end: idx });
        open = { type: h === 'crescendo-start' ? 'crescendo' : 'diminuendo', start: idx };
      } else if (open && isHpEnd(h)) {
        spans.push({ ...open, end: idx });
        open = null;
      }
    });
    if (open) spans.push({ ...open, end: list.length - 1 });
    const rampFor = (idx) => {
      for (const s of spans) {
        if (idx >= s.start && idx <= s.end) {
          const t = s.end === s.start ? 1 : (idx - s.start) / (s.end - s.start);
          return s.type === 'crescendo' ? 0.6 + 0.65 * t : 1.25 - 0.65 * t;
        }
      }
      return 1;
    };
    let curDyn = 'mf';
    let curM = -1;
    let accState = {};
    list.forEach((item, idx) => {
      if (!item.note.isRest && item.note.articulation === 'fermata') {
        item.totalSustainMs = item.totalSustainMs * 1.75;
      }
      if (item.note.dynamic) curDyn = item.note.dynamic;
      item.gain = Math.min(1.0, Math.max(0.2, dynamicGain(curDyn) * rampFor(idx)));
      // Accidental carry within the measure + key signature default.
      // Repeats revisit the same measure, so state resets per measure.
      if (item.measureNum !== curM) {
        curM = item.measureNum;
        accState = {};
      }
      const n = item.note;
      if (!n.isRest) {
        const pc = (n.step || '').toUpperCase();
        let eff = n.accidental || '';
        if (!eff) {
          if (accState[pc] !== undefined) eff = accState[pc];
          else if (v.clef !== 'percussion') eff = keyAlterFor(pc, keyForMeasure(item.measureNum));
        } else {
          accState[pc] = eff;
        }
        item.effAcc = eff;
        const km = keyForMeasure(item.measureNum);
        item.effExtraAccs = (n.pitches || []).map(p => p.accidental || (v.clef === 'percussion' ? '' : keyAlterFor(p.step, km)));
      } else {
        item.effAcc = null;
      }
    });
    // Swing: pair on-beat straight 8ths with the following 8th
    if (swing.value.on) {
      const R = Math.min(0.75, Math.max(0.5, swing.value.amount || 0.67));
      const isSw = (it) => it && !it.note.isRest && it.note.duration === '8' && !it.note.tuplet
        && !(it.note.dots > 0) && !it.note.hasTie && !it.isTiedContinuation;
      for (let i = 0; i < list.length - 1; i++) {
        const a = list[i];
        const b = list[i + 1];
        if (!isSw(a) || !isSw(b)) continue;
        if (Math.abs(a.startBeatInMeasure % 1) > 0.001) continue;
        if (Math.abs(b.eventTimeMs - (a.eventTimeMs + a.durationMs)) > 1.5) continue;
        const aScale = R / 0.5;
        const bScale = (1 - R) / 0.5;
        const aOrigDur = a.durationMs;
        a.durationMs *= aScale;
        a.totalSustainMs *= aScale;
        const shift = a.durationMs - aOrigDur;
        b.eventTimeMs += shift;
        b.durationMs *= bScale;
        b.totalSustainMs *= bScale;
        i++;
      }
    }
  });

  // Schedule playback events
  const allTimedEvents = [];
  voicePlaybackNotes.forEach(list => {
    allTimedEvents.push(...list);
  });

  // Group events by eventTimeMs
  const eventTimeMap = new Map();
  allTimedEvents.forEach(item => {
    const roundedTime = Math.round(item.eventTimeMs);
    if (!eventTimeMap.has(roundedTime)) eventTimeMap.set(roundedTime, []);
    eventTimeMap.get(roundedTime).push(item);
  });

  eventTimeMap.forEach((itemsAtTime, eventTimeMs) => {
    const firstItem = itemsAtTime[0];
    const displayGlobalBeat = (firstItem.measureNum * bpm) + firstItem.startBeatInMeasure;

    const timeout = setTimeout(() => {
      if (!isPlaying.value) return;
      activePlayingBeat.value = displayGlobalBeat;

      const keysSet = new Set();
      itemsAtTime.forEach(item => {
        keysSet.add(item.globalKey);
        if (!item.note.isRest && !item.note.isHidden) {
          if (!item.isTiedContinuation) {
            const soundDur = Math.max(0.05, (item.totalSustainMs / 1000) * 0.95);
            const sounding = applyOttavaShift(item.note, item.globalKey);
            if (item.effAcc !== undefined && item.effAcc !== null && !item.note.isRest) {
              sounding.accidental = item.effAcc;
              if (item.effExtraAccs) {
                sounding.pitches = (sounding.pitches || []).map((p, pi) => ({ ...p, accidental: item.effExtraAccs[pi] ?? p.accidental }));
              }
            }
            playRealizedNote(sounding, soundDur, item.voiceInstrument, item.measureNum, true, item.gain);
          }
        }
      });
      activePlayingNoteKeys.value = keysSet;

      // Auto-scroll follow playhead
      if (scoreScrollContainer.value && activePlayhead.value) {
        const ph = activePlayhead.value;
        const container = scoreScrollContainer.value;
        if (selectedWrapMode.value === 'single') {
          if (ph.x > container.scrollLeft + container.clientWidth - 120 || ph.x < container.scrollLeft + 60) {
            container.scrollTo({
              left: Math.max(0, ph.x - container.clientWidth / 2),
              behavior: 'smooth'
            });
          }
        } else {
          if (ph.yOffset > container.scrollTop + container.clientHeight - 160 || ph.yOffset < container.scrollTop) {
            container.scrollTo({
              top: Math.max(0, ph.yOffset - 30),
              behavior: 'smooth'
            });
          }
        }
      }
    }, eventTimeMs);

    playTimeouts.push(timeout);

    itemsAtTime.forEach(item => {
      const itemEndMs = eventTimeMs + item.totalSustainMs;
      if (itemEndMs > maxEndTimeMs) maxEndTimeMs = itemEndMs;
    });
  });

  const endTimeout = setTimeout(() => {
    stopPlay();
  }, maxEndTimeMs + 150);
  playTimeouts.push(endTimeout);
}

function notifyChange() {
  const payload = props.format === 'musicxml' ? computedMusicXml.value : (props.format === 'json' ? voices.value : computedAbc.value);
  emit('update:modelValue', payload);
  emit('change', {
    voices: voices.value,
    notes: activeVoice.value?.notes || [],
    abc: computedAbc.value,
    musicXml: computedMusicXml.value,
    title: scoreTitle.value,
    key: selectedKey.value,
    timeSignature: selectedTimeSignature.value,
    tempo: selectedTempo.value,
    clef: selectedClef.value,
    keyChanges: [...keyChanges.value],
    timeChanges: [...timeChanges.value],
    tempoChanges: [...tempoChanges.value],
    tempoRamps: [...tempoRamps.value],
    swing: { ...swing.value },
    instrument: selectedInstrument.value,
    wrapMode: selectedWrapMode.value,
    isMultiVoice: isMultiVoice.value
  });
}

function getScoreData() {
  return {
    voices: JSON.parse(JSON.stringify(voices.value)),
    notes: activeVoice.value?.notes ? JSON.parse(JSON.stringify(activeVoice.value.notes)) : [],
    abc: computedAbc.value,
    musicXml: computedMusicXml.value,
    title: scoreTitle.value,
    key: selectedKey.value,
    timeSignature: selectedTimeSignature.value,
    tempo: selectedTempo.value,
    clef: selectedClef.value,
    keyChanges: [...keyChanges.value],
    timeChanges: [...timeChanges.value],
    tempoChanges: [...tempoChanges.value],
    tempoRamps: [...tempoRamps.value],
    swing: { ...swing.value },
    instrument: selectedInstrument.value,
    wrapMode: selectedWrapMode.value,
    isMultiVoice: isMultiVoice.value
  };
}

// Watchers
watch(() => props.title, (newVal) => {
  if (newVal !== undefined && newVal !== null) scoreTitle.value = newVal;
});

watch(scoreTitle, () => {
  notifyChange();
});

watch(() => props.keySignature, (newVal) => {
  if (newVal) selectedKey.value = newVal;
});

watch(selectedKey, () => {
  notifyChange();
});

watch(() => props.timeSignature, (newVal) => {
  if (newVal) selectedTimeSignature.value = newVal;
});

watch(selectedTimeSignature, () => {
  notifyChange();
});

watch(() => props.tempo, (newVal) => {
  if (newVal) selectedTempo.value = newVal;
});

watch(() => props.clef, (newVal) => {
  if (newVal && activeVoice.value) activeVoice.value.clef = newVal;
});

watch(() => props.keyChanges, (newVal) => {
  if (Array.isArray(newVal)) keyChanges.value = [...newVal];
}, { deep: true });

watch(() => props.timeChanges, (newVal) => {
  if (Array.isArray(newVal)) timeChanges.value = [...newVal];
}, { deep: true });

watch(() => props.tempoChanges, (newVal) => {
  if (Array.isArray(newVal)) tempoChanges.value = [...newVal];
}, { deep: true });

watch(() => props.tempoRamps, (newVal) => {
  if (Array.isArray(newVal)) tempoRamps.value = [...newVal];
}, { deep: true });

watch(() => props.swing, (newVal) => {
  if (newVal) swing.value = { on: !!newVal.on, amount: Math.min(0.75, Math.max(0.5, newVal.amount || 0.67)) };
}, { deep: true });

watch(() => props.wrapMode, (newVal) => {
  if (newVal) selectedWrapMode.value = newVal;
});

watch(selectedWrapMode, () => {
  notifyChange();
});

// selectedClef agora atualiza activeVoice.clef diretamente via computed setter

watch(selectedTempo, () => {
  notifyChange();
});

// Watch initial input prop

function parseIncomingModelValue(newVal) {
  if (!newVal) return;
  if (Array.isArray(newVal)) {
    if (newVal.length > 0 && newVal[0].notes !== undefined) {
      voices.value = JSON.parse(JSON.stringify(newVal));
    } else {
      voices.value[0].notes = JSON.parse(JSON.stringify(newVal));
    }
  } else if (typeof newVal === 'string') {
    if (newVal.includes('<score-partwise') || newVal.includes('<?xml')) {
      try {
        const parsed = musicXmlToNotes(newVal);
        if (parsed.voices && parsed.voices.length > 0) {
          voices.value = parsed.voices;
          activeVoiceId.value = parsed.voices[0].id;
        } else {
          voices.value[0].notes = parsed.notes;
        }
        if (parsed.title) scoreTitle.value = parsed.title;
        if (parsed.timeSignature) selectedTimeSignature.value = parsed.timeSignature;
        keyChanges.value = Array.isArray(parsed.keyChanges) ? [...parsed.keyChanges] : [];
        timeChanges.value = Array.isArray(parsed.timeChanges) ? [...parsed.timeChanges] : [];
        tempoChanges.value = Array.isArray(parsed.tempoChanges) ? [...parsed.tempoChanges] : [];
        tempoRamps.value = Array.isArray(parsed.tempoRamps) ? [...parsed.tempoRamps] : [];
        if (parsed.tempo) selectedTempo.value = parsed.tempo;
      } catch (e) {
        console.warn('Error parsing MusicXML modelValue:', e);
      }
    } else if (newVal.includes('X:') || newVal.includes('K:') || newVal.includes('M:') || newVal.includes('V:')) {
      try {
        const parsed = abcToNotes(newVal);
        if (parsed.voices && parsed.voices.length > 0) {
          voices.value = parsed.voices;
          activeVoiceId.value = parsed.voices[0].id;
        } else {
          voices.value[0].notes = parsed.notes;
        }
        if (parsed.title) scoreTitle.value = parsed.title;
        if (parsed.composer) scoreComposer.value = parsed.composer;
        if (parsed.timeSignature) selectedTimeSignature.value = parsed.timeSignature;
        keyChanges.value = Array.isArray(parsed.keyChanges) ? [...parsed.keyChanges] : [];
        timeChanges.value = Array.isArray(parsed.timeChanges) ? [...parsed.timeChanges] : [];
        tempoChanges.value = Array.isArray(parsed.tempoChanges) ? [...parsed.tempoChanges] : [];
        tempoRamps.value = Array.isArray(parsed.tempoRamps) ? [...parsed.tempoRamps] : [];
        if (parsed.tempo) selectedTempo.value = parsed.tempo;
        if (parsed.key) selectedKey.value = parsed.key;
      } catch (e) {
        console.warn('Error parsing ABC modelValue:', e);
      }
    }
  }
}

watch(() => props.modelValue, (newVal) => {
  if (!newVal) return;
  if (typeof newVal === 'string' && newVal.trim() === computedAbc.value.trim()) return;
  if (typeof newVal === 'string' && newVal.trim() === computedMusicXml.value.trim()) return;
  parseIncomingModelValue(newVal);
}, { immediate: true, deep: true });

function handleKeyDown(e) {
  if (props.readonly) return;
  // Don't intercept if user is typing in an input, textarea or select
  if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT')) {
    return;
  }

  const isCmdOrCtrl = e.metaKey || e.ctrlKey;
  if (isCmdOrCtrl && (e.key === 'z' || e.key === 'Z')) {
    e.preventDefault();
    if (e.shiftKey) {
      redo();
    } else {
      undo();
    }
    return;
  } else if (isCmdOrCtrl && (e.key === 'y' || e.key === 'Y')) {
    e.preventDefault();
    redo();
    return;
  } else if (e.key === 'Escape') {
    selectedNoteIndex.value = null;
    return;
  } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNoteIndex.value !== null && activeVoice.value) {
    const notes = activeVoice.value.notes;
    if (notes && notes[selectedNoteIndex.value]) {
      e.preventDefault();
      pushHistory();
      notes.splice(selectedNoteIndex.value, 1);
      selectedNoteIndex.value = null;
      notifyChange();
    }
    return;
  }

  // TAB direct editing & navigation shortcuts
  if (selectedNoteIndex.value !== null && activeVoice.value) {
    const isTabVoice = !!activeVoice.value.showTab;

    // 1. Digits 0 to 9 for fret input
    if (isTabVoice && /^[0-9]$/.test(e.key) && !isCmdOrCtrl && !e.altKey) {
      e.preventDefault();
      fretInputBuffer += e.key;
      let fret = parseInt(fretInputBuffer, 10);
      if (fret > 24) {
        fretInputBuffer = e.key;
        fret = parseInt(fretInputBuffer, 10);
      }
      clearTimeout(fretInputTimer);
      fretInputTimer = setTimeout(() => {
        fretInputBuffer = '';
      }, 900);

      setFretOnSelectedNote(fret, activeTabString.value);
      return;
    }

    // 2. Increment / Decrement fret with + / = / -
    if (isTabVoice && (e.key === '+' || e.key === '=') && !isCmdOrCtrl) {
      e.preventDefault();
      stepFret(1);
      return;
    }
    if (isTabVoice && (e.key === '-' || e.key === '_') && !isCmdOrCtrl) {
      e.preventDefault();
      stepFret(-1);
      return;
    }

    // 3. ArrowUp / ArrowDown: change string in TAB mode (1 to 6)
    if (isTabVoice && e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeTabString.value > 1) {
        handleTabStringChange(activeTabString.value - 1);
      }
      return;
    }
    if (isTabVoice && e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeTabString.value < 6) {
        handleTabStringChange(activeTabString.value + 1);
      }
      return;
    }

    // 4. Quick TAB techniques
    if (isTabVoice && !isCmdOrCtrl && !e.altKey) {
      const lower = e.key.toLowerCase();
      if (lower === 'x') {
        e.preventDefault();
        toggleTabDead();
        return;
      }
      if (lower === 'h' || lower === 'p') {
        e.preventDefault();
        toggleTabHammerOrPull();
        return;
      }
      if (lower === 'v' || e.key === '~') {
        e.preventDefault();
        toggleTabVibrato();
        return;
      }
      if (lower === 'b') {
        e.preventDefault();
        toggleTabBend(activeNoteHasTabBend() ? null : 'full');
        return;
      }
      if (lower === 'g') {
        e.preventDefault();
        toggleTabGhost();
        return;
      }
      if (e.key === '/' || e.key === '\\') {
        e.preventDefault();
        toggleTabSlide(e.key === '/' ? 'up' : 'down');
        return;
      }
    }

    // 5. ArrowLeft / ArrowRight: horizontal note navigation
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (selectedNoteIndex.value > 0) {
        selectedNoteIndex.value--;
        const n = activeVoice.value.notes[selectedNoteIndex.value];
        if (n && n.tabString) activeTabString.value = n.tabString;
        playSelectedNoteSound();
      }
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (selectedNoteIndex.value < activeVoice.value.notes.length - 1) {
        selectedNoteIndex.value++;
        const n = activeVoice.value.notes[selectedNoteIndex.value];
        if (n && n.tabString) activeTabString.value = n.tabString;
        playSelectedNoteSound();
      }
      return;
    }
  }
}

const onCurrentZoom = (val) => {
  if (val && typeof val === 'number') {
    internalZoom.value = val;
  }
};

onMounted(() => {
  window.addEventListener('beforeprint', handleBeforePrint);
  window.addEventListener('afterprint', cleanupPrint);
  appEvents.on('currentZoom', onCurrentZoom);
});

onUnmounted(() => {
  stopPlay();
  endNoteDragListeners();
  noteDrag.value = null;
  window.removeEventListener('beforeprint', handleBeforePrint);
  window.removeEventListener('afterprint', cleanupPrint);
  appEvents.off('currentZoom', onCurrentZoom);
  cleanupPrint();
});

defineExpose({
  togglePlay,
  startPlay,
  stopPlay,
  isPlaying: computed(() => isPlaying.value),
  getGuitarChord,
  guitarFrame,
  setInlineClef,
  setNoteheadType,
  printScore,
  getScoreData,
  getVoices: () => voices.value,
  getNotes: () => activeVoice.value?.notes || [],
  getAbc: () => computedAbc.value,
  getMusicXml: () => computedMusicXml.value,
  getPartAbc: () => partAbc.value,
  getPartMusicXml: () => partMusicXml.value,
  getPartJson: () => partJson.value,
  getPartVoiceId: () => partVoiceId.value,
  selectPart,
  getTempo: () => selectedTempo.value,
  setTempo: (bpm) => { selectedTempo.value = bpm; },
  getInstrument: () => selectedInstrument.value,
  setInstrument: (inst) => { selectedInstrument.value = inst; handleInstrumentChange(); },
  clearScore,
  undo,
  redo,
  play: startPlay,
  stop: stopPlay
});
</script>

<style scoped>
.music-sheet-container {
  user-select: none;
}

@media print {
  body {
    background: #ffffff !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  .music-sheet-sidebar,
  .music-sheet-toolbar,
  .playback-controls,
  .floating-tools,
  .app-header,
  .app-nav {
    display: none !important;
  }
  .music-sheet-container {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    background: #ffffff !important;
  }
  .music-sheet-svg {
    width: 100% !important;
    height: auto !important;
  }
}

.sidebar-scroll-area::-webkit-scrollbar {
  width: 5px;
}
.sidebar-scroll-area::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-scroll-area::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.4);
  border-radius: 4px;
}
.sidebar-scroll-area::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.7);
}
</style>
